import { listingVerdict } from "../signals/verdict";
import { decisionScore, familyOf, TIMING_RULE, timingInfluences } from "../signals/timingRule";
import { saleProceeds } from "../finance/costs";
import { h, clear, field, numberInput, moneyInput } from "./dom";
import type { Actions, AppState } from "./state";
import { fetchComps, fetchCommute, fetchDeveloperClearout, fetchEnblocDigest, fetchEnblocInsight, fetchEnblocStatus, fetchEvaluateHdb, fetchEvaluatePrivate, fetchHdbTownMedians, fetchLocate, fetchPrivateComps, fetchSchools, fetchUndervaluedHdb, fetchUndervaluedPrivate, HDB_FLAT_TYPES, HDB_TOWNS, PRIVATE_TYPES, type ClearoutProject, type Comp, type CommuteResult, type CompStats, type DeveloperClearout, type DistrictTrend, type EvalResult, type FairValueResponse, type PrivateComp, type PrivateFairValueResponse, type PrivateFilters, type School } from "../api";
import { bandColor, bandLabel, districtCode, headlineKindLabel, outcomeLabel, snapshotOf, type EnblocHeadline, type EnblocIndicator, type EnblocInsight, type EnblocSnapshot } from "../signals/enbloc";
import { recommendPrivateRegions, recommendPrivateDistricts, DISTRICT_NAMES, TOWN_DISTRICT, type EntryFitOpts } from "../signals/regions";
import { inferEntryStance } from "../signals/entryfit";
import { blendArea, BLEND_WEIGHTS, type BlendResult } from "../signals/blend";
import { computeSellSignal } from "../signals/sell";
import { UPCOMING_MRT, mrtForDistrict, mrtForTown, mrtBadge } from "../signals/connectivity";
import { FLOWS, buyCostSummary, offerTemplate, moveFlows, moveSequencing, type Flow, type FlowClass } from "../signals/diyflow";
import { computeMovePlan, MOVE_SCENARIOS, classOf, type MoveScenario, type PropClass } from "../signals/moveplan";
import { lineChart, scoreGauge, type LineSeries } from "../charts/charts";
import {
  carBuyingPower,
  carEquity,
  feasibility,
  propertyBuyingPower,
  propertyEquity,
  RULES, holdingOf, rateIsManual, buyerOf } from "../signals/affordability";
import { getTheme, setTheme, type ThemePref } from "./theme";
import { valueRange } from "./components";
import { SMART_QUESTIONS, detectReprofilePrompts, yearsHeldKnown, type CompSnapshot, type Profile, type ShortlistItem, type StrataInputs } from "../profile/schema";
import { strataHealth, STRATA_LABEL, strataColour, type StrataResult } from "../signals/strata";
import { scanListing, type ParsedListing, type ParseOpts } from "./ocr";
import { hdbEcEligibility, ELIGIBILITY, type CeilingCheck } from "../signals/eligibility";
import {
  convictionText,
  entryFactors,
  fmtMetric,
  fmtPct,
  fmtPsf,
  fmtEstimate,
  fmtSGD,
  isTimingOnly,
  verdictColor,
  verdictLabel,
  type SeriesSignal,
} from "../signals/timing";
import { hdbLeaseFinance, RISK_LABEL } from "../signals/leaseDecay";

const disclaimer =
  "Heuristic decision-support using public market data — not financial advice. Verify figures before acting.";

// --- Remembered UI state (survives re-renders within a session) -------------
// Nearly every input triggers a full re-render, which rebuilds the DOM. These
// module-level maps let a collapsed/expanded section and the active sub-view
// group persist across those rebuilds, so the page doesn't reset under you.
const openSections = new Map<string, boolean>();
const activeGroups = new Map<string, string>();

// --- Data provenance --------------------------------------------------------
// A standardized footnote shown under every dataset and chart, so the user
// always knows the licensed source and how current the data is.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatAsOf(s: string): string {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(s);
  if (!m) return s;
  const mon = MONTHS[Number(m[2]) - 1] ?? m[2];
  return m[3] ? `${Number(m[3])} ${mon} ${m[1]}` : `${mon} ${m[1]}`;
}
// Latest (max) period string from a list of YYYY-MM month labels.
function latestMonth(months: (string | null | undefined)[]): string | null {
  const vs = months.filter((x): x is string => !!x).sort();
  return vs.length ? vs[vs.length - 1] : null;
}
function sourceNote(source: string, asOf?: string | null): HTMLElement {
  const date = asOf ? formatAsOf(asOf) : null;
  return h("p", { class: "source-note" }, `Source: ${source}${date ? ` · data as of ${date}` : ""}`);
}

// --- HDB / EC eligibility (household income ceilings) ----------------------
// The eligibility gate (are you *allowed* to buy this?), distinct from the
// affordability math (can you *finance* it?). Constants + logic in
// signals/eligibility.ts; this renders the read.
function ceilingRow(c: CeilingCheck): HTMLElement {
  const status = c.eligible
    ? `✓ Eligible · S$${Math.abs(c.deltaMonthly).toLocaleString()}/mo headroom`
    : `✗ Over by S$${Math.abs(c.deltaMonthly).toLocaleString()}/mo`;
  return h("div", { class: "elig-row" },
    h("div", { class: "elig-head" },
      h("span", { class: "elig-scheme" }, c.scheme),
      h("span", { class: `elig-status ${c.eligible ? "good" : "bad"}` }, status),
    ),
    h("div", { class: "muted small" },
      `Ceiling S$${c.ceiling.toLocaleString()}/mo household gross${c.note ? ` — ${c.note}` : ""}`),
  );
}

function hdbEligibilityView(p: Profile, flatType?: string): HTMLElement {
  const e = hdbEcEligibility(p, flatType);
  const box = h("div", { class: "callout column elig-box" });
  box.append(h("h3", { style: "font-size:15px;margin:0" }, "Eligibility to purchase (household income)"));
  if (!e.incomeKnown) {
    box.append(h("p", { class: "muted small" },
      "Enter your household gross monthly income in Profile to check the HDB/EC income-ceiling eligibility."));
    box.append(sourceNote(ELIGIBILITY.source, ELIGIBILITY.asOf));
    return box;
  }
  box.append(h("p", { class: "muted small" },
    `Household income S$${e.income.toLocaleString()}/mo · assessed as ${e.applicant === "single" ? "a single applicant" : "a family"}.`));
  box.append(ceilingRow(e.hdbNew), ceilingRow(e.hdbResaleGrant), ceilingRow(e.ec), ceilingRow(e.ehg));
  if (e.singleUnderAge)
    box.append(h("p", { class: "field-hint warn" }, "Singles must be at least 35 to buy a subsidised HDB flat."));
  box.append(sourceNote(ELIGIBILITY.source, ELIGIBILITY.asOf));
  return box;
}

// Compact EC-only eligibility note for the private evaluate panel (EC straddles
// HDB and private, so it carries an income ceiling the rest of private does not).
function ecEligibilityNote(p: Profile): HTMLElement | null {
  const e = hdbEcEligibility(p);
  if (!e.incomeKnown) return null;
  const inc = e.income;
  const std = ELIGIBILITY.ec.standard;
  const nt = ELIGIBILITY.ec.newTender;
  let msg: string;
  let warn = false;
  if (inc <= std) {
    msg = `EC eligibility: household S$${inc.toLocaleString()}/mo is within the S$${std.toLocaleString()} new-EC income ceiling.`;
  } else if (inc <= nt) {
    msg = `EC eligibility: household S$${inc.toLocaleString()}/mo is over the standard S$${std.toLocaleString()} EC ceiling, but within the S$${nt.toLocaleString()} ceiling for ECs from land-sale tenders closing on/after 24 Aug 2026.`;
  } else {
    warn = true;
    msg = `EC eligibility: household S$${inc.toLocaleString()}/mo exceeds even the S$${nt.toLocaleString()} new-EC ceiling — only resale / privatised ECs (no income ceiling) are open to you.`;
  }
  return h("p", { class: `field-hint ${warn ? "warn" : ""}` }, msg);
}

// --- Lease / tenure display ------------------------------------------------
// One consistent chip across every panel: "Freehold" or "~X yrs left" (the
// remaining lease as of the transaction month — a longevity read), warn-styled
// under 60 years, where SG CPF usage and bank LTV start tightening. The exact
// raw URA/HDB string is kept as a hover tooltip (verify-the-figure ethos).
const SHORT_LEASE_YEARS = 60;
function leaseChip(opts: { freehold?: boolean; leaseLeft?: number | null; raw?: string }): HTMLElement | null {
  const raw = (opts.raw ?? "").trim();
  if (opts.freehold) return h("span", { class: "chip", title: raw || "Freehold" }, "Freehold");
  const yrs = opts.leaseLeft;
  if (yrs == null || !Number.isFinite(yrs)) return raw ? h("span", { class: "chip", title: raw }, raw) : null;
  const y = Math.round(yrs);
  const short = y < SHORT_LEASE_YEARS;
  return h("span", { class: short ? "chip warn" : "chip", title: raw || `${y} years remaining at sale` },
    `~${y} yrs left${short ? " · short lease" : ""}`);
}
// Plain-text version of the lease chip, for saved shortlist labels etc.
function leaseLabel(opts: { freehold?: boolean; leaseLeft?: number | null; raw?: string }): string {
  if (opts.freehold) return "Freehold";
  const y = opts.leaseLeft;
  if (y != null && Number.isFinite(y)) return `~${Math.round(y)} yrs left`;
  return (opts.raw ?? "").trim();
}
// Parse HDB's "61 years 04 months" remaining-lease string to a year figure.
function hdbLeaseLeft(remaining: string): number | null {
  const y = /(\d+)\s*year/.exec(remaining ?? "");
  if (!y) return null;
  const mo = /(\d+)\s*month/.exec(remaining ?? "");
  return Number(y[1]) + (mo ? Number(mo[1]) / 12 : 0);
}

type Interests = { hdb: boolean; private: boolean; car: boolean };
function getInterests(p: Profile): Interests {
  return p.interests ?? { hdb: true, private: true, car: true };
}
// The single shared "target HDB flat type" ("" = any), with back-compat for the
// old per-panel filter field.
function hdbTarget(p: Profile): string {
  return p.targetHdbFlatType ?? p.hdbFilterFlatType ?? "";
}

// --- Shared school autocomplete -------------------------------------------
// The full MOE school list (fetched once). We render our own suggestion list
// (a native <datalist> is unreliable on mobile browsers), and resolve the
// picked name to its postal code so OneMap geocodes exactly, not a free name.
const normName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
let schoolsPromise: Promise<School[]> | null = null;
let schoolsByNorm = new Map<string, School>();
function ensureSchools(): Promise<School[]> {
  if (!schoolsPromise) {
    schoolsPromise = fetchSchools("")
      .then((list) => {
        schoolsByNorm = new Map(list.map((s) => [normName(s.name), s]));
        return list;
      })
      .catch(() => []);
  }
  return schoolsPromise;
}
// Resolve typed/selected school text to a postal code when we can match it
// (exact, else a unique substring match); otherwise return the raw text.
function resolveSchoolQuery(input: string): string {
  const nq = normName(input);
  if (!nq) return input;
  const exact = schoolsByNorm.get(nq);
  if (exact?.postal) return exact.postal;
  const matches = [...schoolsByNorm.values()].filter((s) => normName(s.name).includes(nq));
  if (matches.length === 1 && matches[0].postal) return matches[0].postal;
  return input;
}
// A text input with a custom, mobile-friendly school autocomplete: type ≥2
// chars → clickable MOE matches; picking one sets the exact name (which then
// resolves to its postal code for routing).
function schoolField(placeholder: string, value = "", onCommit?: (v: string) => void): HTMLInputElement {
  void ensureSchools();
  const inp = h("input", { type: "text", placeholder, value, autocomplete: "off" }) as HTMLInputElement;
  let sug: HTMLElement | null = null;
  const clearSug = () => { sug?.remove(); sug = null; };
  inp.addEventListener("input", () => {
    clearSug();
    const nq = normName(inp.value);
    if (nq.length < 2) return;
    const matches = [...schoolsByNorm.values()].filter((s) => normName(s.name).includes(nq)).slice(0, 8);
    if (matches.length === 0) return;
    sug = h("div", { class: "ac-suggest" },
      ...matches.map((s) => h("button", {
        type: "button", class: "ac-item",
        // mousedown fires before blur, so the pick registers on mobile + desktop.
        onmousedown: (e: Event) => { e.preventDefault(); inp.value = titleCase(s.name); clearSug(); onCommit?.(inp.value); },
      }, `${titleCase(s.name)}${s.postal ? ` · S(${s.postal})` : ""}`)));
    (inp.parentElement ?? inp).append(sug);
  });
  inp.addEventListener("blur", () => setTimeout(clearSug, 150));
  if (onCommit) inp.addEventListener("change", () => onCommit(inp.value));
  return inp;
}
// A plain work-location text input that commits its trimmed value on change
// (blur/Enter), so the profile stays the source of truth for commute ranking.
function workInput(value: string, onCommit: (v: string) => void): HTMLInputElement {
  const inp = h("input", { type: "text", value, placeholder: "e.g. One-North, CBD, or a postal code",
    onchange: () => onCommit(inp.value.trim()) }) as HTMLInputElement;
  return inp;
}
// Whether a market segment matches the user's interests.
function segVisible(s: SeriesSignal, int: Interests): boolean {
  if (s.asset_class === "car") return int.car;
  return s.id.startsWith("private") ? int.private : int.hdb;
}

// ---------------------------------------------------------------- Wizard
// Guided first-run setup: a short step-by-step flow so a new profile lands on
// the dashboard already populated. Everything is written to the encrypted local
// vault as you go; nothing is sent anywhere.
// Module-level so the wizard keeps its place across app re-renders (e.g. when
// market signals finish loading and the shell repaints).
let wizardStep = 0;

export function renderWizard(state: AppState, actions: Actions): HTMLElement {
  const w = JSON.parse(JSON.stringify(state.profile)) as Profile;
  if (!w.interests) w.interests = { hdb: true, private: true, car: false };
  const STEPS = 4;
  let step = Math.min(wizardStep, STEPS - 1);
  const commit = () => void actions.saveProfileQuiet(w);

  const root = h("section", { class: "view" });
  const card = h("div", { class: "wizard-card" });
  const dots = h("div", { class: "wizard-steps" });

  const money = (key: keyof Profile) =>
    moneyInput(w[key] as number, (v) => { (w[key] as number) = v; commit(); }, "change");

  const stepInterests = () => {
    const mk = (label: string, k: "hdb" | "private" | "car") =>
      field(label, h("input", { type: "checkbox", checked: w.interests[k] ? "checked" : null,
        onchange: (e: Event) => { w.interests[k] = (e.target as HTMLInputElement).checked; commit(); } }) as HTMLInputElement);
    return [
      h("h3", {}, "What are you evaluating?"),
      h("p", { class: "muted small" }, "We'll show only what's relevant and hide the rest. You can change this anytime in Profile."),
      h("div", { class: "form-grid" }, mk("HDB (public housing)", "hdb"), mk("Private property", "private"), mk("Car", "car")),
    ];
  };
  const stepIncome = () => [
    h("h3", {}, "Your income"),
    h("p", { class: "muted small" }, "Drives loan eligibility (TDSR/MSR). Household gross, per month."),
    h("div", { class: "form-grid" },
      field("Age", numberInput(w.ageYears, (v) => { w.ageYears = v; commit(); }, "1", "change")),
      field("Household gross income (monthly)", money("monthlyGrossIncome")),
    ),
  ];
  const stepSavings = () => [
    h("h3", {}, "Cash & CPF"),
    h("p", { class: "muted small" }, "What you could put toward a purchase. Stays encrypted on this device."),
    h("div", { class: "form-grid" },
      field("Cash savings", money("cashSavings")),
      field("CPF OA balance", money("cpfOaBalance")),
      field("Other liquid assets", money("otherLiquidAssets")),
    ),
  ];
  const stepHoldings = () => [
    h("h3", {}, "What you own now"),
    h("p", { class: "muted small" }, "If you own a home, its value and how long you've held it decide the cash freed by selling — Seller's Stamp Duty, the HDB 5-year MOP and your CPF refund. Leave blank if you don't own one."),
    h("div", { class: "form-grid" },
      field("Current property — value", money("currentPropertyValue")),
      field("Current property — outstanding loan", money("currentPropertyOutstandingLoan")),
      field("Years since you bought it", yearsHeldInput(w, commit),
        "Blank = we'll leave SSD out and flag it, rather than guess."),
      w.interests.car ? field("Current car — value (0 if none)", money("currentCarValue")) : null,
    ),
  ];
  const stepFns = [stepInterests, stepIncome, stepSavings, stepHoldings];

  const paint = () => {
    clear(dots);
    for (let i = 0; i < STEPS; i++) dots.append(h("div", { class: i <= step ? "wizard-dot on" : "wizard-dot" }));
    clear(card);
    card.append(...stepFns[step]());
    const back = h("button", { class: "ghost", onclick: () => { if (step > 0) { step--; wizardStep = step; paint(); } } }, "Back");
    const nextLabel = step === STEPS - 1 ? "Finish" : "Next";
    const next = h("button", { class: "primary", onclick: async () => {
      if (step < STEPS - 1) { step++; wizardStep = step; paint(); return; }
      wizardStep = 0;
      w.onboarded = true;
      // Pass w as its own baseline: initial setup shouldn't fire re-profile prompts.
      await actions.saveProfile(w, w);
      actions.setTab("dashboard");
    } }, nextLabel);
    card.append(h("div", { class: "wizard-actions" }, step > 0 ? back : h("span", {}), next));
  };
  paint();

  root.append(
    h("div", { class: "wizard" },
      h("h1", { class: "page-title" }, "Let's set you up"),
      h("p", { class: "muted small" }, "Four quick steps. Private & encrypted on this device — nothing is sent to any server."),
      dots,
      card,
      h("button", { class: "link", onclick: async () => { wizardStep = 0; w.onboarded = true; await actions.saveProfile(w, w); actions.setTab("dashboard"); } }, "Skip setup — I'll fill it in later"),
    ),
  );
  return root;
}

// ---------------------------------------------------------------- Dashboard
// Home — "your position" first (what you can afford, what selling frees up),
// market timing second and explicitly experimental (UX_FINDINGS U-01, U-11;
// owner decision D-6). No "now"/"best window" framing: the app informs, it
// doesn't push a purchase.
export function renderDashboard(state: AppState, actions: Actions): HTMLElement {
  const p = state.profile;
  const root = h("section", { class: "view" });
  const int = getInterests(p);

  root.append(h("h1", { class: "page-title" }, "Your position"));
  const status = marketStatusLine(state, actions);
  if (status) root.append(status);

  if (state.reprofilePrompts.length > 0) {
    root.append(
      h("div", { class: "banner", role: "status" },
        h("strong", {}, "Assumptions may have changed. "),
        "Because you updated: " +
          SMART_QUESTIONS.filter((q) => state.reprofilePrompts.includes(q.id))
            .map((q) => q.prompt.replace(/\?$/, ""))
            .join("; ") +
          ". Re-check your targets on the Property tab.",
        h("button", { class: "link", onclick: () => actions.dismissReprofile() }, "Dismiss"),
      ),
    );
  }

  // Empty state: nothing to compute from yet → one sentence, one button. Never
  // a row of "S$0" results (U-03).
  if (!hasFinances(p)) {
    root.append(
      h("div", { class: "empty-state" },
        h("h2", {}, "See what you can afford"),
        h("p", {}, "Add your income and savings — about a minute. Everything stays encrypted on this device; nothing is sent anywhere."),
        h("button", { class: "primary", onclick: () => {
          wizardStep = 1; // jump straight to income
          void actions.saveProfile({ ...state.profile, onboarded: false }, state.profile);
        } }, "Continue setup"),
      ),
    );
  } else {
    root.append(affordCard(state, actions, int));
    if (p.currentPropertyValue > 0 && (int.hdb || int.private)) root.append(currentHomeCard(state, actions));
  }

  root.append(marketTimingSection(state, actions, int));
  root.append(h("p", { class: "disclaimer" }, disclaimer));
  return root;
}

// "Market data to Aug 2026", or — when the refresh failed — what we're showing
// instead and a Retry. Never a raw "API 500" (U-05).
function marketStatusLine(state: AppState, actions: Actions): HTMLElement | null {
  const asOf = latestMonth(state.signals.map((s) => s.as_of));
  const retry = () => h("button", { class: "link", onclick: () => { void actions.refreshSignals(); } }, "Retry");
  if (state.signalsError && state.signalsSavedAt) {
    return h("div", { class: "banner", role: "status" },
      h("strong", {}, "Couldn't refresh market data. "),
      `Showing the copy saved on ${fmtDate(state.signalsSavedAt)}${asOf ? ` (data to ${formatAsOf(asOf)})` : ""}. Your own figures are unaffected. `,
      retry());
  }
  if (state.signalsError) {
    return h("div", { class: "banner", role: "status" },
      h("strong", {}, "Market data couldn't load. "),
      "Your buying power and costs still work — they're computed on this device. Market timing and comparisons need the data service. ",
      retry());
  }
  if (state.signalsLoading) return h("p", { class: "as-of muted small" }, "Loading market data…");
  if (!asOf) return null;
  const stale = monthsSince(asOf) > 2;
  return h("p", { class: `as-of small ${stale ? "caution" : "muted"}` },
    `Market data to ${formatAsOf(asOf)}${stale ? " — older than usual; figures may lag the market" : ""} · sources: data.gov.sg, URA`);
}

function monthsSince(ym: string): number {
  const m = /^(\d{4})-(\d{2})/.exec(ym);
  if (!m) return 0;
  const now = new Date();
  return (now.getFullYear() - Number(m[1])) * 12 + (now.getMonth() + 1 - Number(m[2]));
}

// What you can afford — the headline answer. Estimates at 3 significant figures
// with the binding constraint in plain words, and one next step.
function affordCard(state: AppState, actions: Actions, int: Interests): HTMLElement {
  const p = state.profile;
  const hdbBp = propertyBuyingPower(p, true, { sora: state.rates?.sora });
  const privBp = propertyBuyingPower(p, false, { sora: state.rates?.sora });
  const carBp = carBuyingPower(p);
  const keepCurrent = p.currentPropertyValue > 0 && p.buyIntent === "second";
  const rows: HTMLElement[] = [];
  const stat = (label: string, value: number, limit: string) =>
    h("div", { class: "stat" },
      h("div", { class: "stat-label" }, label),
      h("div", { class: "stat-value" }, fmtEstimate(value)),
      h("div", { class: "stat-sub" }, limit));
  if (int.hdb) rows.push(stat("HDB flat", hdbBp.maxPrice, limitLabel(hdbBp.boundedBy)));
  if (int.private) rows.push(stat("Private home", privBp.maxPrice, limitLabel(privBp.boundedBy)));
  if (int.car) rows.push(stat("Car", carBp.maxPrice, limitLabel(carBp.boundedBy)));
  const owns = p.currentPropertyValue > 0;
  return h("div", { class: "hero-card" },
    h("h2", {}, "What you can afford"),
    h("div", { class: "stat-row" }, ...rows),
    h("p", { class: "muted small" },
      "Estimated maximum purchase price from your income, cash and CPF under current MAS/HDB loan rules (stress-tested). " +
      (owns ? (keepCurrent ? "Assumes you keep your current home and buy a second (ABSD applies)." : "Assumes you sell your current home first.") : "")),
    h("div", { class: "actions" },
      h("button", { class: "primary", onclick: () => actions.setTab("property") }, owns ? "Review your move plan" : "Check a listing"),
      h("button", { class: "link", onclick: () => actions.setTab("profile") }, "Edit your figures")),
  );
}

// Your current home — what selling it would free up, line by line, so no
// deduction is hidden (U-02). Unknown holding period is shown as unknown.
function currentHomeCard(state: AppState, actions: Actions): HTMLElement {
  const p = state.profile;
  const sale = saleProceeds(holdingOf(p), p.useAgent !== false);
  const line = (k: string, v: string, cls = "") => h("div", { class: `mp-row ${cls}` }, h("span", {}, k), h("span", {}, v));
  const neg = (v: number) => (v > 0 ? `− ${fmtSGD(v)}` : fmtSGD(0));
  return h("div", { class: "callout column" },
    h("h2", {}, "If you sold your current home"),
    h("div", { class: "mp-block" },
      line("Value (your estimate)", fmtSGD(sale.value)),
      line("Outstanding loan", neg(sale.outstandingLoan)),
      sale.ssdUnknown
        ? line("Seller's Stamp Duty", "not included — add years held", "caution")
        : line(`Seller's Stamp Duty${sale.ssdRate > 0 ? ` (${Math.round(sale.ssdRate * 100)}%)` : ""}`, neg(sale.ssd)),
      line(p.useAgent === false ? "Legal fees (DIY, no agent)" : "Agent (~2%) and legal fees", neg(sale.agentFee + sale.legal)),
      line(`Back to your CPF${sale.estimatedRefund ? " (estimate)" : ""}`, fmtSGD(sale.cpfRefund)),
      line("Cash to your bank", fmtSGD(sale.cash), "emph"),
    ),
    sale.ssdUnknown
      ? h("p", { class: "small caution" },
          `We don't know how long you've held it, so SSD is left out. If you bought within the SSD period it could be up to ${fmtSGD(sale.ssdIfRecent)}. `,
          h("button", { class: "link", onclick: () => actions.setTab("profile") }, "Add years held"))
      : null,
    sale.cpfRefundIncomplete
      ? h("p", { class: "small caution" }, "The CPF refund only counts CPF used at purchase until you add years held (monthly instalments and accrued interest need it).")
      : null,
  );
}

// What the P3 backtest found, in plain words, for the families the user follows.
function timingEvidence(int: Interests): HTMLElement {
  const items: HTMLElement[] = [];
  if (int.private) items.push(h("li", {}, h("strong", {}, "Private: "), "tested 1975–2026 — soft-priced entries did better than stretched ones over the next 12 months, but only just (the margin could be close to zero)."));
  if (int.hdb) items.push(h("li", {}, h("strong", {}, "HDB: "), "tested 1990–2026 — the score did not predict better entries (prices kept trending up after \"stretched\" reads), so HDB timing isn't used here."));
  if (int.car) items.push(h("li", {}, h("strong", {}, "COE: "), "not yet tested."));
  return h("ul", { class: "evidence small muted" }, ...items);
}

// Market timing — shown with its backtest evidence (P3, timingRule.ts). Segments
// within your budget come first; each card says whether it's in reach.
function marketTimingSection(state: AppState, actions: Actions, int: Interests): HTMLElement {
  const p = state.profile;
  const wrap = h("section", { class: "column", "aria-labelledby": "mt-h" });
  wrap.append(
    h("div", { class: "section-head" },
      h("h2", { id: "mt-h" }, "Market timing"),
      h("span", { class: "chip caution-chip" }, "Weak evidence")),
    h("p", { class: "muted small" },
      "Whether each market's prices look soft or stretched against their own recent trend. Treat it as context — not a reason to buy or sell."),
    timingEvidence(int),
  );
  if (state.signalsLoading) { wrap.append(h("p", { class: "muted" }, "Loading market data…")); return wrap; }
  const hdbBp = propertyBuyingPower(p, true, { sora: state.rates?.sora });
  const privBp = propertyBuyingPower(p, false, { sora: state.rates?.sora });
  const affordablePsf = privBp.maxPrice / (p.targetPrivateAreaSqft || 900);
  const has = hasFinances(p);
  const reach = (s: SeriesSignal): boolean | null => {
    if (!has || s.asset_class === "car" || s.latest_value == null) return null;
    const isPsf = s.metric === "median_psf" || s.metric === "new_sale_psf";
    return isPsf ? affordablePsf >= s.latest_value : hdbBp.maxPrice >= s.latest_value;
  };
  const shown = [...state.signals]
    // Only families where the backtest found the score predictive (P3 rule,
    // timingRule.ts); HDB timing is shown on the Property tab for information.
    .filter((s) => s.entry_score !== null && s.metric !== "district_psf" && s.metric !== "price_index" && segVisible(s, int) && timingInfluences(s.id))
    .map((s) => ({ s, inReach: reach(s) }))
    .sort((a, b) => Number(b.inReach === true) - Number(a.inReach === true) || (b.s.entry_score ?? 0) - (a.s.entry_score ?? 0));
  if (shown.length === 0) {
    wrap.append(h("p", { class: "muted" }, state.signalsError ? "Market timing will appear once market data loads."
      : state.signals.length > 0 ? "No timing read is used for the markets you follow — see the evidence above." : "No market data yet."));
    return wrap;
  }
  const grid = h("div", { class: "signal-grid" });
  for (const { s, inReach } of shown.slice(0, 6)) grid.append(signalCard(s, inReach, p.targetPrivateAreaSqft || 900));
  wrap.append(grid);
  wrap.append(h("button", { class: "link", onclick: () => actions.setTab(int.hdb || int.private ? "property" : "cars") }, "All markets and why each scores as it does →"));
  return wrap;
}

// ---------------------------------------------------------------- Property
export function renderProperty(state: AppState, actions: Actions): HTMLElement {
  const p = state.profile;
  const root = h("section", { class: "view" });
  root.append(h("h1", { class: "page-title" }, "Property"));

  const hdbBp = propertyBuyingPower(p, true, { sora: state.rates?.sora });
  const privBp = propertyBuyingPower(p, false, { sora: state.rates?.sora });
  const areaSqft = p.targetPrivateAreaSqft || 900;

  const int = getInterests(p);

  // Headline: buying power + indicative rate (always visible).
  root.append(
    h("div", { class: "tiles" },
      int.hdb ? tile("HDB buying power", hasFinances(p) ? fmtEstimate(hdbBp.maxPrice) : "—", hasFinances(p) ? `estimate · ${limitLabel(hdbBp.boundedBy)}` : "add income or savings in Profile") : null,
      int.private ? tile("Private buying power", hasFinances(p) ? fmtEstimate(privBp.maxPrice) : "—", hasFinances(p) ? `estimate · ${limitLabel(privBp.boundedBy)}` : "add income or savings in Profile") : null,
    ),
    mortgageRateLine(state),
  );

  // Buy intent — only when you already own. Switches the headline between
  // "sell current & upgrade" (replacement buyer) and "keep current & buy 2nd"
  // (ABSD + 45% LTV, existing mortgage still counts), so the numbers match plan.
  if (p.currentPropertyValue > 0) {
    const intent: "upgrade" | "second" = p.buyIntent === "second" ? "second" : "upgrade";
    const mkBtn = (val: "upgrade" | "second", label: string) =>
      h("button", { class: intent === val ? "chip chip-on" : "chip",
        onclick: () => { if (intent !== val) void actions.saveProfile({ ...state.profile, buyIntent: val }); } }, label);
    root.append(
      h("div", { class: "callout column" },
        h("span", { class: "field-label" }, "When you buy your next property:"),
        h("div", { class: "chip-row" },
          mkBtn("upgrade", "Sell current & upgrade"),
          mkBtn("second", "Keep current & buy 2nd")),
        h("p", { class: "muted small" }, intent === "second"
          ? "Buying power assumes you keep your current home — 20% ABSD, 45% LTV, and your existing mortgage still counts against TDSR."
          : "Buying power assumes you sell your current home to fund the next — mortgage discharged, equity freed as cash, no ABSD."),
      ),
    );
  }

  // Your targets — promoted to the top and shared across every panel below, so
  // private and HDB analysis are parametrised symmetrically:
  //   • private → target unit size (sqft), turns $psf into a unit price
  //   • HDB     → target flat type (its size proxy)
  // Committing either re-renders the whole tab so all downstream stays in sync.
  {
    const targetControls: HTMLElement[] = [];
    if (int.private) {
      const affordablePsf = privBp.maxPrice / (areaSqft || 900);
      const sizeInput = numberInput(areaSqft, (v) => {
        void actions.saveProfile({ ...state.profile, targetPrivateAreaSqft: v || 900 });
      }, "50", "change");
      targetControls.push(
        h("label", { class: "field" },
          h("span", { class: "field-label" }, "Target private size (sqft)"),
          sizeInput,
          h("span", { class: "field-hint" },
            `Buying power ≈ ${fmtPsf(affordablePsf, p.priceUnit ?? "psf")} at ${areaSqft} sqft — drives private summary & recommendations.`)),
      );
    }
    if (int.hdb) {
      const hdbType = hdbTarget(p);
      const typeSel = h("select", { onchange: () => void actions.saveProfile({ ...state.profile, targetHdbFlatType: (typeSel as HTMLSelectElement).value }) },
        h("option", { value: "", selected: hdbType === "" ? "selected" : null }, "Any flat type"),
        ...HDB_FLAT_TYPES.map((t) => h("option", { value: t, selected: t === hdbType ? "selected" : null }, t)),
      ) as HTMLSelectElement;
      targetControls.push(
        h("label", { class: "field" },
          h("span", { class: "field-label" }, "Target HDB flat type"),
          typeSel,
          h("span", { class: "field-hint" },
            `${hdbType || "All flat types"} — drives HDB recommendations & blended score.`)),
      );

      // Remaining-lease input — drives the lease-decay engine (CPF/LTV/tenure).
      const leaseVal = p.targetHdbRemainingLease ?? 0;
      const leaseInput = numberInput(
        leaseVal,
        (v) => void actions.saveProfile({ ...state.profile, targetHdbRemainingLease: v }),
        "1",
        "change",
      );
      const leaseFin = leaseVal > 0 ? hdbLeaseFinance(leaseVal, p.ageYears) : null;
      targetControls.push(
        h("label", { class: "field" },
          h("span", { class: "field-label" }, "HDB remaining lease (years)"),
          leaseInput,
          leaseFin
            ? h("span", { class: `field-hint ${leaseFin.risk === "low" ? "" : "warn"}` },
                `${RISK_LABEL[leaseFin.risk]} — ${leaseFin.note}`)
            : h("span", { class: "field-hint" },
                "Optional — remaining lease of the flat you're weighing. Under-100-yr leases can cut CPF use, LTV and loan tenure, and thin the future resale pool.")),
      );
    }
    if (targetControls.length) root.append(h("div", { class: "callout" }, h("div", { class: "row" }, ...targetControls)));
  }

  // Synthesised "what should I do now" verdict — the one-glance takeaway.
  root.append(whatToDoSummary(state, hdbBp, privBp, areaSqft, int));

  // One-time prompt when enough URA history has accumulated for trend insights.
  const trendPrompt = longitudinalPromptBanner(state, actions);
  if (trendPrompt) root.append(trendPrompt);

  // The analysis below is chunked into switchable sub-views (Evaluate /
  // Recommend / Trends / Sell & plan) so the tab reads as a short, scannable
  // page instead of one long scroll. The header above (buying power, targets,
  // "what to do") stays visible; the groups swap in place beneath the switcher.

  // Owned-property panels — only built when the user already owns a property.
  const sell = sellSignalPanel(state, actions);
  const planner = movePlanPanel(state, actions);

  // Market-timing series list (used inside the Trends group).
  const propSignals = state.signals.filter((s) => s.asset_class === "property" && s.metric !== "district_psf" && s.metric !== "price_index" && segVisible(s, int));
  const affordablePsf = privBp.maxPrice / (areaSqft || 900);
  const listHost = h("div", { class: "series-list" });
  if (propSignals.length === 0) listHost.append(h("p", { class: "muted" }, "No property series yet."));
  for (const s of propSignals) {
    let feas: ReturnType<typeof feasibility> | null = null;
    if (!isTimingOnly(s.metric)) {
      feas = s.metric === "median_psf" || s.metric === "new_sale_psf"
        ? feasibility(affordablePsf, s.latest_value ?? 0, decisionScore(s.id, s.entry_score))
        : feasibility(hdbBp.maxPrice, s.latest_value ?? 0, decisionScore(s.id, s.entry_score));
    }
    listHost.append(seriesRow(actions, s, feas));
  }
  const favCount = propSignals.filter((s) => s.verdict === "favourable").length;

  // Longitudinal trends — id used as the "See the trends →" scroll/reveal target.
  let trendsSec: HTMLElement | null = null;
  if (int.private) {
    const ready = state.trends?.ready ?? false;
    trendsSec = section("Longitudinal trends — district prices over time",
      longitudinalPanel(state, actions), ready && !p.longitudinalNotified, "prop-trends");
    trendsSec.id = "afm-trends";
  }

  // Agent-free transaction guide — id used as the shortlist "Evaluate & offer"
  // scroll/reveal target.
  const propClasses: FlowClass[] = [...(int.hdb ? ["hdb" as const] : []), ...(int.private ? ["private" as const] : [])];
  let diySec: HTMLElement | null = null;
  if (propClasses.length) {
    diySec = section("Cut out the agent — DIY transaction guide",
      diyPanel(state, actions, { hdb: hdbBp.maxPrice, private: privBp.maxPrice, car: 0 }, propClasses), false, "prop-diy");
    diySec.id = "afm-diy";
  }

  root.append(groupedSections("property", [
    { id: "evaluate", label: "Evaluate", nodes: [
      (int.hdb || int.private)
        ? section("Evaluate a listing — is it a buy?", evaluateListingPanel(state, actions, hdbBp.maxPrice, privBp.maxPrice, int), true, "prop-eval")
        : null,
      section(`Shortlist${(p.shortlist?.length ?? 0) > 0 ? ` · ${p.shortlist!.length}` : ""} — saved to evaluate & offer`,
        shortlistPanel(state, actions), (p.shortlist?.length ?? 0) > 0, "prop-shortlist"),
      // Compare — saved listings side by side vs their recent-transaction baselines.
      (p.shortlist?.length ?? 0) >= 2
        ? section("Compare — side by side", comparePanel(state, actions), true, "prop-compare")
        : null,
      (int.hdb || int.private)
        ? section("Under-valued units — below fair value", fairValuePanel(state, actions, hdbBp.maxPrice, privBp.maxPrice, int), false, "prop-undervalued")
        : null,
    ] },
    { id: "recommend", label: "Recommend", nodes: [
      section("Recommendations — where to look", recommendationsPanel(state, hdbBp.maxPrice, privBp.maxPrice, areaSqft, int, actions), true, "prop-recs"),
      int.private
        ? section("Developer clear-out watch — ABSD-deadline pressure", developerClearoutPanel(state, privBp.maxPrice), false, "prop-clearout")
        : null,
      (int.hdb || int.private)
        ? section("Best areas overall — blended score", blendedPanel(state, actions, hdbBp.maxPrice, privBp.maxPrice, areaSqft, int), false, "prop-blended")
        : null,
      section("Best areas by daily commute", commutePanel(state, actions), false, "prop-commute"),
    ] },
    { id: "trends", label: "Trends", nodes: [
      trendsSec,
      section(`Market timing by segment · ${propSignals.length} segments, ${favCount} favourable`,
        h("div", { class: "column" },
          int.private ? h("p", { class: "field-hint" }, `Private segments priced at ${areaSqft} sqft (change size at the top).`) : null,
          listHost), false, "prop-timing"),
      section("Future connectivity — upcoming MRT", connectivityPanel(state, int), false, "prop-mrt"),
    ] },
    { id: "plan", label: "Sell & plan", nodes: [
      sell ? section("Sell signal — your current property", sell, true, "prop-sell") : null,
      planner ? section("Sell → Buy planner — financing your next move", planner, true, "prop-planner") : null,
      diySec,
      section("Financing details (loan / cash / stamp duty)",
        h("div", { class: "callout" }, powerBreakdown("HDB / EC", hdbBp), powerBreakdown("Private (bank loan)", privBp)), false, "prop-financing"),
    ] },
  ]));

  root.append(h("p", { class: "disclaimer" }, disclaimer));
  return root;
}

// Synthesise afford + timing + (if owned) sell into a single top-of-page
// takeaway, so the answer to "what should I do now?" is one glance away.
function whatToDoSummary(
  state: AppState,
  hdbBp: ReturnType<typeof propertyBuyingPower>,
  privBp: ReturnType<typeof propertyBuyingPower>,
  areaSqft: number,
  int: Interests,
): HTMLElement {
  const p = state.profile;
  const affordablePsf = privBp.maxPrice / (areaSqft || 900);

  // Rank the tracked segments the user cares about by combined afford+timing.
  const picks = state.signals
    .filter((s) => s.asset_class === "property" && s.metric !== "price_index" && segVisible(s, int))
    .map((s) => {
      const isPsf = s.metric === "median_psf" || s.metric === "district_psf" || s.metric === "new_sale_psf";
      const power = isPsf ? affordablePsf : hdbBp.maxPrice;
      const feas = feasibility(power, s.latest_value ?? 0, decisionScore(s.id, s.entry_score));
      let label = s.segment;
      if (s.metric === "district_psf") {
        const d = Number(/\.d(\d{2})$/.exec(s.id)?.[1] ?? 0);
        label = `D${d} ${DISTRICT_NAMES[d] ?? ""}`.trim();
      }
      return { label, feas, verdict: s.verdict, ratio: feas.affordabilityRatio, score: feas.overallScore };
    });
  const affordable = picks.filter((x) => x.ratio >= 1).sort((a, b) => b.score - a.score);

  const bullets: HTMLElement[] = [];
  let lead: string;
  let tone: "good" | "bad" | "" = "";

  if (!hasFinances(p)) {
    lead = "Add your income and savings in Profile to see which markets are within reach.";
  } else if (affordable.length > 0) {
    tone = "good";
    lead = `${affordable.length} of the markets you follow ${affordable.length === 1 ? "is" : "are"} within your budget.`;
    for (const x of affordable.slice(0, 3)) {
      bullets.push(h("li", {},
        `${x.label}: costs ${Math.round(100 / x.ratio)}% of your budget · timing ${verdictLabel(x.verdict).toLowerCase()} · fit ${x.score}/100`));
    }
  } else if (picks.length > 0) {
    tone = "bad";
    const closest = [...picks].sort((a, b) => b.ratio - a.ratio)[0];
    lead = "Nothing in the tracked segments fits your buying power at this unit size yet.";
    bullets.push(h("li", {}, `Closest: ${closest.label} — your budget covers about ${(closest.ratio * 100).toFixed(0)}% of its typical price.`));
    bullets.push(h("li", {}, "Grow savings/income, extend tenure, or target a smaller unit / cheaper district to close the gap."));
  } else {
    lead = state.signalsError ? "Market data couldn't load — your buying power above still works. Retry from Home." : "Market data is still loading…";
  }

  // Sell / move angle if they own a property with a chosen segment.
  if (p.currentPropertyValue > 0 && p.currentPropertyType) {
    const sell = computeSellSignal(state.signals.find((s) => s.id === p.currentPropertyType));
    if (sell) {
      const verb = sell.verdict === "favourable" ? "looks favourable to sell"
        : sell.verdict === "hold" ? "leans toward holding" : "is neutral to sell";
      bullets.push(h("li", {}, `Your current property ${verb} (${sell.sellScore}/100) — see the Sell → Buy planner to sequence a move.`));
    }
  }

  // HDB/EC purchase-eligibility (income-ceiling) badge, when HDB is in scope.
  if (int.hdb) {
    const e = hdbEcEligibility(p);
    if (e.incomeKnown) {
      const nf = e.hdbNew.eligible
        ? "within"
        : `over by S$${Math.abs(e.hdbNew.deltaMonthly).toLocaleString()}/mo`;
      const ecOk = e.income <= ELIGIBILITY.ec.newTender;
      bullets.push(h("li", {},
        `Eligibility: household S$${e.income.toLocaleString()}/mo is ${nf} the S$${e.hdbNew.ceiling.toLocaleString()} ${e.applicant === "single" ? "singles " : ""}new-flat income ceiling; ${ecOk ? "within" : "over"} the EC ceiling. Details in Evaluate a listing.`));
    } else {
      bullets.push(h("li", {}, "Add your household income in Profile to check HDB/EC purchase eligibility (income ceilings)."));
    }
  }

  return h("div", { class: `verdict-hero ${tone}` },
    h("h2", {}, "What's within reach"),
    h("div", { class: "lead" }, lead),
    bullets.length ? h("ul", {}, ...bullets) : null,
    h("div", { class: "muted small" }, "Fit (0–100) blends how comfortably a market fits your budget with the experimental timing read" + (p.currentPropertyValue > 0 ? ", plus your sell signal" : "") + ". Details in the sections below."),
  );
}

// True on phone widths. Read at render time (re-renders on tab switch), so the
// long panels can default to collapsed on mobile to cut the scroll.
function isMobile(): boolean {
  try { return window.matchMedia("(max-width: 640px)").matches; } catch { return false; }
}

// the tab is short; expand what you need. `key` gives the section a stable
// identity independent of its summary text (which can carry live counts) so its
// open/closed state is remembered across re-renders; `mobileOpen` lets a section
// open on desktop stay collapsed on a phone (defaults to the desktop state).
function section(summary: string, content: HTMLElement, open = false, key?: string, mobileOpen = open): HTMLElement {
  const k = key ?? summary;
  const def = isMobile() ? mobileOpen : open;
  const isOpen = openSections.has(k) ? openSections.get(k)! : def;
  const el = h("details", { class: "section", open: isOpen ? "open" : null },
    h("summary", {}, summary),
    h("div", { class: "section-body" }, content)) as HTMLDetailsElement;
  el.addEventListener("toggle", () => openSections.set(k, el.open));
  return el;
}

// A group of sections shown together as one switchable sub-view of a tab.
interface ViewGroup { id: string; label: string; nodes: (HTMLElement | null)[]; }

// Segment a long tab into switchable groups shown one at a time, behind a
// sticky chip switcher — turning one endless scroll into a short, chunked view.
// The active group is remembered per tab across re-renders; empty groups drop
// out automatically. Falls back to a plain stack when there's ≤1 real group.
function groupedSections(tabKey: string, groups: ViewGroup[]): HTMLElement {
  const live = groups
    .map((g) => ({ id: g.id, label: g.label, nodes: g.nodes.filter((n): n is HTMLElement => !!n) }))
    .filter((g) => g.nodes.length > 0);
  const body = h("div", { class: "grouped" });
  if (live.length <= 1) {
    for (const g of live) for (const n of g.nodes) body.append(n);
    return body;
  }
  const panels = new Map<string, HTMLElement>();
  const chips = new Map<string, HTMLElement>();
  const subnav = h("div", { class: "subnav", role: "tablist" });
  const show = (id: string) => {
    activeGroups.set(tabKey, id);
    for (const [gid, p] of panels) p.hidden = gid !== id;
    for (const [gid, c] of chips) {
      c.className = gid === id ? "subchip on" : "subchip";
      c.setAttribute("aria-selected", gid === id ? "true" : "false");
    }
  };
  for (const g of live) {
    const chip = h("button", { class: "subchip", type: "button", role: "tab",
      "data-group": g.id, onclick: () => show(g.id) }, g.label);
    chips.set(g.id, chip);
    subnav.append(chip);
    const panel = h("div", { class: "subview", "data-group": g.id }, ...g.nodes);
    panels.set(g.id, panel);
    body.append(panel);
  }
  const saved = activeGroups.get(tabKey);
  show(saved && live.some((g) => g.id === saved) ? saved : live[0].id);
  return h("div", { class: "grouped-tab" }, subnav, body);
}

// Open a section by id and scroll to it — first revealing its sub-view group if
// it's currently hidden behind the switcher (used by the shortlist → offer and
// trends handoffs).
function revealSection(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  const panel = el.closest(".subview") as HTMLElement | null;
  if (panel?.hidden) {
    const gid = panel.getAttribute("data-group");
    (document.querySelector(`.subchip[data-group="${gid}"]`) as HTMLElement | null)?.click();
  }
  el.setAttribute("open", "");
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Sell signal for the user's existing property (shown only if they own one).
function sellSignalPanel(state: AppState, actions: Actions): HTMLElement | null {
  const p = state.profile;
  if (!(p.currentPropertyValue > 0)) return null;
  const box = h("div", { class: "callout column" });
  let currentType = p.currentPropertyType ?? "";
  const unit = p.priceUnit ?? "psf";
  // Options built from the actual market segments we have data for.
  const opt = (id: string, label: string) => h("option", { value: id, selected: id === currentType ? "selected" : null }, label);
  const hdbOpts = state.signals.filter((s) => s.metric === "median_price").sort((a, b) => a.id.localeCompare(b.id));
  const distOpts = state.signals
    .filter((s) => s.metric === "district_psf")
    .map((s) => ({ s, d: Number(/\.d(\d{2})$/.exec(s.id)?.[1] ?? 0) }))
    .sort((a, b) => a.d - b.d);
  const typeSel = h("select", {},
    h("option", { value: "" }, "Select your property's segment…"),
    h("optgroup", { label: "HDB (by flat type)" }, ...hdbOpts.map((s) => opt(s.id, s.segment))),
    h("optgroup", { label: "Private (by district)" }, ...distOpts.map(({ s, d }) => opt(s.id, `D${d} ${DISTRICT_NAMES[d] ?? ""}`))),
  ) as HTMLSelectElement;
  const content = h("div", { class: "column" });

  const rebuild = () => {
    clear(content);
    if (!currentType) { content.append(h("p", { class: "muted small" }, "Pick your property's segment to get a sell signal.")); return; }
    const sig = state.signals.find((s) => s.id === currentType);
    const sell = computeSellSignal(sig);
    if (!sell) { content.append(h("p", { class: "muted small" }, "No market data for that segment yet.")); return; }
    const equity = propertyEquity(p);
    const color = sell.sellScore >= 62 ? "var(--good)" : sell.sellScore <= 40 ? "var(--bad)" : "var(--neutral)";
    const label = sell.verdict === "favourable" ? "Favourable to sell" : sell.verdict === "hold" ? "Hold" : "Neutral";
    content.append(
      h("div", { class: "series-head" },
        h("div", {},
          h("div", { class: "verdict", style: `color:${color}` }, label),
          h("div", { class: "muted small" },
            `Your est. value ${fmtSGD(p.currentPropertyValue)} · equity ${fmtSGD(equity)} · market ${sell.metric === "district_psf" || sell.metric === "median_psf" || sell.metric === "new_sale_psf" ? fmtPsf(sell.latest, unit) : fmtMetric(sell.latest, sell.metric)} · 12m ${fmtPct(sell.momentum12m)}${sell.valuationZ != null ? ` · stretch z ${sell.valuationZ.toFixed(1)}` : ""}`),
        ),
        scoreGauge(sell.sellScore, "sell"),
      ),
      h("p", {}, sell.note),
    );
    if (sell.verdict === "favourable" && equity > 0) {
      content.append(h("p", { class: "muted small" },
        (() => {
          const sl = saleProceeds(holdingOf(p), p.useAgent !== false);
          return `Selling now would free ≈ ${fmtSGD(sl.cash)} in cash plus ${fmtSGD(sl.cpfRefund)} back to your CPF OA (after${sl.ssd > 0 ? " SSD," : ""} ${p.useAgent === false ? "legal fees (no agent)" : "agent and legal fees"} and the CPF refund) — see buying power below.`;
        })()));
    }
    if (sig) content.append(sourceNote(sig.source, sig.as_of));
  };

  typeSel.addEventListener("change", () => {
    currentType = typeSel.value;
    void actions.saveProfileQuiet({ ...state.profile, currentPropertyType: currentType });
    rebuild();
  });

  box.append(
    h("h3", {}, "Sell signal — your current property"),
    h("p", { class: "muted small" },
      "Whether now looks like a good time to sell, from the same market data as the buy signals — inverted, so a segment trading rich vs its trend favours selling."),
    h("div", { class: "row" }, field("Your property's segment", typeSel)),
    content,
  );
  rebuild();
  return box;
}

// Sell → Buy planner: models the four transaction sequences for someone who
// already owns a home, showing the CPF-vs-cash split from a sale, what the next
// purchase demands upfront, and the SG timing/tax gates that apply.
function movePlanPanel(state: AppState, actions: Actions): HTMLElement | null {
  const p = state.profile;
  if (!(p.currentPropertyValue > 0)) return null;

  const box = h("div", { class: "callout column" });
  const validScenario = MOVE_SCENARIOS.some((s) => s.id === p.movePlanScenario);
  let scenario: MoveScenario = validScenario ? (p.movePlanScenario as MoveScenario) : "sell-first";
  const curClass = classOf(p.currentPropertyType);
  let nextClass: PropClass = p.movePlanNextClass ?? curClass ?? "private";
  // Remembered target price, else a like-for-like figure so numbers show at once.
  let nextPrice = p.movePlanNextPrice && p.movePlanNextPrice > 0
    ? p.movePlanNextPrice
    : Math.round(p.currentPropertyValue) || 0;
  const remember = () => void actions.saveProfileQuiet({
    ...state.profile, movePlanScenario: scenario, movePlanNextClass: nextClass, movePlanNextPrice: nextPrice,
  });

  const result = h("div", { class: "column" });

  // Scenario chooser.
  const scenBtns = MOVE_SCENARIOS.map((s) =>
    h("button", {
      class: s.id === scenario ? "chip chip-on" : "chip",
      onclick: () => { scenario = s.id; syncScenBtns(); remember(); rebuild(); },
      title: s.blurb,
    }, s.label));
  const syncScenBtns = () => scenBtns.forEach((b, i) =>
    (b.className = MOVE_SCENARIOS[i].id === scenario ? "chip chip-on" : "chip"));

  // Next-property class toggle.
  const classBtns = (["hdb", "private"] as PropClass[]).map((c) =>
    h("button", {
      class: c === nextClass ? "chip chip-on" : "chip",
      onclick: () => { nextClass = c; syncClassBtns(); remember(); rebuild(); },
    }, c === "hdb" ? "HDB / EC" : "Private"));
  const syncClassBtns = () => classBtns.forEach((b, i) =>
    (b.className = (["hdb", "private"] as PropClass[])[i] === nextClass ? "chip chip-on" : "chip"));

  const priceInp = numberInput(nextPrice, (v) => { nextPrice = v || 0; remember(); rebuild(); }, "10000");

  const kv = (label: string, value: string, cls = "") =>
    h("div", { class: `mp-row ${cls}` }, h("span", { class: "muted small" }, label), h("span", {}, value));

  const rebuild = () => {
    clear(result);
    if (!curClass) {
      result.append(h("p", { class: "muted small" },
        "Set your current property's segment in the Sell signal above (or Profile → Holdings) so the planner knows if it's HDB or private."));
      return;
    }
    if (!(nextPrice > 0)) {
      result.append(h("p", { class: "muted small" }, "Enter the price of the next property to see the plan."));
      return;
    }
    const plan = computeMovePlan(p, { scenario, nextClass, nextPrice }, { sora: state.rates?.sora });

    const verdictColorV = plan.ok ? "var(--good)" : plan.warnings.some((w) => w.startsWith("⛔")) ? "var(--bad)" : "var(--neutral)";
    const verdictText = plan.warnings.some((w) => w.startsWith("⛔"))
      ? "Not possible as set out"
      : plan.ok ? "Financially workable" : "Funding gap — see below";
    result.append(h("div", { class: "verdict", style: `color:${verdictColorV}` }, verdictText));

    // Sale side.
    if (plan.sells) {
      result.append(h("div", { class: "mp-block" },
        h("div", { class: "mp-title" }, "Selling your current home"),
        kv("Estimated value", fmtSGD(plan.currentValue)),
        kv("Less outstanding loan", `− ${fmtSGD(plan.currentLoan)}`),
        plan.ssd > 0 ? kv("Less Seller's Stamp Duty", `− ${fmtSGD(plan.ssd)}`, "warn") : null,
        kv(p.useAgent === false ? "Less legal fees (no agent — DIY)" : "Less agent (2%) + legal fees", `− ${fmtSGD(plan.sellingCosts)}`),
        kv("Net sale proceeds", fmtSGD(plan.saleNetProceeds), "emph"),
        plan.sale.estimatedRefund
          ? kv(`→ Refunded to CPF OA (est. ${fmtSGD(plan.sale.cpfPrincipal)} used + ${fmtSGD(plan.sale.cpfAccruedInterest)} accrued interest)`, fmtSGD(plan.cpfRefund))
          : kv("→ Refunded to CPF OA (your CPF dashboard figure)", fmtSGD(plan.cpfRefund)),
        kv("→ Cash in hand", fmtSGD(plan.cashFromSale)),
      ));
    }

    // Purchase side.
    result.append(h("div", { class: "mp-block" },
      h("div", { class: "mp-title" }, `Buying the next home (${nextClass === "hdb" ? "HDB / EC" : "Private"})`),
      kv("Purchase price", fmtSGD(plan.nextPrice)),
      kv(`${plan.lender} — loan (LTV ${(plan.ltv * 100).toFixed(0)}%, ${plan.loanBoundedBy === "income" ? "income-capped" : "LTV-capped"})`, fmtSGD(plan.loan)),
      kv("Downpayment (cash/CPF)", fmtSGD(plan.downpayment)),
      kv("Buyer's Stamp Duty", fmtSGD(plan.bsd)),
      plan.absd > 0 ? kv(`ABSD (${((plan.absd / plan.nextPrice) * 100).toFixed(0)}%${plan.absdRefundable ? ", refundable if the first home sells within 6 months" : ""})`, fmtSGD(plan.absd), "warn") : null,
      kv("Total upfront needed", fmtSGD(plan.upfrontNeeded), "emph"),
      kv("— of which must be cash (min cash, ABSD, fees not payable by CPF)", fmtSGD(plan.cashNeeded)),
      kv(plan.proceedsAvailableAtPurchase ? "Funds available (cash + CPF + sale)" : "Funds available (cash + CPF)", fmtSGD(plan.fundsAvailable)),
      plan.shortfall > 0
        ? kv("Shortfall", fmtSGD(plan.shortfall), "warn")
        : kv("Surplus after purchase", fmtSGD(plan.fundsAvailable - plan.upfrontNeeded), "good"),
    ));

    // Monthly, and every loan option side by side (HDB loan vs bank tiers).
    result.append(h("p", { class: "muted small" },
      `Est. repayment ≈ ${fmtSGD(plan.monthlyAtMarketRate)}/mo over ${plan.tenureYears} yrs (stress-tested ${fmtSGD(plan.monthlyRepaymentStress)}/mo).`));
    if (plan.options.length > 1) result.append(lenderTable(plan.options.map((o) => ({ label: o.terms.label, eligible: o.terms.eligible, note: o.terms.note, used: o.terms.label === plan.lender, f: o }))));

    // Warnings / gates.
    if (plan.warnings.length > 0) {
      const wl = h("ul", { class: "mp-warn" });
      for (const w of plan.warnings) wl.append(h("li", { class: w.startsWith("⛔") ? "warn" : "" }, w));
      result.append(wl);
    }
  };

  box.append(
    h("h3", {}, "Sell → Buy planner"),
    h("p", { class: "muted small" },
      "Pick a sequence and the next home's price. The planner applies Singapore rules — Seller's Stamp Duty, HDB MOP, ABSD/LTV tiers, the Private→HDB wait-out — and splits your sale proceeds into CPF refund vs cash."),
    h("div", { class: "chip-row" }, ...scenBtns),
    h("div", { class: "row mp-inputs" },
      field("Next property type", h("div", { class: "chip-row" }, ...classBtns)),
      field("Next property price (SGD)", priceInp),
    ),
    result,
  );
  rebuild();
  return box;
}

// Sell signal for the user's existing car (shown only if they entered a value).
// Used-car values track COE, so a stretched COE category favours selling.
function carSellPanel(state: AppState, actions: Actions): HTMLElement | null {
  const p = state.profile;
  if (!(p.currentCarValue > 0)) return null;
  const box = h("div", { class: "callout column" });
  let cat = p.currentCarCategory ?? "";
  const catSel = h("select", {},
    h("option", { value: "" }, "Select COE category…"),
    ...(["cat-a", "cat-b", "cat-e"] as const).map((c) =>
      h("option", { value: c, selected: c === cat ? "selected" : null },
        c === "cat-a" ? "Cat A (small)" : c === "cat-b" ? "Cat B (large)" : "Cat E (open)")),
  ) as HTMLSelectElement;
  const content = h("div", { class: "column" });

  const rebuild = () => {
    clear(content);
    if (!cat) { content.append(h("p", { class: "muted small" }, "Pick your car's COE category to get a sell signal.")); return; }
    const sig = state.signals.find((s) => s.id === `coe.${cat}`);
    const sell = computeSellSignal(sig);
    if (!sell) { content.append(h("p", { class: "muted small" }, "No COE data for that category yet.")); return; }
    const equity = carEquity(p);
    const color = sell.sellScore >= 62 ? "var(--good)" : sell.sellScore <= 40 ? "var(--bad)" : "var(--neutral)";
    const label = sell.verdict === "favourable" ? "Favourable to sell" : sell.verdict === "hold" ? "Hold" : "Neutral";
    content.append(
      h("div", { class: "series-head" },
        h("div", {},
          h("div", { class: "verdict", style: `color:${color}` }, label),
          h("div", { class: "muted small" },
            `Your car value ${fmtSGD(p.currentCarValue)} · equity ${fmtSGD(equity)} · COE premium ${fmtMetric(sell.latest, sell.metric)} · 12m ${fmtPct(sell.momentum12m)}${sell.valuationZ != null ? ` · stretch z ${sell.valuationZ.toFixed(1)}` : ""}`),
        ),
        scoreGauge(sell.sellScore, "sell"),
      ),
      h("p", {}, sell.note),
    );
    if (sell.verdict === "favourable" && equity > 0) {
      content.append(h("p", { class: "muted small" }, `Selling now would free ≈ ${fmtSGD(equity)} toward your next car.`));
    }
  };

  catSel.addEventListener("change", () => {
    cat = catSel.value;
    void actions.saveProfileQuiet({ ...state.profile, currentCarCategory: cat as Profile["currentCarCategory"] });
    rebuild();
  });

  box.append(
    h("h3", {}, "Sell signal — your current car"),
    h("p", { class: "muted small" },
      "Used-car values track COE — when your category's COE is high vs its recent trend, used cars hold value, so it's a more favourable time to sell."),
    h("div", { class: "row" }, field("Your car's COE category", catSel)),
    content,
  );
  rebuild();
  return box;
}

// One blended score per area: affordability + market timing + commute. Applies
// to both HDB (town median for the flat type) and private (the town's primary
// district $psf × your target size), since commute is keyed to residential
// areas that hold both.
function blendedPanel(state: AppState, actions: Actions, hdbBudget: number, privBudget: number, areaSqft: number, int: Interests): HTMLElement {
  const box = h("div", { class: "callout column" });
  const content = h("div", { class: "column" });
  const tabPriv = h("button", { class: "tab active" }, "Private");
  const tabHdb = h("button", { class: "tab" }, "HDB");
  const select = (which: "priv" | "hdb") => {
    tabPriv.className = which === "priv" ? "tab active" : "tab";
    tabHdb.className = which === "hdb" ? "tab active" : "tab";
    clear(content);
    content.append(which === "priv" ? blendBody(state, actions, "private", privBudget, areaSqft) : blendBody(state, actions, "hdb", hdbBudget, areaSqft));
  };
  tabPriv.addEventListener("click", () => select("priv"));
  tabHdb.addEventListener("click", () => select("hdb"));

  box.append(
    h("h3", {}, "Best areas overall — one blended score"),
    h("p", { class: "muted small" },
      `Combines ${Math.round(BLEND_WEIGHTS.affordability * 100)}% affordability + ${Math.round(BLEND_WEIGHTS.timing * 100)}% market timing + ${Math.round(BLEND_WEIGHTS.commute * 100)}% commute into one 0–100 rating per area. Needs OneMap set up.`),
  );
  const subtabs = h("div", { class: "subtabs" });
  if (int.private) subtabs.append(tabPriv);
  if (int.hdb) subtabs.append(tabHdb);
  if (int.private && int.hdb) box.append(subtabs);
  box.append(content);
  select(int.private ? "priv" : "hdb");
  return box;
}

function blendBody(state: AppState, actions: Actions, kind: "hdb" | "private", budget: number, areaSqft: number): HTMLElement {
  const wrap = h("div", { class: "column" });
  const p = state.profile;
  const workInp = h("input", { type: "text", value: p.workLocationArea ?? "", placeholder: "your work" }) as HTMLInputElement;
  const spouseInp = h("input", { type: "text", value: p.spouseWorkLocationArea ?? "", placeholder: "spouse work (optional)" }) as HTMLInputElement;
  const schoolInp = schoolField("child's school (optional)", p.childSchool ?? "",
    (v) => void actions.saveProfileQuiet({ ...state.profile, childSchool: v.trim() }));
  const flatType = hdbTarget(p) || "4 ROOM";
  const host = h("div", { class: "comps" });

  // Private per-district $psf + timing, keyed by district number.
  const districtData = new Map<number, { psf: number | null; entry: number | null }>();
  if (kind === "private") {
    for (const s of state.signals) {
      if (s.metric !== "district_psf") continue;
      const m = /\.d(\d{2})$/.exec(s.id);
      if (m) districtData.set(Number(m[1]), { psf: s.latest_value, entry: s.entry_score });
    }
  }

  const btn = h("button", { class: "primary", onclick: async () => {
    const works = [workInp.value.trim(), spouseInp.value.trim()].filter(Boolean);
    if (works.length === 0) { host.replaceChildren(h("p", { class: "muted" }, "Enter at least one work location.")); return; }
    void actions.saveProfileQuiet({ ...state.profile, workLocationArea: workInp.value.trim(), spouseWorkLocationArea: spouseInp.value.trim(), childSchool: schoolInp.value.trim() });
    host.replaceChildren(h("p", { class: "muted" }, "Scoring areas — commute + prices + timing… (~20s)"));
    try {
      const commute = await fetchCommute(works, resolveSchoolQuery(schoolInp.value.trim()));
      if (!commute.areas) {
        host.replaceChildren(h("p", { class: "muted" },
          commute.configured === false ? "Needs OneMap set up for commute (see setup)." : commute.error ?? "No commute result."));
        return;
      }
      let medians: Record<string, number> = {};
      let hdbEntry: number | null = null;
      if (kind === "hdb") {
        medians = await fetchHdbTownMedians(flatType);
        const slug = flatType.toLowerCase().replace(/\s+/g, "-");
        hdbEntry = decisionScore(`hdb.resale.${slug}`, state.signals.find((s) => s.id === `hdb.resale.${slug}`)?.entry_score);
      }
      const results: BlendResult[] = commute.areas.map((a) => {
        let sum = 0; let cnt = 0;
        for (const l of a.legs) if (l.pt != null) { sum += l.pt; cnt++; }
        let townMedian: number | null;
        let entryScore: number | null;
        if (kind === "hdb") {
          townMedian = medians[a.area.toUpperCase()] ?? null;
          entryScore = hdbEntry;
        } else {
          const d = districtData.get(TOWN_DISTRICT[a.area.toUpperCase()] ?? -1);
          townMedian = d && d.psf ? Math.round(d.psf * areaSqft) : null; // district $psf × target size
          entryScore = d?.entry ?? null;
        }
        return {
          ...blendArea({ town: a.area, combinedPt: cnt > 0 ? sum : null, numDest: cnt > 0 ? cnt : a.legs.length, townMedian, buyingPower: budget, entryScore }),
        };
      });
      results.sort((x, y) => y.blended - x.blended);
      renderBlended(host, results, kind === "hdb"
        ? `Flat type: ${flatType}. Higher = better overall. Afford/Timing/Commute are the 0–100 components.`
        : `Private, priced at ${areaSqft} sqft using each area's district $psf. Higher = better overall. Afford/Timing/Commute are the 0–100 components.`);
    } catch (e) {
      host.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`));
    }
  } }, "Rank areas overall");

  wrap.append(
    h("p", { class: "muted small" }, kind === "hdb"
      ? `Priced for your target flat type (${flatType}).`
      : `Priced from each area's private district $psf at your target size (${areaSqft} sqft). Areas without enough URA data score affordability as neutral.`),
    h("div", { class: "row" },
      field("Your work", workInp),
      field("Spouse work", spouseInp),
      field("Child's school", schoolInp),
      btn),
    host,
  );
  return wrap;
}

function renderBlended(host: HTMLElement, results: BlendResult[], subtitle: string): void {
  clear(host);
  host.append(h("p", { class: "muted small" }, subtitle));
  const list = h("div", { class: "comp-list" });
  results.forEach((r, i) => {
    const color = r.blended >= 65 ? "var(--good)" : r.blended <= 45 ? "var(--bad)" : "var(--neutral)";
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" },
          `${i + 1}. ${titleCase(r.town)} — `,
          h("span", { style: `color:${color};font-weight:700` }, `${r.blended}/100`)),
        h("div", { class: "muted small" },
          `Afford ${r.affordability} · Timing ${r.timing ?? "not used"} · Commute ${r.commute}` +
          `${r.townMedian ? ` · median ${fmtSGD(r.townMedian)}` : ""}` +
          `${r.avgPt != null ? ` · ~${r.avgPt}m avg transit` : ""}`),
      ),
    );
  });
  host.append(list);
  host.append(sourceNote("Blended from URA & HDB transacted prices + OneMap routing"));
}

// Rank residential areas by daily commute (drive + public transport) to work
// and, optionally, a child's school. Routing via OneMap (SLA), official + free.
function commutePanel(state: AppState, actions: Actions): HTMLElement {
  const box = h("div", { class: "callout column" });
  const workInp = h("input", { type: "text", value: state.profile.workLocationArea ?? "", placeholder: "e.g. One-North, or a postal code" }) as HTMLInputElement;
  const spouseInp = h("input", { type: "text", value: state.profile.spouseWorkLocationArea ?? "", placeholder: "spouse workplace (optional)" }) as HTMLInputElement;
  const schoolInp = schoolField("child's school (optional)", state.profile.childSchool ?? "",
    (v) => void actions.saveProfileQuiet({ ...state.profile, childSchool: v.trim() }));
  const host = h("div", { class: "comps" });

  const btn = h("button", { class: "primary", onclick: async () => {
    const works = [workInp.value.trim(), spouseInp.value.trim()].filter(Boolean);
    if (works.length === 0) { host.replaceChildren(h("p", { class: "muted" }, "Enter at least one work location.")); return; }
    // Keep the profile in sync — these are profile fields, not throwaway filters.
    void actions.saveProfileQuiet({ ...state.profile, workLocationArea: workInp.value.trim(), spouseWorkLocationArea: spouseInp.value.trim(), childSchool: schoolInp.value.trim() });
    host.replaceChildren(h("p", { class: "muted" }, "Routing driving + public transport across areas… (~15–25s)"));
    try {
      const res = await fetchCommute(works, resolveSchoolQuery(schoolInp.value.trim()));
      renderCommute(host, res);
    } catch (e) {
      host.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`));
    }
  } }, "Rank areas by commute");

  box.append(
    h("h3", {}, "Best areas by daily commute"),
    h("p", { class: "muted small" },
      "Ranks residential areas by driving and public-transport time to both workplaces and (optionally) your child's school, at a weekday morning peak. Prefilled from your profile (edits here save back). Routing by OneMap (SLA)."),
    h("div", { class: "row" },
      field("Your work", workInp),
      field("Spouse work (optional)", spouseInp),
      field("Child's school (optional)", schoolInp),
      btn),
    host,
  );
  return box;
}

function renderCommute(host: HTMLElement, res: CommuteResult): void {
  clear(host);
  if (res.error || !res.areas || !res.destinations) {
    const msg = res.configured === false
      ? "Commute routing isn't set up yet — it needs a free OneMap account (see setup)."
      : res.error ?? "No result.";
    host.append(h("p", { class: "muted" }, msg));
    return;
  }
  const dests = res.destinations;
  host.append(h("p", { class: "muted small" },
    `${dests.map((d) => `${d.label}: ${d.name}`).join(" · ")}. Ranked by combined public-transport commute; 🚗 drive · 🚆 transit, minutes.`));
  const list = h("div", { class: "comp-list" });
  const fmt = (n: number | null) => (n == null ? "—" : `${n}m`);
  res.areas.forEach((a, i) => {
    const legs = a.legs.map((l, j) => `${dests[j]?.label ?? "?"}: 🚗 ${fmt(l.drive)} · 🚆 ${fmt(l.pt)}`).join("  │  ");
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" }, `${i + 1}. ${a.area}`),
        h("div", { class: "muted small" }, legs),
      ),
    );
  });
  host.append(list);
  host.append(sourceNote(`OneMap (SLA) — live routing · computed ${formatAsOf(new Date().toISOString())}`));
}

// Profile-driven shortlisting for ALL property types, using compliant open data
// only: real HDB transactions (unit-level) + private region/district guidance.
function recommendationsPanel(
  state: AppState,
  hdbBudget: number,
  privBudget: number,
  areaSqft: number,
  int: Interests,
  actions: Actions,
): HTMLElement {
  const box = h("div", { class: "callout column" });
  const content = h("div", { class: "column" });
  const tabPriv = h("button", { class: "tab active" }, "Private");
  const tabHdb = h("button", { class: "tab" }, "HDB");
  const select = (which: "priv" | "hdb") => {
    tabPriv.className = which === "priv" ? "tab active" : "tab";
    tabHdb.className = which === "hdb" ? "tab active" : "tab";
    clear(content);
    content.append(which === "priv" ? privateRecs(state, privBudget, areaSqft, actions) : hdbRecs(state, hdbBudget, actions));
  };
  tabPriv.addEventListener("click", () => select("priv"));
  tabHdb.addEventListener("click", () => select("hdb"));

  const subtabs = h("div", { class: "subtabs" });
  if (int.private) subtabs.append(tabPriv);
  if (int.hdb) subtabs.append(tabHdb);

  box.append(
    h("h3", {}, "Recommendations — what to shortlist"),
    h("p", { class: "muted small" },
      "Profile-driven, from official data only. Private is region/district-level plus real transacted units; HDB shows real transacted units by town."),
  );
  if (int.private && int.hdb) box.append(subtabs); // toggle only when both apply
  box.append(content);
  select(int.private ? "priv" : "hdb");
  return box;
}

// Evaluate a specific listing (that the user is considering) against the
// fair-value model → a BUY / CONSIDER / PASS, saved to the shortlist. You
// supply the listing; nothing is scraped.
function evaluateListingPanel(state: AppState, actions: Actions, hdbBudget: number, privBudget: number, int: Interests): HTMLElement {
  const box = h("div", { class: "callout column" });
  const content = h("div", { class: "column" });
  const tabPriv = h("button", { class: "tab active" }, "Private");
  const tabHdb = h("button", { class: "tab" }, "HDB");
  const select = (which: "priv" | "hdb") => {
    tabPriv.className = which === "priv" ? "tab active" : "tab";
    tabHdb.className = which === "hdb" ? "tab active" : "tab";
    clear(content);
    content.append(which === "priv" ? evalPrivateBody(state, actions, privBudget) : evalHdbBody(state, actions, hdbBudget));
  };
  tabPriv.addEventListener("click", () => select("priv"));
  tabHdb.addEventListener("click", () => select("hdb"));
  box.append(
    h("h3", {}, "Evaluate a listing"),
    h("p", { class: "muted small" }, "Enter a property you're considering — the model predicts its fair value and blends the asking-vs-fair gap with your buying power and market timing into a BUY / CONSIDER / PASS, saved to your shortlist. Your inputs stay on this device."),
  );
  const subtabs = h("div", { class: "subtabs" });
  if (int.private) subtabs.append(tabPriv);
  if (int.hdb) subtabs.append(tabHdb);
  if (int.private && int.hdb) box.append(subtabs);
  box.append(content);
  select(int.private ? "priv" : "hdb");
  return box;
}

// Transit access folded into a listing verdict, from the listing text or a
// OneMap nearest-MRT lookup.
// A minimal transit shape used across the verdict, shortlist and comps.
interface MrtLite { name?: string; code?: string; distanceM?: number; walkMin?: number }
interface MrtInfo extends MrtLite { line?: string; source?: "listing" | "onemap" }
// Walk-time thresholds (minutes): at or below NEAR is a clear plus; at or above
// FAR is a caution that soft-caps the verdict for a non-driver.
const MRT_NEAR_MIN = 6;
const MRT_FAR_MIN = 12;
function mrtWalkMin(m: MrtLite): number | null {
  if (m.walkMin != null && Number.isFinite(m.walkMin)) return m.walkMin;
  if (m.distanceM != null && Number.isFinite(m.distanceM)) return Math.max(1, Math.round(m.distanceM / 80));
  return null;
}
function mrtLabel(m: MrtInfo): string {
  const station = [m.name, m.code ? `(${m.code})` : ""].filter(Boolean).join(" ") || "nearest MRT";
  const wm = mrtWalkMin(m);
  const dist = m.distanceM != null ? `${m.distanceM < 1000 ? `${m.distanceM} m` : `${(m.distanceM / 1000).toFixed(1)} km`}` : "";
  const walk = wm != null ? `${m.source === "onemap" ? "~" : ""}${wm} min walk` : "";
  const tail = [dist, walk].filter(Boolean).join(", ");
  return `${station}${tail ? ` · ${tail}` : ""}`;
}
// A compact "🚇 station · N min" chip, coloured by walk time (green ≤6, red ≥12).
function mrtChip(m: MrtLite): HTMLElement {
  const wm = mrtWalkMin(m);
  const station = [m.name, m.code ? `(${m.code})` : ""].filter(Boolean).join(" ") || "MRT";
  const tail = wm != null ? ` · ${wm} min` : m.distanceM != null ? ` · ${m.distanceM} m` : "";
  const col = wm == null ? "" : wm <= MRT_NEAR_MIN ? "var(--good)" : wm >= MRT_FAR_MIN ? "var(--bad)" : "var(--neutral)";
  return h("span", { class: "chip", style: col ? `color:${col};border-color:${col}` : "",
    title: `Nearest MRT${m.distanceM != null ? ` · ~${m.distanceM} m walk` : ""}` }, `🚇 ${station}${tail}`);
}
// A lazy "🚇 nearest MRT" link that resolves via OneMap only when tapped, so a
// comps list doesn't fire N geocodes automatically. Replaces itself with a chip.
function mrtLazyLink(query: string, fallback?: string): HTMLElement {
  const wrap = h("span", {});
  if (!query.trim()) return wrap;
  const btn = h("button", { class: "link", onclick: async () => {
    (btn as HTMLButtonElement).disabled = true; btn.textContent = "🚇 …";
    try {
      const loc = await fetchLocate(query, fallback ? { street: fallback } : undefined);
      if (loc.found && loc.nearestMrt) wrap.replaceChildren(mrtChip(loc.nearestMrt));
      else wrap.replaceChildren(h("span", { class: "muted small" }, loc.mrtConfigured === false ? "MRT lookup off" : "no MRT found"));
    } catch { wrap.replaceChildren(h("span", { class: "muted small" }, "MRT lookup failed")); }
  } }, "🚇 nearest MRT") as HTMLButtonElement;
  wrap.append(btn);
  return wrap;
}

function evalResultView(state: AppState, actions: Actions, res: EvalResult, asking: number, affordable: boolean, entry: number | null, buildItem: (fair: number, verdict: "buy" | "consider" | "pass", gap: number) => ShortlistItem, strata?: StrataResult, mrt?: MrtInfo | null): HTMLElement {
  if (!res.ok) return h("p", { class: "muted" }, res.note ?? "Not enough comparable data to evaluate.");
  const base = listingVerdict({ asking, fair: res.fairValue, p10: res.p10, p90: res.p90, affordable, entryScore: entry });
  const gap = base.gap;
  // An underfunded MCST caps an otherwise-BUY at CONSIDER (never overrides a
  // PASS, never lifts). Shown transparently as its own factor.
  let verdict = base.verdict;
  const caps: string[] = [];
  if (strata && strata.band === "underfunded" && verdict === "buy") { verdict = "consider"; caps.push("strata health"); }
  // Transit access: for a buyer who doesn't drive, a far-from-MRT unit soft-caps
  // an otherwise-BUY at CONSIDER (same rule as strata — never overrides a PASS,
  // never lifts). Informational only when the user drives.
  const transitDependent = state.profile.transitDependent === true;
  const wm = mrt ? mrtWalkMin(mrt) : null;
  if (transitDependent && wm != null && wm >= MRT_FAR_MIN && verdict === "buy") { verdict = "consider"; caps.push("MRT distance"); }
  const capText = caps.length ? ` · capped by ${caps.join(" + ")}` : "";
  const pct = Math.round(gap * 100);
  const wrap = h("div", { class: `verdict-hero ${verdict === "buy" ? "good" : verdict === "pass" ? "bad" : ""}` });
  const mrtLi = (): HTMLElement | null => {
    if (!mrt || wm == null) {
      return transitDependent ? h("li", { class: "muted" }, "MRT access: not resolved — add a project name/address to check") : null;
    }
    const near = wm <= MRT_NEAR_MIN, far = wm >= MRT_FAR_MIN;
    const col = near ? "var(--good)" : far ? "var(--bad)" : "var(--neutral)";
    const tag = near ? "excellent" : far ? (transitDependent ? "far — you don't drive" : "far") : "moderate";
    return h("li", { style: transitDependent ? `color:${col}` : "" }, `MRT access: ${mrtLabel(mrt)} — ${tag}`);
  };
  // Range first, verdict second (U-04): the band is the evidence the verdict
  // is read from.
  if (res.p10 != null && res.p90 != null) {
    wrap.append(valueRange({
      p10: res.p10, p50: res.fairValue, p90: res.p90,
      marker: { value: asking, label: "Asking" },
      mapeOos: res.mapeOos, n: res.n, title: "Estimated value",
    }));
  }
  wrap.append(
    h("div", { class: "lead", style: `color:${verdictColour(verdict)}` }, `${VERDICT_LABEL[verdict]} · ${pct <= 0 ? `${Math.abs(pct)}% below fair value` : `${pct}% above fair value`} · ${base.position === "in-band" ? "within the normal range" : base.position === "below-band" ? "below the normal range" : "above the normal range"}${capText}`),
    h("div", { class: "muted small" }, res.p10 != null && res.p90 != null
      ? `A price inside the usual range isn't a bargain or a premium — differences that small are things the model can't see (facing, renovation, view).${res.projectKnown ? " This project is in the model." : ""}`
      : `Asking ${fmtSGD(asking)} vs model fair value ≈ ${fmtSGD(res.fairValue)} · fit on ${res.n} txns, R² ${(res.r2 * 100).toFixed(0)}%${res.projectKnown ? " · project in model" : ""}`),
    ...(res.imputed?.length ? [h("div", { class: "muted small" }, `Not provided, assumed typical for this segment: ${res.imputed.join(", ")}. Enter them for a sharper estimate.`)] : []),
    ...(res.extrapolated?.length ? [h("div", { class: "muted small", style: "color:var(--bad)" }, `Outside the range of recent sales: ${res.extrapolated.join(", ")} — treat the estimate with extra caution.`)] : []),
    h("ul", {},
      h("li", {}, affordable ? "Within your buying power" : "Above your buying power"),
      h("li", {}, entry == null ? "Market timing: no signal" : entry >= 62 ? "Market timing: favourable" : entry <= 40 ? "Market timing: stretched" : "Market timing: neutral"),
      mrtLi(),
      strata && strata.band !== "unknown"
        ? h("li", { style: `color:${strataColour(strata.band)}` }, `Strata/MCST health: ${STRATA_LABEL[strata.band]} (${strata.score}/100)`)
        : h("li", { class: "muted" }, "Strata/MCST health: not scored — add the fund figures above"),
    ),
  );
  if (strata && strata.band !== "unknown") wrap.append(strataFactorsView(strata));
  // En-bloc potential for the evaluated unit — private only. Transient here (no
  // itemId), so it fetches on demand; saving to the shortlist then persists it.
  const preview = buildItem(Math.round(res.fairValue), verdict, gap);
  // Only offer en-bloc signals when a real project name was given — the generic
  // "D15 private listing" fallback has nothing to search on.
  if (preview.klass === "private" && !/private listing$/i.test(preview.label)) {
    wrap.append(enblocPanel(state, actions, {
      project: preview.label, district: districtCode(preview.locality),
      tenure: preview.tenure ?? null, mrtMin: mrt ? mrtWalkMin(mrt) : null,
    }));
  }
  const saveBtn = h("button", { class: "primary", onclick: () => {
    toggleShortlist(state, actions, buildItem(Math.round(res.fairValue), verdict, gap));
    saveBtn.textContent = "Saved to shortlist ✓";
    (saveBtn as HTMLButtonElement).disabled = true;
  } }, "Save to shortlist");
  wrap.append(h("div", { class: "actions" }, saveBtn));
  return wrap;
}

// Expandable breakdown of the strata/MCST health factors.
function strataFactorsView(strata: StrataResult): HTMLElement {
  const dot = (v: string) => v === "good" ? "var(--good)" : v === "bad" ? "var(--bad)" : v === "warn" ? "var(--neutral)" : "var(--muted)";
  return h("details", { class: "section" },
    h("summary", { class: "muted small" }, `Strata/MCST health — why ${STRATA_LABEL[strata.band]} (${strata.score}/100)`),
    h("div", { class: "section-body column" },
      ...strata.factors.map((f) => h("div", { class: "muted small" },
        h("span", { style: `color:${dot(f.verdict)};font-weight:700` }, `● ${f.label}: `), f.note)),
      h("p", { class: "source-note" }, "Heuristic read of user-supplied MCST figures — not a rating. Verify against the §47 certificate and audited accounts."),
    ),
  );
}

// Set an input's value programmatically and route it through its existing
// onChange handler (which updates the closure variable driving the form).
function setInputValue(inp: HTMLInputElement, value: number | string): void {
  inp.value = String(value);
  inp.dispatchEvent(new Event("input", { bubbles: true }));
  inp.dispatchEvent(new Event("change", { bubbles: true }));
}
function setSelectValue(sel: HTMLSelectElement, value: string): void {
  sel.value = value;
  sel.dispatchEvent(new Event("change", { bubbles: true }));
}

// A "scan a screenshot to prefill" control. OCR runs entirely on-device via
// ocr.ts (Tesseract in the browser); the image never leaves this device.
// `prefill` applies the parsed fields and returns the human labels it filled.
function scanRow(prefill: (p: ParsedListing) => string[], opts: ParseOpts = {}): HTMLElement {
  const fileInp = h("input", { type: "file", accept: "image/*", style: "display:none" }) as HTMLInputElement;
  const status = h("span", { class: "muted small" });
  const btn = h("button", { class: "ghost", type: "button", onclick: () => fileInp.click() }, "📷 Scan screenshot to prefill") as HTMLButtonElement;
  fileInp.addEventListener("change", async () => {
    const file = fileInp.files?.[0];
    if (!file) return;
    btn.disabled = true;
    status.textContent = "Reading screenshot on your device…";
    try {
      const parsed = await scanListing(file, opts);
      const filled = prefill(parsed);
      status.textContent = filled.length
        ? `Prefilled ${filled.join(", ")} — check and correct, then Evaluate.`
        : "Couldn't read the key fields — please enter them manually.";
    } catch (e) {
      status.textContent = `Scan failed: ${(e as Error).message}`;
    } finally {
      btn.disabled = false;
      fileInp.value = "";
    }
  });
  return h("div", { class: "scan-row" }, btn, fileInp, status);
}

function evalPrivateBody(state: AppState, actions: Actions, privBudget: number): HTMLElement {
  const p = state.profile;
  const wrap = h("div", { class: "column" });
  let district = p.privateDistrictPick ?? "";
  const dSel = h("select", { onchange: () => { district = dSel.value; } },
    h("option", { value: "" }, "District…"),
    ...Array.from({ length: 28 }, (_, i) => i + 1).map((d) => h("option", { value: String(d).padStart(2, "0"), selected: String(d).padStart(2, "0") === district ? "selected" : null }, `D${d} — ${DISTRICT_NAMES[d] ?? ""}`)),
  ) as HTMLSelectElement;
  const projInp = h("input", { type: "text", placeholder: "Project name (optional, improves accuracy)" }) as HTMLInputElement;
  let asking = 0; const askInp = moneyInput(0, (v) => { asking = v; }, "change");
  let sqft = 0; const sqftInp = numberInput(0, (v) => { sqft = v; }, "10", "change");
  const typeSel = h("select", {}, ...PRIVATE_TYPES.map((t) => h("option", { value: t }, t))) as HTMLSelectElement;
  const tenureSel = h("select", {}, h("option", { value: "leasehold" }, "Leasehold (99-yr)"), h("option", { value: "freehold" }, "Freehold")) as HTMLSelectElement;
  let lease = 0; const leaseInp = numberInput(0, (v) => { lease = v; }, "1", "change");
  let privFloor = 0; const floorInp = numberInput(0, (v) => { privFloor = v; }, "1", "change");
  const host = h("div", {});
  const compsHost = h("div", { class: "comps" }); // past txns in the scanned project
  // Street/address captured from a scan, used (with the project) to anchor comps
  // and to resolve the nearest MRT.
  let scannedStreet = "";
  let scannedAddress = "";
  let mrt: MrtInfo | null = null; // transit access for the verdict
  let locating = false;
  let compSnap: CompSnapshot | undefined; // frozen listing-vs-recent-txns evidence
  const refLine = h("p", { class: "muted small" }); // scanned address + nearest MRT

  const updateRef = () => {
    const bits = [projInp.value.trim(), scannedAddress].filter(Boolean);
    const mrtStr = mrt ? ` · 🚇 ${mrtLabel(mrt)}` : "";
    refLine.textContent = bits.length || mrt ? `📍 ${bits.join(" · ")}${mrtStr}` : "";
  };
  // Resolve the nearest MRT (and fill the address) via OneMap when the listing
  // didn't state it. A listing-provided MRT always wins and skips the call.
  const resolveLocation = async () => {
    if (mrt && mrt.source === "listing") return;
    const q = (projInp.value.trim() || scannedAddress || scannedStreet).trim();
    if (!q || locating) return;
    locating = true;
    try {
      const loc = await fetchLocate(q);
      if (loc.found) {
        if (!mrt && loc.nearestMrt) mrt = { ...loc.nearestMrt, source: "onemap" };
        if (!scannedAddress && loc.address) scannedAddress = loc.address;
        updateRef();
      }
    } catch { /* non-fatal; verdict still works without transit */ } finally { locating = false; }
  };

  // Strata / MCST financial-health inputs (private only, all optional). No
  // public feed exists — the buyer brings these from the AGM audited accounts
  // or the BMSMA §47 certificate. Scored heuristically and folded into the
  // verdict below.
  const strata: StrataInputs = {};
  const unitsInp = numberInput(0, (v) => { strata.units = v || undefined; }, "1", "change");
  const contribInp = moneyInput(0, (v) => { strata.monthlyContribution = v || undefined; }, "change");
  const sinkInp = moneyInput(0, (v) => { strata.sinkingBalance = v || undefined; }, "change");
  const arrearsInp = numberInput(0, (v) => { strata.arrearsPct = v || undefined; }, "1", "change");
  const ageInp = numberInput(0, (v) => { strata.ageYears = v || undefined; }, "1", "change");
  const levyChk = h("input", { type: "checkbox", onchange: (e: Event) => { strata.specialLevy3y = (e.target as HTMLInputElement).checked; } }) as HTMLInputElement;
  const selfChk = h("input", { type: "checkbox", onchange: (e: Event) => { strata.selfManaged = (e.target as HTMLInputElement).checked; } }) as HTMLInputElement;
  const strataBox = h("details", { class: "section" },
    h("summary", {}, "Strata / MCST financial health (optional)"),
    h("div", { class: "section-body column" },
      h("p", { class: "muted small" }, "No public feed exists for a condo's fund health — bring these from the seller. Even with no numbers, work the checklist:"),
      h("ul", { class: "muted small" },
        h("li", {}, "Request the BMSMA §47 certificate — fund balances, arrears, and any levies (the authoritative source)."),
        h("li", {}, "Last 1–2 AGM minutes + audited financial statements."),
        h("li", {}, "Sinking-fund balance and whether it's growing; any planned major works (repainting, lifts, façade)."),
        h("li", {}, "Special levies in the last 3 years; management-fund surplus/deficit; owners in arrears."),
      ),
      h("div", { class: "row" }, field("Units in development", unitsInp), field("Monthly maint.+sinking ($/mth)", contribInp)),
      h("div", { class: "row" }, field("Sinking-fund balance ($)", sinkInp), field("Owners in arrears (%)", arrearsInp)),
      h("div", { class: "row" }, field("Development age (yrs)", ageInp), field("Special levy in last 3 yrs", levyChk), field("Self-managed (no agent)", selfChk)),
    ),
  ) as HTMLDetailsElement;

  // Pull the *same project's* (or street's) past transactions from URA data, so
  // the user can eyeball the listing against what's actually sold there — the
  // core comparison the model exists to make.
  const loadProjectComps = async () => {
    const project = projInp.value.trim();
    if (!project && !scannedStreet) { clear(compsHost); return; }
    compsHost.replaceChildren(h("p", { class: "muted small" }, `Loading past transactions in ${project || scannedStreet}…`));
    try {
      const query = { district: dSel.value, project: project || undefined, street: project ? undefined : (scannedStreet || undefined) };
      const { comps, cached, stats } = await fetchPrivateComps({ district: dSel.value, project, street: project ? "" : scannedStreet });
      clear(compsHost);
      const label = project || scannedStreet;
      // Freeze the "listing vs recent transactions" read for the shortlist.
      const rate = asking > 0 && sqft > 0 ? asking / sqft : 0;
      if (stats && stats.n > 0 && rate > 0) {
        compSnap = snapshotFrom(stats, rate, label, query);
        compsHost.append(marketPositionLine(compSnap));
      }
      if (comps.length === 0) {
        compsHost.append(h("p", { class: "muted small" },
          `No cached URA transactions found for "${label}". Comps come from the rolling ~6-month URA cache — try the district-wide "Under-valued units" section below for more.`));
        return;
      }
      compsHost.append(h("h3", { style: "font-size:16px" }, `Past transactions in ${label}`));
      renderPrivateComps(compsHost, comps, cached, state.profile.priceUnit ?? "psf");
    } catch (e) {
      compsHost.replaceChildren(h("p", { class: "muted small" }, `Couldn't load project transactions: ${(e as Error).message}`));
    }
  };

  const run = async () => {
    if (!dSel.value || !(asking > 0) || !(sqft > 0)) { host.replaceChildren(h("p", { class: "muted" }, "Enter district, asking price and floor area.")); return; }
    host.replaceChildren(h("p", { class: "muted" }, "Predicting fair value…"));
    try {
      const areaSqm = sqft / 10.7639;
      const freehold = tenureSel.value === "freehold";
      const res = await fetchEvaluatePrivate({ district: dSel.value, areaSqm, tenure: freehold ? "freehold" : "leasehold", remainingLease: lease, propertyType: typeSel.value, project: projInp.value.trim(), floor: privFloor || undefined });
      const dNum = Number(dSel.value);
      const affordable = asking <= privBudget;
      const entry = state.signals.find((s) => s.id === `private.resale.psf.d${dSel.value}`)?.entry_score ?? null;
      const strataRes = strataHealth({ ...strata, areaSqm });
      const buildItem = (fair: number, verdict: "buy" | "consider" | "pass", gap: number): ShortlistItem => ({
        id: `listing|priv|${(projInp.value.trim() || dSel.value)}|${Math.round(areaSqm)}|${asking}|${Date.now()}`,
        klass: "private", label: projInp.value.trim() || `D${dNum} private listing`, locality: `D${dNum} ${DISTRICT_NAMES[dNum] ?? ""}`.trim(),
        propertyType: typeSel.value, areaSqm: Math.round(areaSqm), refPrice: asking, fairValue: fair, pctBelow: gap,
        tenure: freehold ? "Freehold" : `${lease || "?"}y lease`, savedAt: new Date().toISOString(), source: "listing", verdict,
        strata: { ...strata },
        mrt: mrt ? { name: mrt.name, code: mrt.code, distanceM: mrt.distanceM, walkMin: mrt.walkMin } : undefined,
        comps: compSnap,
      });
      await resolveLocation(); // ensure nearest MRT is known before the verdict
      await loadProjectComps(); // capture the comp snapshot before the verdict is saveable
      host.replaceChildren(evalResultView(state, actions, res, asking, affordable, entry, buildItem, strataRes, mrt));
    } catch (e) { host.replaceChildren(h("p", { class: "muted" }, `Couldn't evaluate: ${(e as Error).message}`)); }
  };

  const scan = scanRow((parsed) => {
    const filled: string[] = [];
    if (parsed.mrtName || parsed.mrtCode || parsed.mrtWalkMin || parsed.mrtDistanceM) {
      mrt = { name: parsed.mrtName, code: parsed.mrtCode, distanceM: parsed.mrtDistanceM, walkMin: parsed.mrtWalkMin, source: "listing" };
      filled.push("nearest MRT");
    }
    if (parsed.project) { setInputValue(projInp, parsed.project); filled.push("project name"); }
    if (parsed.street) scannedStreet = parsed.street;
    if (parsed.address) scannedAddress = parsed.address;
    updateRef();
    // Resolve the nearest MRT via OneMap when the listing didn't state one.
    if (!mrt) void resolveLocation();
    if (parsed.district) { setSelectValue(dSel, parsed.district); filled.push("district"); }
    if (parsed.price) { setInputValue(askInp, parsed.price); filled.push("asking price"); }
    const ft = parsed.areaSqft ?? (parsed.areaSqm ? Math.round(parsed.areaSqm * 10.7639) : undefined);
    if (ft) { setInputValue(sqftInp, ft); filled.push("floor area"); }
    if (parsed.propertyType && PRIVATE_TYPES.includes(parsed.propertyType)) { setSelectValue(typeSel, parsed.propertyType); filled.push("property type"); }
    if (parsed.tenure) { setSelectValue(tenureSel, parsed.tenure); filled.push("tenure"); }
    if (parsed.remainingLease) { setInputValue(leaseInp, parsed.remainingLease); filled.push("remaining lease"); }
    let strataFilled = false;
    if (parsed.units) { setInputValue(unitsInp, parsed.units); filled.push("units"); strataFilled = true; }
    if (parsed.maintenanceFee) { setInputValue(contribInp, parsed.maintenanceFee); filled.push("maintenance fee"); strataFilled = true; }
    if (parsed.sinkingBalance) { setInputValue(sinkInp, parsed.sinkingBalance); filled.push("sinking-fund balance"); strataFilled = true; }
    if (strataFilled) strataBox.open = true;
    return filled;
  });

  // EC carries an income ceiling the rest of private does not — show it only
  // when Executive Condominium is the selected type.
  const ecNoteBox = h("div", {});
  const refreshEcNote = () => {
    clear(ecNoteBox);
    if (typeSel.value === "Executive Condominium") {
      const note = ecEligibilityNote(state.profile);
      if (note) ecNoteBox.append(note);
    }
  };
  typeSel.addEventListener("change", refreshEcNote);
  refreshEcNote();

  wrap.append(
    scan,
    refLine,
    h("div", { class: "row" }, field("District", dSel), field("Project (optional)", projInp)),
    h("div", { class: "row" }, field("Asking price", askInp), field("Floor area (sqft)", sqftInp)),
    h("div", { class: "row" }, field("Property type", typeSel), field("Tenure", tenureSel), field("Remaining lease (yrs, if leasehold)", leaseInp)),
    h("div", { class: "row" }, field("Floor (optional)", floorInp)),
    ecNoteBox,
    strataBox,
    h("div", { class: "actions" }, h("button", { class: "primary", onclick: run }, "Evaluate")),
    host,
    compsHost,
  );
  return wrap;
}

function evalHdbBody(state: AppState, actions: Actions, hdbBudget: number): HTMLElement {
  const p = state.profile;
  const wrap = h("div", { class: "column" });
  let town = p.hdbFilterTown ?? "";
  const townSel = h("select", { onchange: () => { town = townSel.value; } }, h("option", { value: "" }, "Town…"), ...HDB_TOWNS.map((t) => h("option", { value: t, selected: t === town ? "selected" : null }, titleCase(t)))) as HTMLSelectElement;
  const typeSel = h("select", {}, ...HDB_FLAT_TYPES.map((t) => h("option", { value: t, selected: t === hdbTarget(p) ? "selected" : null }, t))) as HTMLSelectElement;
  let asking = 0; const askInp = moneyInput(0, (v) => { asking = v; }, "change");
  let sqm = 0; const sqmInp = numberInput(0, (v) => { sqm = v; }, "1", "change");
  let storey = 0; const storeyInp = numberInput(0, (v) => { storey = v; }, "1", "change");
  let lease = 0; const leaseInp = numberInput(0, (v) => { lease = v; }, "1", "change");
  const host = h("div", {});
  const compsHost = h("div", { class: "comps" }); // recent transacted comps for the town
  const refLine = h("p", { class: "muted small" }); // scanned block/address + nearest MRT
  let scannedBlock = "";
  let scannedAddress = "";
  let mrt: MrtInfo | null = null;
  let locating = false;
  let compSnap: CompSnapshot | undefined; // frozen listing-vs-recent-txns evidence

  const updateRef = () => {
    const bits = [scannedBlock ? `Blk ${scannedBlock}` : "", scannedAddress].filter(Boolean);
    const mrtStr = mrt ? ` · 🚇 ${mrtLabel(mrt)}` : "";
    refLine.textContent = bits.length || mrt ? `📍 ${bits.join(" · ")}${mrtStr}` : "";
  };
  const resolveLocation = async () => {
    if (mrt && mrt.source === "listing") return;
    const q = (scannedAddress || [scannedBlock, titleCase(townSel.value || "")].filter(Boolean).join(" ")).trim();
    if (!q || locating) return;
    locating = true;
    try {
      const loc = await fetchLocate(q);
      if (loc.found) { if (!mrt && loc.nearestMrt) mrt = { ...loc.nearestMrt, source: "onemap" }; if (!scannedAddress && loc.address) scannedAddress = loc.address; updateRef(); }
    } catch { /* non-fatal */ } finally { locating = false; }
  };

  // Recent transacted flats in the same town/flat-type (data.gov HDB), with the
  // scanned block floated to the top for a like-for-like read.
  const loadTownComps = async () => {
    if (!townSel.value || !typeSel.value) { clear(compsHost); return; }
    compsHost.replaceChildren(h("p", { class: "muted small" }, `Loading recent ${typeSel.value} transactions in ${titleCase(townSel.value)}…`));
    try {
      const { comps, stats } = await fetchComps({ town: townSel.value, flatType: typeSel.value });
      clear(compsHost);
      // Freeze the "listing vs recent transactions" read (HDB priced per m²).
      const rate = asking > 0 && sqm > 0 ? asking / sqm : 0;
      if (stats && stats.n > 0 && rate > 0) {
        compSnap = snapshotFrom(stats, rate, `${titleCase(townSel.value)} ${typeSel.value}`, { town: townSel.value, flatType: typeSel.value });
        compsHost.append(marketPositionLine(compSnap));
      }
      if (comps.length === 0) { compsHost.append(h("p", { class: "muted small" }, "No recent transactions found for that town + flat type.")); return; }
      if (scannedBlock) comps.sort((a, b) => (b.block === scannedBlock ? 1 : 0) - (a.block === scannedBlock ? 1 : 0));
      compsHost.append(h("h3", { style: "font-size:16px" }, `Recent transactions — ${titleCase(townSel.value)} ${typeSel.value}`));
      renderComps(compsHost, comps, hdbBudget);
    } catch (e) {
      compsHost.replaceChildren(h("p", { class: "muted small" }, `Couldn't load transactions: ${(e as Error).message}`));
    }
  };

  const run = async () => {
    if (!townSel.value || !typeSel.value || !(asking > 0) || !(sqm > 0)) { host.replaceChildren(h("p", { class: "muted" }, "Enter town, flat type, asking price and floor area.")); return; }
    host.replaceChildren(h("p", { class: "muted" }, "Predicting fair value…"));
    try {
      const res = await fetchEvaluateHdb({ town: townSel.value, flatType: typeSel.value, areaSqm: sqm, storey, remainingLease: lease });
      const affordable = asking <= hdbBudget;
      const slug = typeSel.value.toLowerCase().replace(/\s+/g, "-");
      const entry = decisionScore(`hdb.resale.${slug}`, state.signals.find((s) => s.id === `hdb.resale.${slug}`)?.entry_score);
      const buildItem = (fair: number, verdict: "buy" | "consider" | "pass", gap: number): ShortlistItem => ({
        id: `listing|hdb|${townSel.value}|${sqm}|${asking}|${Date.now()}`,
        klass: "hdb", label: `${titleCase(townSel.value)} ${typeSel.value}`, locality: titleCase(townSel.value),
        propertyType: typeSel.value, areaSqm: sqm, refPrice: asking, fairValue: fair, pctBelow: gap,
        tenure: `99-yr lease${lease ? `, ${lease}y left` : ""}`, savedAt: new Date().toISOString(), source: "listing", verdict,
        mrt: mrt ? { name: mrt.name, code: mrt.code, distanceM: mrt.distanceM, walkMin: mrt.walkMin } : undefined,
        comps: compSnap,
      });
      await resolveLocation();
      await loadTownComps(); // capture the comp snapshot before the verdict is saveable
      host.replaceChildren(evalResultView(state, actions, res, asking, affordable, entry, buildItem, undefined, mrt));
    } catch (e) { host.replaceChildren(h("p", { class: "muted" }, `Couldn't evaluate: ${(e as Error).message}`)); }
  };

  const scan = scanRow((parsed) => {
    const filled: string[] = [];
    if (parsed.mrtName || parsed.mrtCode || parsed.mrtWalkMin || parsed.mrtDistanceM) {
      mrt = { name: parsed.mrtName, code: parsed.mrtCode, distanceM: parsed.mrtDistanceM, walkMin: parsed.mrtWalkMin, source: "listing" };
      filled.push("nearest MRT");
    }
    if (parsed.town && HDB_TOWNS.includes(parsed.town)) { setSelectValue(townSel, parsed.town); filled.push("town"); }
    if (parsed.flatType && HDB_FLAT_TYPES.includes(parsed.flatType)) { setSelectValue(typeSel, parsed.flatType); filled.push("flat type"); }
    if (parsed.block) scannedBlock = parsed.block;
    if (parsed.address) scannedAddress = parsed.address;
    updateRef();
    if (!mrt) void resolveLocation();
    if (parsed.price) { setInputValue(askInp, parsed.price); filled.push("asking price"); }
    const sm = parsed.areaSqm ?? (parsed.areaSqft ? Math.round(parsed.areaSqft / 10.7639) : undefined);
    if (sm) { setInputValue(sqmInp, sm); filled.push("floor area"); }
    if (parsed.floor) { setInputValue(storeyInp, parsed.floor); filled.push("storey"); }
    if (parsed.remainingLease) { setInputValue(leaseInp, parsed.remainingLease); filled.push("remaining lease"); }
    return filled;
  }, { towns: HDB_TOWNS });

  // Household-income eligibility strip — re-computed when the flat type changes.
  const eligBox = h("div", {});
  const refreshElig = () => { clear(eligBox); eligBox.append(hdbEligibilityView(state.profile, typeSel.value)); };
  typeSel.addEventListener("change", refreshElig);
  refreshElig();

  wrap.append(
    scan,
    refLine,
    h("div", { class: "row" }, field("Town", townSel), field("Flat type", typeSel)),
    eligBox,
    h("div", { class: "row" }, field("Asking price", askInp), field("Floor area (sqm)", sqmInp)),
    h("div", { class: "row" }, field("Floor / storey", storeyInp), field("Remaining lease (yrs)", leaseInp)),
    h("div", { class: "actions" }, h("button", { class: "primary", onclick: run }, "Evaluate")),
    host,
    compsHost,
  );
  return wrap;
}

// Under-valued units: fit a hedonic fair-value model on recent transacted
// prices within a comparable segment, and rank units that closed below the
// model's predicted fair value. Compliant — licensed transacted data only.
// Private and HDB share this panel via subtabs.
function fairValuePanel(state: AppState, actions: Actions, hdbBudget: number, privBudget: number, int: Interests): HTMLElement {
  const box = h("div", { class: "callout column" });
  const content = h("div", { class: "column" });
  const tabPriv = h("button", { class: "tab active" }, "Private");
  const tabHdb = h("button", { class: "tab" }, "HDB");
  const select = (which: "priv" | "hdb") => {
    tabPriv.className = which === "priv" ? "tab active" : "tab";
    tabHdb.className = which === "hdb" ? "tab active" : "tab";
    clear(content);
    content.append(which === "priv" ? privateFairValueBody(state, actions, privBudget) : hdbFairValueBody(state, actions, hdbBudget));
  };
  tabPriv.addEventListener("click", () => select("priv"));
  tabHdb.addEventListener("click", () => select("hdb"));

  box.append(
    h("h3", {}, "Under-valued units — below fair value"),
    h("p", { class: "muted small" },
      "Fits a fair-value model on recent real transactions in a comparable segment, then flags units that sold below what their attributes predict. Transacted data only — a screen for negotiation, not a valuation of any specific listing."),
    fairValueExplainer(),
  );
  const subtabs = h("div", { class: "subtabs" });
  if (int.private) subtabs.append(tabPriv);
  if (int.hdb) subtabs.append(tabHdb);
  if (int.private && int.hdb) box.append(subtabs);
  box.append(content);
  select(int.private ? "priv" : "hdb");
  return box;
}

// --- Shortlist -------------------------------------------------------------
// A module-level hook so saving from an under-valued list can refresh the
// shortlist panel in place (saves are quiet — no full re-render).
let refreshShortlist: (() => void) | null = null;
let refreshCompare: (() => void) | null = null;

function isShortlisted(state: AppState, id: string): boolean {
  return (state.profile.shortlist ?? []).some((x) => x.id === id);
}
function toggleShortlist(state: AppState, actions: Actions, item: ShortlistItem): void {
  const list = [...(state.profile.shortlist ?? [])];
  const idx = list.findIndex((x) => x.id === item.id);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(item);
  void actions.saveProfileQuiet({ ...state.profile, shortlist: list });
  refreshShortlist?.();
  refreshCompare?.();
}

// Saved projects/units, each with a buying-power check and a one-tap handoff to
// the agent-free offer flow (pre-fills the DIY flow + fair-value price).
function shortlistPanel(state: AppState, actions: Actions): HTMLElement {
  const wrap = h("div", { class: "callout column" });
  const host = h("div", { class: "comp-list" });
  const rebuild = () => {
    clear(host);
    const items = state.profile.shortlist ?? [];
    if (items.length === 0) {
      host.append(h("p", { class: "muted small" }, "Save units from the Under-valued panels (☆ Save) to collect projects to evaluate and act on."));
      return;
    }
    // Open the collapsible section once there's something to show (e.g. after
    // the first save, when this rebuild runs with the element in the DOM).
    host.closest("details")?.setAttribute("open", "");
    for (const it of [...items].reverse()) host.append(shortlistRow(state, actions, it));
  };
  refreshShortlist = rebuild;
  rebuild();
  wrap.append(
    h("h3", {}, "Shortlist"),
    h("p", { class: "muted small" }, "Projects you've saved from the under-valued analysis. Each shows whether it fits your buying power, plus a one-tap handoff to the agent-free offer flow anchored at the model's fair value."),
    enblocStatusLine(state),
    host,
  );
  return wrap;
}

// At-a-glance "en-bloc news feed" status, shown when the shortlist holds any
// private unit. Confirms whether FIRECRAWL_API_KEY is live on the Worker without
// opening a unit. Loads async; renders nothing until resolved (and nothing at
// all when there are no private units to which en-bloc applies).
function enblocStatusLine(state: AppState): HTMLElement {
  const line = h("p", { class: "muted small enbloc-status" });
  const hasPrivate = (state.profile.shortlist ?? []).some((s) => s.klass === "private");
  if (!hasPrivate) return line; // empty placeholder
  void fetchEnblocStatus().then((s) => {
    if (s == null) { line.textContent = ""; return; }
    line.replaceChildren(
      h("span", { class: `enbloc-dot ${s.configured ? "on" : "off"}` }, s.configured ? "🟢" : "⚪"),
      s.configured
        ? " En-bloc news feed: enabled — per-project checks fetch live collective-sale news."
        : " En-bloc news feed: not enabled — scoring still works from known attributes. Set FIRECRAWL_API_KEY on the Worker to add news.",
    );
  });
  return line;
}

function shortlistRow(state: AppState, actions: Actions, it: ShortlistItem): HTMLElement {
  const isHdb = it.klass === "hdb";
  const bp = propertyBuyingPower(state.profile, isHdb, { sora: state.rates?.sora });
  const fits = it.refPrice <= bp.maxPrice;
  const feasText = fits
    ? `Within your buying power (${fmtSGD(bp.maxPrice)})`
    : `Above your buying power by ${fmtSGD(it.refPrice - bp.maxPrice)}`;
  const offerBtn = h("button", { class: "primary", onclick: async () => {
    await actions.saveProfile({ ...state.profile, diyMode: "single", diyFlow: isHdb ? "hdb-buy" : "private-buy", diyPrice: Math.round(it.fairValue) });
    revealSection("afm-diy");
  } }, "Evaluate & offer →");
  const rm = h("button", { class: "ghost", onclick: () => toggleShortlist(state, actions, it) }, "Remove");
  const gapPct = Math.round(it.pctBelow * 100);
  const gapText = gapPct <= 0 ? `${Math.abs(gapPct)}% below fair` : `${gapPct}% above fair`;
  const priceLabel = it.source === "listing" ? "asking " : "";
  // Metadata chips live on their own wrapping row so they never crowd (and, on
  // narrow phones, overlap) the title line above them.
  const metaChips = [
    it.tenure ? h("span", { class: "chip" }, it.tenure) : null,
    it.mrt && (it.mrt.walkMin != null || it.mrt.distanceM != null) ? mrtChip(it.mrt) : null,
    it.comps ? compChip(it.comps) : null,
    strataBadge(it),
    // Surface a saved en-bloc read on the collapsed row, so the band is visible
    // without expanding the panel below (private units only).
    it.klass === "private" && it.enbloc ? enblocChip(it.enbloc) : null,
  ].filter(Boolean) as HTMLElement[];
  return h("div", { class: "comp" },
    h("div", { class: "comp-main" },
      it.verdict ? h("span", { class: "chip", style: `color:${verdictColour(it.verdict)};border-color:${verdictColour(it.verdict)};font-weight:700` }, VERDICT_LABEL[it.verdict]) : null,
      ` ${titleCase(it.label)} · ${priceLabel}${fmtSGD(it.refPrice)} `,
      h("span", { style: `color:${gapPct <= 0 ? "var(--good)" : "var(--bad)"};font-weight:700` }, gapText)),
    metaChips.length ? h("div", { class: "meta-chips" }, ...metaChips) : null,
    h("div", { class: "muted small" }, `${it.locality}${it.propertyType ? ` · ${titleCase(it.propertyType)}` : ""} · fair ≈ ${fmtSGD(it.fairValue)} · ${Math.round(it.areaSqm)} m²`),
    h("div", { class: "muted small", style: `color:${fits ? "var(--good)" : "var(--bad)"}` }, feasText),
    // En-bloc potential — private units only (the collective-sale regime and its
    // indicators are private-residential; HDB flats don't go en bloc).
    it.klass === "private"
      ? enblocPanel(state, actions, { project: it.label, district: districtCode(it.locality), tenure: it.tenure ?? null, mrtMin: it.mrt?.walkMin ?? null, itemId: it.id, snapshot: it.enbloc })
      : null,
    h("div", { class: "actions" }, offerBtn, rm),
  );
}

// ---- En-bloc potential (private shortlisted units) ------------------------
// A collapsible panel per private shortlist row. Shows a privacy-safe,
// district-level collective-sale digest by default, and — only on an explicit,
// opt-in tap — fetches this specific development's scored en-bloc insight from
// the Worker (which gathers via Firecrawl, scores, and persists it as a shared,
// validated corpus). A compact snapshot is remembered on the item too.
// Options let the panel serve both a saved shortlist row (with `itemId`, so a
// fetched snapshot is persisted for future reference) and a transient
// Evaluate-a-listing result (no itemId → fetch on demand, nothing persisted).
interface EnblocPanelOpts {
  project: string;
  district: string | null;
  tenure?: string | null;
  mrtMin?: number | null;
  itemId?: string;
  snapshot?: EnblocSnapshot | null;
}

// A compact chip summarising a saved en-bloc read (band colour + score/label),
// shown on the collapsed shortlist row so the verdict is visible without opening.
function enblocChip(snap: EnblocSnapshot): HTMLElement {
  const txt = snap.score != null && snap.band !== "insufficient" ? `🏗️ En-bloc ${snap.score}` : `🏗️ ${bandLabel(snap.band)}`;
  return h("span", { class: "chip", title: `En-bloc potential · ${bandLabel(snap.band)} · confidence ${snap.confidencePct}%`,
    style: `color:${bandColor(snap.band)};border-color:${bandColor(snap.band)}` }, txt);
}

function enblocPanel(state: AppState, actions: Actions, opts: EnblocPanelOpts): HTMLElement {
  const { project, district, snapshot } = opts;
  const body = h("div", { class: "section-body enbloc-body" });
  const scoreHost = h("div", { class: "enbloc-scorecard" });
  const digestHost = h("div", { class: "enbloc-digest muted small" });

  // Restore a previously-fetched snapshot immediately (persisted for reference).
  if (snapshot) scoreHost.append(enblocSnapshotView(snapshot));

  const consent = h("p", { class: "field-hint" },
    "Taps the app's server to fetch public collective-sale news for this project. " +
    "Only the project name is sent — your finances never leave this device.");

  let loading = false;
  const check = h("button", { class: "ghost small" }, snapshot ? "Refresh en-bloc signals" : "Check en-bloc signals");
  check.addEventListener("click", async () => {
    if (loading) return;
    loading = true;
    check.setAttribute("disabled", "true");
    check.textContent = "Checking…";
    try {
      const ins = await fetchEnblocInsight({ project, district, tenure: opts.tenure ?? null, mrtMin: opts.mrtMin ?? null });
      clear(scoreHost);
      scoreHost.append(enblocInsightView(ins));
      // Persist a compact snapshot on the item for future reference (local vault).
      if (opts.itemId) void actions.saveProfileQuiet(withEnblocSnapshot(state.profile, opts.itemId, snapshotOf(ins)));
    } catch {
      // Don't wipe an existing result on a transient failure — keep the last
      // snapshot (if any) and show a gentle inline note instead.
      const prev = opts.itemId
        ? (state.profile.shortlist ?? []).find((s) => s.id === opts.itemId)?.enbloc ?? snapshot
        : snapshot;
      clear(scoreHost);
      if (prev) scoreHost.append(enblocSnapshotView(prev));
      scoreHost.append(h("p", { class: "field-hint", style: "color:var(--neutral)" },
        "Couldn't reach the news feed just now (the crawl may be busy). Tap refresh to try again."));
    } finally {
      loading = false;
      check.removeAttribute("disabled");
      check.textContent = "Refresh en-bloc signals";
    }
  });

  body.append(digestHost, scoreHost, h("div", { class: "actions" }, check), consent);

  // Load the privacy-safe district digest lazily when the panel is first opened.
  const details = h("details", { class: "section enbloc" },
    h("summary", {},
      "🏗️ En-bloc potential",
      snapshot ? h("span", { class: "chip", style: `color:${bandColor(snapshot.band)};border-color:${bandColor(snapshot.band)}` },
        snapshot.score != null && snapshot.band !== "insufficient" ? `${snapshot.score}/100` : bandLabel(snapshot.band)) : null),
    body) as HTMLDetailsElement;
  let digestLoaded = false;
  details.addEventListener("toggle", () => {
    if (!details.open || digestLoaded) return;
    digestLoaded = true;
    void loadEnblocDigest(digestHost, district);
  });
  return details;
}

async function loadEnblocDigest(host: HTMLElement, district: string | null): Promise<void> {
  if (!district) { host.append("District unknown — per-project check still works below."); return; }
  host.append(`Loading ${district} collective-sale activity…`);
  const digest = await fetchEnblocDigest(district);
  clear(host);
  if (!digest || (digest.headlines.length === 0 && digest.projectsTracked === 0)) {
    host.append(digest && !digest.configured
      ? "District news feed not enabled yet. Per-project scoring still works from what's known."
      : `No recent ${district} collective-sale news on file yet.`);
    return;
  }
  host.append(h("div", { class: "enbloc-digest-head" },
    h("strong", {}, `${district} collective-sale activity`),
    digest.projectsTracked > 0 ? ` · ${digest.projectsTracked} tracked nearby` : ""));
  if (digest.headlines.length) host.append(enblocHeadlineList(digest.headlines.slice(0, 4)));
}

// Full scored insight view: band + score, confidence, indicators, outcome, news.
function enblocInsightView(ins: EnblocInsight): HTMLElement {
  const wrap = h("div", { class: "column" });
  const scoreText = ins.score != null && ins.band !== "insufficient" ? `${ins.score}/100` : "—";
  wrap.append(
    h("div", { class: "enbloc-head" },
      h("span", { class: "enbloc-score", style: `color:${bandColor(ins.band)}` }, scoreText),
      h("div", { class: "column", style: "gap:2px" },
        h("strong", { style: `color:${bandColor(ins.band)}` }, bandLabel(ins.band)),
        h("span", { class: "muted small" }, `Confidence ${ins.confidencePct}% · based on ${ins.indicators.filter((i) => i.present).length} of ${ins.indicators.length} indicators`)),
      outcomeLabel(ins.outcome) ? h("span", { class: "chip", style: `color:${ins.outcome === "sold" ? "var(--good)" : "var(--neutral)"};margin-left:auto` }, outcomeLabel(ins.outcome)!) : null),
  );
  wrap.append(h("p", { class: "muted small" }, ins.summary));

  const present = ins.indicators.filter((i) => i.present);
  if (present.length) {
    const list = h("div", { class: "enbloc-indicators" });
    for (const ind of present) list.append(enblocIndicatorRow(ind));
    wrap.append(list);
  }
  const absent = ins.indicators.filter((i) => !i.present);
  if (absent.length) {
    wrap.append(h("p", { class: "field-hint" },
      `Awaiting data for: ${absent.map((i) => i.label.replace(/\s*\(.*\)$/, "").toLowerCase()).join(", ")}.`));
  }

  if (ins.headlines.length) {
    wrap.append(h("div", { class: "enbloc-news-head muted small" }, "Recent collective-sale news"));
    wrap.append(enblocHeadlineList(ins.headlines.slice(0, 6)));
  }
  wrap.append(h("p", { class: "source-note" },
    `${ins.dataSource === "heuristic" ? "Computed from known attributes (news feed not enabled)." : "Public news via Firecrawl."} Last refreshed ${fmtDate(ins.lastRefreshed)}. Stored for future reference and re-validated over time.`));
  return wrap;
}

// One indicator: label, its value, a contribution bar, and learned hit-rate.
function enblocIndicatorRow(ind: EnblocIndicator): HTMLElement {
  const pct = Math.round((ind.sub ?? 0) * 100);
  const pred = ind.predictiveness != null
    ? h("span", { class: "chip small", title: `Historically present in ${Math.round(ind.predictiveness * 100)}% of tracked sites later sold en bloc (n=${ind.predictivenessN})` },
        `${Math.round(ind.predictiveness * 100)}% hist.`)
    : null;
  return h("div", { class: "enbloc-ind" },
    h("div", { class: "enbloc-ind-top" },
      h("span", { class: "enbloc-ind-label" }, ind.label.replace(/\s*\(.*\)$/, "")),
      ind.value ? h("span", { class: "muted small" }, ind.value) : null,
      pred),
    h("div", { class: "enbloc-bar" }, h("div", { class: "enbloc-bar-fill", style: `width:${pct}%` })));
}

function enblocHeadlineList(headlines: EnblocHeadline[]): HTMLElement {
  const list = h("div", { class: "enbloc-news" });
  for (const h0 of headlines) {
    list.append(h("a", { class: "enbloc-headline", href: h0.url, target: "_blank", rel: "noopener noreferrer" },
      h("span", { class: "chip small enbloc-kind" }, headlineKindLabel(h0.kind)),
      h("span", { class: "enbloc-headline-title" }, h0.title),
      h("span", { class: "muted small enbloc-headline-meta" }, [h0.source, h0.date ? fmtDate(h0.date) : null].filter(Boolean).join(" · "))));
  }
  return list;
}

// Compact restored-snapshot view (shown before a live refresh).
function enblocSnapshotView(s: NonNullable<ShortlistItem["enbloc"]>): HTMLElement {
  return h("div", { class: "column", style: "gap:4px" },
    h("div", { class: "enbloc-head" },
      h("span", { class: "enbloc-score", style: `color:${bandColor(s.band)}` }, s.score != null && s.band !== "insufficient" ? `${s.score}/100` : "—"),
      h("div", { class: "column", style: "gap:2px" },
        h("strong", { style: `color:${bandColor(s.band)}` }, bandLabel(s.band)),
        h("span", { class: "muted small" }, `Confidence ${s.confidencePct}%${outcomeLabel(s.outcome) ? ` · ${outcomeLabel(s.outcome)}` : ""}`))),
    h("p", { class: "source-note" }, `Saved ${fmtDate(s.fetchedAt)} — refresh for the latest.`));
}

// Immutably set an en-bloc snapshot on one shortlisted item.
function withEnblocSnapshot(p: Profile, itemId: string, snap: NonNullable<ShortlistItem["enbloc"]>): Profile {
  return { ...p, shortlist: (p.shortlist ?? []).map((s) => (s.id === itemId ? { ...s, enbloc: snap } : s)) };
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" });
}

// A compact MCST-health chip for a shortlisted unit that has strata figures.
function strataBadge(it: ShortlistItem): HTMLElement | null {
  if (!it.strata) return null;
  const sr = strataHealth({ ...it.strata, areaSqm: it.areaSqm });
  if (sr.band === "unknown") return null;
  return h("span", { class: sr.band === "underfunded" ? "chip warn" : "chip", title: "Strata / MCST financial health" }, `MCST ${STRATA_LABEL[sr.band]}`);
}

// ---- Side-by-side compare -------------------------------------------------
// Re-pull the recent-transaction stats for a saved listing and re-freeze its
// comp snapshot (used by the per-listing "↻ comps" refresh). Uses the frozen
// query where present, else reconstructs it from the item's fields.
async function refreshItemComps(state: AppState, actions: Actions, it: ShortlistItem): Promise<void> {
  const rate = listingRateOf(it);
  if (!(rate > 0)) return;
  let stats: CompStats | null = null;
  let query = it.comps?.query;
  const scope = it.comps?.scope ?? it.label;
  if (it.klass === "private") {
    const district = query?.district ?? (/D(\d+)/.exec(it.locality)?.[1] ?? "").padStart(2, "0");
    const generic = /private listing$/i.test(it.label);
    const project = query?.project ?? (it.source === "listing" && !generic ? it.label : undefined);
    query = { district, project, street: query?.street };
    stats = (await fetchPrivateComps({ district, project, street: query.street })).stats;
  } else {
    const town = query?.town ?? it.locality.toUpperCase();
    const flatType = query?.flatType ?? it.propertyType;
    query = { town, flatType };
    stats = (await fetchComps({ town, flatType })).stats;
  }
  if (stats && stats.n > 0) {
    const snap = snapshotFrom(stats, rate, scope, query);
    const list = (state.profile.shortlist ?? []).map((x) => (x.id === it.id ? { ...x, comps: snap } : x));
    await actions.saveProfileQuiet({ ...state.profile, shortlist: list });
  }
}

// A transposed, sticky-metric comparison table across selected saved listings —
// metric rows, one column per listing. Best like-for-like read on a narrow
// screen: the metric labels stay pinned while listing columns scroll sideways.
function comparePanel(state: AppState, actions: Actions): HTMLElement {
  const wrap = h("div", { class: "callout column" });
  const chipRow = h("div", { class: "chip-row" });
  const tableHost = h("div", {});

  const selectedIds = (): string[] => {
    const items = state.profile.shortlist ?? [];
    const ids = new Set(items.map((i) => i.id));
    const saved = (state.profile.compareIds ?? []).filter((id) => ids.has(id));
    if (saved.length) return saved;
    // Default: all user-entered listings, else everything saved.
    const listings = items.filter((i) => i.source === "listing").map((i) => i.id);
    return listings.length ? listings : items.map((i) => i.id);
  };

  const setSelected = (ids: string[]) => {
    void actions.saveProfileQuiet({ ...state.profile, compareIds: ids });
    rebuild();
  };

  const rebuild = () => {
    const items = state.profile.shortlist ?? [];
    clear(chipRow); clear(tableHost);
    if (items.length < 2) {
      tableHost.append(h("p", { class: "muted small" },
        "Evaluate and save at least two listings (above) to compare them side by side. Each keeps the recent-transaction read it was saved with, so you can weigh them as your buying decision progresses."));
      return;
    }
    const sel = new Set(selectedIds());
    // Selection chips — tap to include/exclude a listing from the comparison.
    for (const it of items) {
      const on = sel.has(it.id);
      chipRow.append(h("button", { class: on ? "chip chip-on" : "chip",
        onclick: () => {
          const next = new Set(selectedIds());
          if (next.has(it.id)) next.delete(it.id); else next.add(it.id);
          setSelected([...next]);
        } }, `${on ? "✓ " : ""}${titleCase(it.label)}`));
    }
    const cols = items.filter((it) => sel.has(it.id));
    if (cols.length < 2) {
      tableHost.append(h("p", { class: "muted small" }, "Select at least two listings above to compare."));
      return;
    }
    tableHost.append(compareTable(state, actions, cols));
  };
  refreshCompare = rebuild;
  rebuild();

  wrap.append(
    h("h3", {}, "Compare — side by side"),
    h("p", { class: "muted small" }, "Your saved listings against each other and against the last-6-months transactions each was measured on. Swipe the table sideways to see more; tap a name to add or drop it."),
    chipRow,
    tableHost,
  );
  return wrap;
}

// Build the transposed table element for the given listing columns.
function compareTable(state: AppState, actions: Actions, cols: ShortlistItem[]): HTMLElement {
  const rateCell = (it: ShortlistItem): string => {
    const r = listingRateOf(it);
    return r > 0 ? fmtPsf(r, it.klass === "hdb" ? "psm" : "psf") : "—";
  };
  const colour = (v: string) => `color:${v};font-weight:700`;
  // Each metric row: a label + a cell renderer per column.
  const row = (label: string, cell: (it: ShortlistItem) => Node | string): HTMLElement =>
    h("tr", {},
      h("th", { class: "cmp-metric", scope: "row" }, label),
      ...cols.map((it) => h("td", {}, cell(it))));

  const gapCell = (it: ShortlistItem): Node => {
    const p = Math.round(it.pctBelow * 100);
    return h("span", { style: colour(p <= 0 ? "var(--good)" : "var(--bad)") }, p <= 0 ? `${Math.abs(p)}% below` : `${p}% above`);
  };
  const vsMedianCell = (it: ShortlistItem): Node => {
    const s = it.comps;
    if (!s || s.n === 0) return document.createTextNode("—");
    const p = Math.round(s.pctVsMedian * 100);
    const col = s.pctVsMedian > 0.02 ? "var(--bad)" : s.pctVsMedian < -0.02 ? "var(--good)" : "var(--neutral)";
    return h("span", { style: colour(col) }, p === 0 ? "= median" : p > 0 ? `+${p}%` : `${p}%`);
  };
  const pctileCell = (it: ShortlistItem): string => {
    const s = it.comps;
    return s && s.n > 0 ? `${ordinal(Math.round(s.percentile * 100))} of ${s.n}` : "—";
  };
  const medianCell = (it: ShortlistItem): string => {
    const s = it.comps;
    return s && s.n > 0 ? fmtPsf(s.medianRate, s.unit) : "—";
  };
  const mrtCell = (it: ShortlistItem): string => {
    if (!it.mrt) return "—";
    const wm = mrtWalkMin(it.mrt);
    const nm = it.mrt.name ?? "MRT";
    return wm != null ? `${nm} · ${wm} min` : nm;
  };
  const fitsCell = (it: ShortlistItem): Node => {
    const bp = propertyBuyingPower(state.profile, it.klass === "hdb", { sora: state.rates?.sora });
    const fits = it.refPrice <= bp.maxPrice;
    return h("span", { style: colour(fits ? "var(--good)" : "var(--bad)") }, fits ? "✓ fits" : "✗ over");
  };
  const asOfCell = (it: ShortlistItem): string => {
    const s = it.comps;
    if (!s) return "—";
    return s.latestMonth ? `to ${s.latestMonth}` : (s.asOf ? s.asOf.slice(0, 10) : "—");
  };

  // Column headers: verdict chip + short name + a comp-refresh button.
  const headRow = h("tr", {},
    h("th", { class: "cmp-metric" }, "Listing"),
    ...cols.map((it) => {
      const refreshBtn = h("button", { class: "link small", title: "Re-pull recent transactions",
        onclick: async () => {
          refreshBtn.textContent = "↻ …"; (refreshBtn as HTMLButtonElement).disabled = true;
          try { await refreshItemComps(state, actions, it); } finally { refreshCompare?.(); refreshShortlist?.(); }
        } }, "↻ comps");
      return h("th", { class: "cmp-head" },
        it.verdict ? h("div", { class: "chip", style: `${colour(verdictColour(it.verdict))};border-color:${verdictColour(it.verdict)}` }, VERDICT_LABEL[it.verdict]) : null,
        h("div", { class: "cmp-name" }, titleCase(it.label)),
        h("div", { class: "muted small" }, it.locality),
        refreshBtn);
    }));

  const table = h("table", { class: "compare-table" },
    h("thead", {}, headRow),
    h("tbody", {},
      row("Asking", (it) => it.source === "listing" ? fmtSGD(it.refPrice) : `${fmtSGD(it.refPrice)} (txn)`),
      row(`Rate`, (it) => rateCell(it)),
      row("vs 6-mo median", (it) => vsMedianCell(it)),
      row("Market percentile", (it) => pctileCell(it)),
      row("6-mo median rate", (it) => medianCell(it)),
      row("Model fair value", (it) => fmtSGD(it.fairValue)),
      row("Gap vs fair", (it) => gapCell(it)),
      row("Size", (it) => `${Math.round(it.areaSqm)} m²`),
      row("Tenure", (it) => it.tenure ?? "—"),
      row("Nearest MRT", (it) => mrtCell(it)),
      row("Buying power", (it) => fitsCell(it)),
      row("Comps as of", (it) => asOfCell(it)),
    ),
  );
  const note = h("p", { class: "source-note" },
    "Rate is $psf for private, $/m² for HDB. “vs 6-mo median” and percentile are frozen from when each listing was saved — tap “↻ comps” on a column to re-pull the latest transactions.");
  return h("div", { class: "column" }, h("div", { class: "compare-wrap" }, table), note);
}

const VERDICT_LABEL: Record<string, string> = { buy: "BUY", consider: "CONSIDER", pass: "PASS" };
function verdictColour(v: string): string {
  return v === "buy" ? "var(--good)" : v === "pass" ? "var(--bad)" : "var(--neutral)";
}

// ---- Listing vs recent transactions (comp snapshot) -----------------------
// A listing's own $psf (private) or $/m² (HDB), from asking ÷ area.
function listingRateOf(it: { klass: "hdb" | "private"; refPrice: number; areaSqm: number }): number {
  if (!(it.areaSqm > 0) || !(it.refPrice > 0)) return 0;
  return it.klass === "hdb" ? it.refPrice / it.areaSqm : it.refPrice / (it.areaSqm * 10.7639);
}
// Freeze how a listing sits against the recent field: percentile from the full
// sorted rate array, gap vs the median. Percentile = share of comps at or below
// the listing's rate (higher = pricier than the field).
function snapshotFrom(stats: CompStats, listingRate: number, scope: string, query: CompSnapshot["query"]): CompSnapshot {
  const rates = stats.rates ?? [];
  const below = rates.filter((r) => r <= listingRate).length;
  const percentile = rates.length ? below / rates.length : 0.5;
  return {
    asOf: new Date().toISOString(),
    unit: stats.unit,
    listingRate: Math.round(listingRate),
    medianRate: Math.round(stats.medianRate),
    p25Rate: Math.round(stats.p25Rate), p75Rate: Math.round(stats.p75Rate),
    minRate: Math.round(stats.minRate), maxRate: Math.round(stats.maxRate),
    n: stats.n, months: stats.months,
    percentile,
    pctVsMedian: stats.medianRate > 0 ? listingRate / stats.medianRate - 1 : 0,
    latestMonth: stats.latestMonth, scope, query,
  };
}
// Ordinal suffix for a percentile (e.g. 78 -> "78th").
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
function pctVsMedianText(snap: CompSnapshot): string {
  const p = Math.round(snap.pctVsMedian * 100);
  return p === 0 ? "at the 6-mo median" : p > 0 ? `${p}% above the 6-mo median` : `${Math.abs(p)}% below the 6-mo median`;
}
// A one-line "asking is X% above/below the 6-mo median, Nth percentile of n
// sales" banner, shown above the comps list. Above the median leans caution.
function marketPositionLine(snap: CompSnapshot): HTMLElement {
  if (snap.n === 0) return h("p", { class: "muted small" }, "No recent transactions to compare against.");
  const col = snap.pctVsMedian > 0.02 ? "var(--bad)" : snap.pctVsMedian < -0.02 ? "var(--good)" : "var(--neutral)";
  const pctile = ordinal(Math.round(snap.percentile * 100));
  const thin = snap.n < 5 ? ` · thin sample (${snap.n}) — read with caution` : "";
  return h("div", { class: "callout column", style: `border-left:3px solid ${col};padding:8px 12px` },
    h("div", {}, h("strong", {}, `Asking ${fmtPsf(snap.listingRate, snap.unit)}`), " vs recent transactions"),
    h("div", { style: `color:${col};font-weight:700` }, pctVsMedianText(snap)),
    h("div", { class: "muted small" },
      `${pctile} percentile of ${snap.n} sale${snap.n === 1 ? "" : "s"} (last ${snap.months} mo) · median ${fmtPsf(snap.medianRate, snap.unit)} · range ${fmtPsf(snap.minRate ?? 0, snap.unit)}–${fmtPsf(snap.maxRate ?? 0, snap.unit)}${thin}`),
  );
}
// Compact market-position chip for a shortlist row.
function compChip(snap: CompSnapshot): HTMLElement | null {
  if (snap.n === 0) return null;
  const p = Math.round(snap.pctVsMedian * 100);
  const col = snap.pctVsMedian > 0.02 ? "var(--bad)" : snap.pctVsMedian < -0.02 ? "var(--good)" : "var(--neutral)";
  const txt = p === 0 ? "= 6-mo median" : p > 0 ? `+${p}% vs 6-mo median` : `${p}% vs 6-mo median`;
  return h("span", { class: "chip", style: `color:${col};border-color:${col}`,
    title: `${ordinal(Math.round(snap.percentile * 100))} percentile of ${snap.n} sales (last ${snap.months} mo)` }, txt);
}

// A ☆/★ save toggle button for an under-valued unit.
function saveButton(state: AppState, actions: Actions, item: ShortlistItem): HTMLElement {
  const btn = h("button", { class: "chip", onclick: () => {
    toggleShortlist(state, actions, item);
    const on = isShortlisted(state, item.id);
    btn.textContent = on ? "★ Saved" : "☆ Save";
    btn.className = on ? "chip chip-on" : "chip";
  } }) as HTMLElement;
  const on = isShortlisted(state, item.id);
  btn.textContent = on ? "★ Saved" : "☆ Save";
  btn.className = on ? "chip chip-on" : "chip";
  return btn;
}

// Current private filter values from the profile.
function privFilterParams(p: Profile): Pick<PrivateFilters, "propertyType" | "tenure" | "maxPrice"> {
  return {
    propertyType: p.privFilterType || "",
    tenure: (p.privFilterTenure as PrivateFilters["tenure"]) || "",
    maxPrice: p.privFilterMaxPrice && p.privFilterMaxPrice > 0 ? p.privFilterMaxPrice : undefined,
  };
}

// Shared private filter controls (type / tenure / max price), persisted to the
// profile. `onApply` runs when a value changes so the caller can re-fetch.
function privFilterControls(state: AppState, actions: Actions, onApply: () => void): HTMLElement {
  const p = state.profile;
  const typeSel = h("select", { onchange: () => { void actions.saveProfileQuiet({ ...state.profile, privFilterType: (typeSel as HTMLSelectElement).value }); onApply(); } },
    h("option", { value: "" }, "Any type"),
    ...PRIVATE_TYPES.map((t) => h("option", { value: t, selected: t === (p.privFilterType ?? "") ? "selected" : null }, t)),
  ) as HTMLSelectElement;
  const tenureSel = h("select", { onchange: () => { void actions.saveProfileQuiet({ ...state.profile, privFilterTenure: (tenureSel as HTMLSelectElement).value as Profile["privFilterTenure"] }); onApply(); } },
    ...([["", "Any tenure"], ["freehold", "Freehold"], ["leasehold", "Leasehold"]] as const).map(([v, l]) =>
      h("option", { value: v, selected: v === (p.privFilterTenure ?? "") ? "selected" : null }, l)),
  ) as HTMLSelectElement;
  const maxInp = moneyInput(p.privFilterMaxPrice ?? 0, (v) => { void actions.saveProfileQuiet({ ...state.profile, privFilterMaxPrice: v }); onApply(); }, "change");
  return h("div", { class: "row" },
    field("Type", typeSel),
    field("Tenure", tenureSel),
    field("Max price (0 = any)", maxInp),
  );
}

// Plain-language explainer of the hedonic fair-value method, collapsed.
function fairValueExplainer(): HTMLElement {
  const li = (label: string, text: string) => h("li", {}, h("strong", {}, label + ": "), text);
  return h("details", { class: "section why" },
    h("summary", { class: "muted small" }, "How fair value is determined"),
    h("div", { class: "section-body" },
      h("p", { class: "muted small" },
        "\"Fair value\" is the price a statistical model predicts for a unit from its own recent comparable sales — so a unit priced below it is cheap for what it is, not just cheap in absolute terms."),
      h("p", { class: "field-label" }, "The method"),
      h("ol", { class: "diy-list" },
        li("Comparable set", "We take the actual transactions in a tight segment — HDB: your town + flat type over the last 6 months; private: the district from the cached URA window — so like is compared with like."),
        li("Hedonic regression", "We fit a least-squares regression of log(price) on the attributes that drive value. Using log(price) means each attribute acts as a percentage effect, and the model learns, from the data itself, how much each one is worth in that segment."),
        li("Attributes (HDB)", "Floor area, storey height (mid of the range), remaining lease (all HDB is 99-year leasehold, so lease decay is the tenure signal), and the transaction month to de-trend the window."),
        li("Attributes (private)", "Floor area, transaction month, tenure (a freehold premium plus, for leasehold, the remaining-lease years), property type, and project fixed effects — i.e. each unit is compared against its own project's peers, the strongest \"cheap for what it is\" control. Sparse projects/types are pooled; the model reports whether project effects were used."),
        li("Fair value & discount", "Each unit's predicted price is its fair value; the discount is actual ÷ fair − 1. A negative figure (e.g. −8%) means it transacted 8% under the model — and the list is ranked by the biggest discounts."),
        li("Reliability", "We show the sample size and R² (share of price variation the model explains). A model needs enough transactions (≥25 HDB / ≥30 private) or it declines rather than guess; a low R² means treat the discounts as rough."),
      ),
      h("p", { class: "muted small" },
        "Limits: the model sees only transacted attributes — never a unit's condition, renovation, view, facing, or en-bloc potential. So a large \"discount\" can also flag a real negative the model can't see (a poor facing, an odd layout). Use it to shortlist and negotiate, then verify each unit yourself."),
    ),
  );
}

function hdbFairValueBody(state: AppState, actions: Actions, hdbBudget: number): HTMLElement {
  const p = state.profile;
  const wrap = h("div", { class: "column" });
  const flatType = hdbTarget(p);
  let town = p.hdbFilterTown ?? "";
  const townSel = h("select", { onchange: () => { town = townSel.value; void actions.saveProfileQuiet({ ...state.profile, hdbFilterTown: town }); } },
    h("option", { value: "" }, "Pick a town…"),
    ...HDB_TOWNS.map((t) => h("option", { value: t, selected: t === town ? "selected" : null }, titleCase(t))),
  ) as HTMLSelectElement;
  const host = h("div", { class: "comps" });
  const run = async () => {
    if (!townSel.value) { host.replaceChildren(h("p", { class: "muted" }, "Pick a town above.")); return; }
    if (!flatType) { host.replaceChildren(h("p", { class: "muted" }, "Set a target flat type at the top of the tab (not \"Any\").")); return; }
    host.replaceChildren(h("p", { class: "muted" }, "Fitting fair-value model on recent transactions…"));
    const f = { maxPrice: state.profile.hdbFilterMaxPrice || undefined, minLease: state.profile.hdbFilterMinLease || undefined };
    const mkItem = (u: FairValueResponse["units"][number]): ShortlistItem => ({
      id: `hdb|${u.block}|${u.street}|${u.area_sqm}|${u.price}|${u.month}`,
      klass: "hdb", label: `${titleCase(u.street)} Blk ${u.block}`, locality: titleCase(townSel.value),
      propertyType: flatType, areaSqm: u.area_sqm, refPrice: u.price, fairValue: Math.round(u.fairValue),
      pctBelow: u.pctBelow, tenure: `99-yr lease · ${u.remaining_lease}`, savedAt: new Date().toISOString(),
    });
    try { renderFairValue(host, await fetchUndervaluedHdb(townSel.value, flatType, f), hdbBudget, state, actions, mkItem); }
    catch (e) { host.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`)); }
  };
  // Filters — max price + min remaining lease, mirroring the private panel.
  const onFilter = () => { if (townSel.value && flatType && host.childElementCount > 0) void run(); };
  const maxInp = moneyInput(p.hdbFilterMaxPrice ?? 0, (v) => { void actions.saveProfileQuiet({ ...state.profile, hdbFilterMaxPrice: v }); onFilter(); }, "change");
  const leaseInp = numberInput(p.hdbFilterMinLease ?? 0, (v) => { void actions.saveProfileQuiet({ ...state.profile, hdbFilterMinLease: v }); onFilter(); }, "5", "change");
  wrap.append(
    h("p", { class: "muted small" }, "Model: size, floor, remaining lease (all HDB flats are 99-year leasehold, so lease decay is the tenure signal) and recency, over the last 6 months in your town + target flat type. Filters narrow the results shown, not the model fit."),
    h("div", { class: "row" }, field("Town", townSel), field("Flat type (set at top)", h("input", { type: "text", value: flatType || "Any", disabled: "disabled" })),
      h("button", { class: "primary", onclick: run }, "Find under-valued")),
    h("div", { class: "row" }, field("Max price (0 = any)", maxInp), field("Min remaining lease (yrs)", leaseInp)),
    host,
  );
  return wrap;
}

function privateFairValueBody(state: AppState, actions: Actions, privBudget: number): HTMLElement {
  const p = state.profile;
  const wrap = h("div", { class: "column" });
  let district = p.privateDistrictPick ?? "";
  const dSel = h("select", { onchange: () => { district = dSel.value; void actions.saveProfileQuiet({ ...state.profile, privateDistrictPick: district }); } },
    h("option", { value: "" }, "Pick a district…"),
    ...Array.from({ length: 28 }, (_, i) => i + 1).map((d) =>
      h("option", { value: String(d).padStart(2, "0"), selected: String(d).padStart(2, "0") === district ? "selected" : null }, `D${d} — ${DISTRICT_NAMES[d] ?? ""}`)),
  ) as HTMLSelectElement;
  const host = h("div", { class: "comps" });
  const run = async () => {
    if (!dSel.value) { host.replaceChildren(h("p", { class: "muted" }, "Pick a district above.")); return; }
    host.replaceChildren(h("p", { class: "muted" }, "Fitting fair-value model on cached URA transactions…"));
    const dNum = Number(dSel.value);
    const mkItem = (u: PrivateFairValueResponse["units"][number]): ShortlistItem => ({
      id: `priv|${u.project}|${u.area_sqm}|${u.price}|${u.month}`,
      klass: "private", label: u.project || u.street, locality: `D${dNum} ${DISTRICT_NAMES[dNum] ?? ""}`.trim(),
      propertyType: u.property_type, areaSqm: u.area_sqm, refPrice: u.price, fairValue: Math.round(u.fairValue),
      pctBelow: u.pctBelow, tenure: leaseLabel({ freehold: u.freehold, leaseLeft: u.leaseLeft, raw: u.tenure }), savedAt: new Date().toISOString(),
    });
    try { renderPrivateFairValue(host, await fetchUndervaluedPrivate(dSel.value, privFilterParams(state.profile)), privBudget, state.profile.priceUnit ?? "psf", state, actions, mkItem); }
    catch (e) { host.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`)); }
  };
  // Re-run automatically when a filter changes, but only if results are showing.
  const onFilter = () => { if (dSel.value && host.childElementCount > 0) void run(); };
  wrap.append(
    h("p", { class: "muted small" }, "Model: size, recency, tenure (freehold premium vs remaining lease for leasehold), property type and project fixed effects (a unit vs its own project's peers), over cached URA transactions in the district. Filters narrow the results shown, not the model fit."),
    h("div", { class: "row" }, field("District", dSel), h("button", { class: "primary", onclick: run }, "Find under-valued")),
    privFilterControls(state, actions, onFilter),
    host,
  );
  return wrap;
}

function renderPrivateFairValue(
  host: HTMLElement, res: PrivateFairValueResponse, budget: number, unit: "psf" | "psm",
  state: AppState, actions: Actions, mkItem: (u: PrivateFairValueResponse["units"][number]) => ShortlistItem,
): void {
  clear(host);
  if (!res.ok || res.units.length === 0) {
    host.append(h("p", { class: "muted" }, res.note ?? "No under-valued units found in that district right now."));
    return;
  }
  host.append(h("p", { class: "muted small" },
    `Model fit on ${res.n} transactions · R² ${(res.r2 * 100).toFixed(0)}%${res.projectFE ? " · project fixed effects" : " · district-level (too few per-project sales for project effects)"}.${res.band ? ` Only units below the normal range are listed: comparable sales fall between ${Math.round(res.band.lowPct * 100)}% and +${Math.round(res.band.highPct * 100)}% of fair value (P10–P90 on held-out sales), so smaller gaps are noise.` : ""} Ranked by discount to fair value; within budget ${fmtSGD(budget)} highlighted. Save (☆) the ones worth pursuing.`));
  const list = h("div", { class: "comp-list" });
  for (const u of res.units) {
    const pct = Math.round(u.pctBelow * 100);
    const affordable = u.price <= budget;
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" },
          `${titleCase(u.project || u.street)} · ${fmtSGD(u.price)} `,
          h("span", { style: `color:var(--good);font-weight:700` }, `${pct}% vs fair`),
          leaseChip({ freehold: u.freehold, leaseLeft: u.leaseLeft, raw: u.tenure }),
          affordable ? null : h("span", { class: "muted small" }, " · above budget")),
        h("div", { class: "muted small" },
          `Fair value ≈ ${fmtSGD(u.fairValue)} · ${titleCase(u.property_type)} · ${Math.round(u.area_sqm)} m² · ${fmtPsf(u.psf, unit)} · ${u.month}${u.outlier ? " · ⚠ statistical outlier — may be a non-market or mis-keyed sale; verify" : ""}`),
        h("div", { class: "diy-meta" }, saveButton(state, actions, mkItem(u))),
      ),
    );
  }
  host.append(list);
  if (!res.units.some((u) => u.tenure)) {
    host.append(h("p", { class: "muted small" }, "Tenure (freehold / leasehold) appears here once the URA cache is refreshed with the new tenure data — trigger a URA ingest to populate it."));
  }
  host.append(sourceNote("URA Data Service — private residential transactions", latestMonth(res.units.map((u) => u.month))));
}

function renderFairValue(
  host: HTMLElement, res: FairValueResponse, budget: number,
  state: AppState, actions: Actions, mkItem: (u: FairValueResponse["units"][number]) => ShortlistItem,
): void {
  clear(host);
  if (!res.ok || res.units.length === 0) {
    host.append(h("p", { class: "muted" }, res.note ?? "No under-valued units found in that segment right now."));
    return;
  }
  host.append(h("p", { class: "muted small" },
    `Model fit on ${res.n} transactions · R² ${(res.r2 * 100).toFixed(0)}% (in-sample).${res.band ? ` Only units below the normal range are listed: comparable sales fall between ${Math.round(res.band.lowPct * 100)}% and +${Math.round(res.band.highPct * 100)}% of fair value (P10–P90 on held-out sales), so smaller gaps are noise.` : ""} Ranked by discount to fair value; within your budget of ${fmtSGD(budget)} highlighted. Save (☆) the ones worth pursuing.`));
  const list = h("div", { class: "comp-list" });
  for (const u of res.units) {
    const pct = Math.round(u.pctBelow * 100); // negative
    const affordable = u.price <= budget;
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" },
          `${titleCase(u.street)} Blk ${u.block} · ${fmtSGD(u.price)} `,
          h("span", { style: `color:var(--good);font-weight:700` }, `${pct}% vs fair`),
          leaseChip({ leaseLeft: hdbLeaseLeft(u.remaining_lease), raw: u.remaining_lease }),
          affordable ? null : h("span", { class: "muted small" }, " · above budget")),
        h("div", { class: "muted small" },
          `Fair value ≈ ${fmtSGD(u.fairValue)} · ${Math.round(u.area_sqm)} sqm · storey ${u.storey} · ${u.month}${u.outlier ? " · ⚠ statistical outlier — may be a non-market or mis-keyed sale; verify" : ""}`),
        h("div", { class: "diy-meta" }, saveButton(state, actions, mkItem(u))),
      ),
    );
  }
  host.append(list);
  host.append(sourceNote("data.gov.sg / HDB — resale flat transactions", latestMonth(res.units.map((u) => u.month))));
}

// A one-time nudge, shown once enough URA history has accumulated to read a
// district price trend. Returns null until it's ready (or once acknowledged).
function longitudinalPromptBanner(state: AppState, actions: Actions): HTMLElement | null {
  const t = state.trends;
  if (!t || !t.ready || state.profile.longitudinalNotified) return null;
  const banner = h("div", { class: "callout column notice" });
  const gotIt = h("button", { class: "primary", onclick: () => void actions.saveProfile({ ...state.profile, longitudinalNotified: true }) }, "Got it");
  const seeBtn = h("button", { class: "ghost", onclick: () => revealSection("afm-trends") }, "See the trends →");
  banner.append(
    h("div", { class: "lead" }, "📈 Enough history for longitudinal trends"),
    h("p", { class: "muted small" },
      `Your accumulated URA data now spans ${t.monthsOfHistory} months across ${t.readyDistricts} district${t.readyDistricts === 1 ? "" : "s"} with enough depth to read a price trend. New "Longitudinal trends" insights are live below.`),
    h("div", { class: "actions" }, seeBtn, gotIt),
  );
  return banner;
}

// Longitudinal trends: district-level $psf over the accumulated URA transaction
// months. Shows progress-to-readiness while history is still shallow, then the
// per-district direction, a ranked mover list, and a trend chart once ready.
function longitudinalPanel(state: AppState, actions: Actions): HTMLElement {
  const box = h("div", { class: "callout column" });
  box.append(h("h3", {}, "Longitudinal trends — district prices over time"));
  const t = state.trends;

  if (!t) {
    box.append(h("p", { class: "muted small" }, "Market history is still loading — check back in a moment."));
    return box;
  }

  if (!t.ready) {
    const closest = [...t.districts].sort((a, b) => b.qualifying - a.qualifying)[0];
    box.append(
      h("p", { class: "muted small" },
        `Building history for a price trend. So far: ${t.monthsOfHistory} transaction month${t.monthsOfHistory === 1 ? "" : "s"} cached${t.totalTxns ? ` (${t.totalTxns.toLocaleString()} transactions)` : ""}. A trend unlocks once a district has at least ${t.minMonths} months with ${t.minN}+ sales each.`),
      closest
        ? h("p", { class: "muted small" }, `Closest: D${Number(closest.district)} ${DISTRICT_NAMES[Number(closest.district)] ?? ""} with ${closest.qualifying}/${t.minMonths} qualifying months. Keep the daily URA refresh running — you'll be prompted here automatically when it's ready.`)
        : h("p", { class: "muted small" }, "Run a URA crawl (see setup) and keep the daily refresh on; you'll be prompted here automatically when it's ready."),
    );
    box.append(sourceNote(t.source, t.maxMonth));
    return box;
  }

  const readyDs = t.districts.filter((d) => d.trendReady);
  const risers = readyDs.filter((d) => d.direction === "rising").sort((a, b) => (b.changePct ?? 0) - (a.changePct ?? 0));
  const coolers = readyDs.filter((d) => d.direction === "cooling").sort((a, b) => (a.changePct ?? 0) - (b.changePct ?? 0));
  const span = t.minMonth && t.maxMonth ? `${formatAsOf(t.minMonth)}–${formatAsOf(t.maxMonth)}` : "the window";
  const pctStr = (v: number | null) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`);
  const dLabel = (d: string) => `D${Number(d)} ${DISTRICT_NAMES[Number(d)] ?? ""}`.trim();

  box.append(h("p", { class: "muted small" },
    `${readyDs.length} district${readyDs.length === 1 ? "" : "s"} now have a readable trend over ${span}: ${risers.length} rising, ${coolers.length} cooling. Average $psf per district-month; a district needs ${t.minMonths}+ months of ${t.minN}+ sales to qualify.`));

  const hottest = risers[0], softest = coolers[0];
  if (hottest || softest) {
    box.append(h("div", { class: "verdict-hero" },
      hottest ? h("div", { class: "lead", style: "color:var(--good)" }, `Hottest: ${dLabel(hottest.district)} ${pctStr(hottest.changePct)}`) : null,
      softest ? h("div", { class: "lead", style: "color:var(--bad)" }, `Softest: ${dLabel(softest.district)} ${pctStr(softest.changePct)}`) : null,
      h("p", { class: "muted small" }, "Direction of average transacted $psf across the accumulated window — a longitudinal read the (single-snapshot) fair-value model can't see.")));
  }

  // District trend chart — default to the user's picked district if it's ready,
  // else the hottest mover.
  const picked = state.profile.privateDistrictPick;
  const initial = readyDs.find((d) => d.district === picked) ?? hottest ?? readyDs[0];
  const chartHost = h("div", {});
  const drawChart = (d: DistrictTrend) => {
    clear(chartHost);
    const pts = d.points.filter((p) => p.n >= t.minN).map((p) => ({ period: p.month, value: p.psf }));
    chartHost.append(
      h("div", { class: "muted small" }, `${dLabel(d.district)} · average $psf · ${pctStr(d.changePct)} over ${span} · ${d.qualifying} months`),
      lineChart([{ points: pts, color: "var(--accent)", label: "avg $psf" }], { height: 180, fill: true }),
    );
  };
  const dSel = h("select", { onchange: () => { const d = readyDs.find((x) => x.district === dSel.value); if (d) drawChart(d); } },
    ...readyDs.map((d) => h("option", { value: d.district, selected: d === initial ? "selected" : null }, `${dLabel(d.district)} (${pctStr(d.changePct)})`)),
  ) as HTMLSelectElement;
  box.append(field("Chart a district", dSel), chartHost);
  if (initial) drawChart(initial);

  // Ranked mover list.
  const list = h("div", { class: "comp-list" });
  for (const d of [...readyDs].sort((a, b) => (b.changePct ?? 0) - (a.changePct ?? 0))) {
    const col = d.direction === "rising" ? "var(--good)" : d.direction === "cooling" ? "var(--bad)" : "var(--neutral)";
    list.append(h("div", { class: "comp" },
      h("div", { class: "comp-main" }, `${dLabel(d.district)} — `, h("span", { style: `color:${col};font-weight:700` }, pctStr(d.changePct))),
      h("div", { class: "muted small" }, `${d.direction ?? "flat"} · ${d.qualifying} months · latest ≈ S$${(d.points.filter((p) => p.n >= t.minN).slice(-1)[0]?.psf ?? 0).toLocaleString()} psf`)));
  }
  box.append(list);

  if (!state.profile.longitudinalNotified) {
    box.append(h("div", { class: "actions" },
      h("button", { class: "ghost", onclick: () => void actions.saveProfile({ ...state.profile, longitudinalNotified: true }) }, "Got it — don't highlight again")));
  }
  box.append(sourceNote(t.source, t.maxMonth));
  return box;
}

// Developer clear-out watch: developments where the developer is under pressure
// to sell unsold stock — the setting for the ABSD-deadline "fire-sale" discounts
// the user asked about. Built from URA's licensed developers'-sales data only,
// so it surfaces discount *pressure* + the developer's own median-price trend,
// never a scraped asking price (the app never scrapes listing portals).
function clearoutBand(band: ClearoutProject["band"]): { label: string; color: string } {
  // Buyer's-eye colouring: more clear-out pressure = a better shot at a discount,
  // so high is the "worth a look" green, moderate amber, low/insufficient muted.
  switch (band) {
    case "high": return { label: "High clear-out pressure", color: "var(--good)" };
    case "moderate": return { label: "Moderate pressure", color: "var(--neutral)" };
    case "low": return { label: "Low pressure", color: "#8a8a8a" };
    default: return { label: "Not enough data yet", color: "#8a8a8a" };
  }
}

function clearoutRow(cp: ClearoutProject): HTMLElement {
  const b = clearoutBand(cp.band);
  const dLabel = cp.district ? `D${Number(cp.district)} ${DISTRICT_NAMES[Number(cp.district)] ?? ""}`.trim() : (cp.region || "");
  const unsoldPctStr = cp.unsoldPct != null ? ` (${Math.round(cp.unsoldPct * 100)}% of project)` : "";
  const priceStr = cp.priceChangePct != null
    ? `median $psf ${cp.priceChangePct >= 0 ? "+" : ""}${(cp.priceChangePct * 100).toFixed(1)}% over ${cp.snapshots} mo`
    : (cp.medianPsf != null ? `median ≈ S$${cp.medianPsf.toLocaleString()} psf` : "");
  const selloutStr = cp.monthsToSellout != null
    ? `≈${cp.monthsToSellout} mo to clear at current pace`
    : (cp.absorptionPerMonth === 0 ? "sales stalled" : "");
  const meta = [dLabel, priceStr, selloutStr, cp.medianPrice != null ? `median ${fmtSGD(cp.medianPrice)}` : ""]
    .filter(Boolean).join(" · ");
  return h("div", { class: "comp" },
    h("div", { class: "comp-main" },
      `${titleCase(cp.project)} — `,
      h("span", { style: `color:${b.color};font-weight:700` }, `${cp.unsold} unsold`),
      h("span", { class: "muted small" }, unsoldPctStr),
      cp.score != null ? h("span", { class: "chip", title: `${cp.confidencePct}% data coverage` }, `${b.label} · ${cp.score}`) : h("span", { class: "chip" }, b.label),
    ),
    meta ? h("div", { class: "muted small" }, meta) : null,
    cp.note ? h("div", { class: "muted small" }, cp.note) : null,
  );
}

function developerClearoutPanel(state: AppState, privBudget: number): HTMLElement {
  const p = state.profile;
  const box = h("div", { class: "callout column" });
  box.append(
    h("h3", {}, "Developer clear-out watch — ABSD-deadline pressure"),
    h("p", { class: "muted small" },
      "Housing developers pay 40% ABSD on land, 35% of it remitted only if they sell every unit within ~5 years of buying the site — so near that deadline they're motivated to discount unsold stock (the classic \"fire-sale\" condos). This ranks developments by clear-out pressure from URA's licensed developers'-sales data: how many units are still unsold, how fast they're selling, and whether the developer's own median price is softening."),
    h("p", { class: "muted small" },
      h("strong", {}, "What this is and isn't: "),
      "a read on discount pressure plus the developer's own transacted median-price trend — not a scraped asking price (this app never scrapes listing portals). Use it to spot where to negotiate, then verify the actual asking price directly."),
  );

  let district = p.privateDistrictPick ?? "";
  const dSel = h("select", { onchange: () => { district = dSel.value; } },
    h("option", { value: "" }, "All districts"),
    ...Array.from({ length: 28 }, (_, i) => i + 1).map((d) =>
      h("option", { value: String(d).padStart(2, "0"), selected: String(d).padStart(2, "0") === district ? "selected" : null }, `D${d} — ${DISTRICT_NAMES[d] ?? ""}`)),
  ) as HTMLSelectElement;
  const rSel = h("select", {},
    h("option", { value: "" }, "All regions"),
    h("option", { value: "CCR" }, "CCR — Core Central"),
    h("option", { value: "RCR" }, "RCR — Rest of Central"),
    h("option", { value: "OCR" }, "OCR — Outside Central"),
  ) as HTMLSelectElement;
  const withinBudget = h("input", { type: "checkbox" }) as HTMLInputElement;

  const host = h("div", { class: "comps" });
  const run = async () => {
    host.replaceChildren(h("p", { class: "muted" }, "Loading developers'-sales data…"));
    const data = await fetchDeveloperClearout({
      district: dSel.value || undefined,
      region: rSel.value || undefined,
      maxPrice: withinBudget.checked && privBudget > 0 ? privBudget : undefined,
    });
    renderClearout(host, data);
  };
  box.append(
    h("div", { class: "row" },
      field("District", dSel),
      field("Region", rSel),
      h("button", { class: "primary", onclick: run }, "Find clear-out candidates")),
  );
  if (privBudget > 0) {
    box.append(h("label", { class: "row", style: "align-items:center;gap:8px" },
      withinBudget, h("span", { class: "muted small" }, `Only show median price within my buying power (${fmtSGD(privBudget)})`)));
  }
  box.append(host);
  return box;
}

function renderClearout(host: HTMLElement, data: DeveloperClearout | null): void {
  clear(host);
  if (!data) {
    host.append(h("p", { class: "muted" }, "Couldn't load developers'-sales data right now. Try again in a moment."));
    return;
  }
  if (data.monthsOfHistory === 0 || data.projects.length === 0) {
    host.append(h("p", { class: "muted" },
      data.monthsOfHistory === 0
        ? "No developers'-sales snapshots cached yet. This populates once a URA crawl runs with the developer-sales service (needs URA_ACCESS_KEY on the Worker)."
        : "No developments with unsold stock match that filter right now."));
    host.append(sourceNote(data.source, data.latestMonth));
    return;
  }
  const s = data.summary;
  host.append(h("p", { class: "muted small" },
    `${s.projectsWithUnsold} development${s.projectsWithUnsold === 1 ? "" : "s"} with unsold stock · ${s.totalUnsoldUnits.toLocaleString()} unsold units total · ${s.highPressure} under high clear-out pressure. Ranked by pressure score (a confidence figure reflects how much data each read has).`));
  if (data.monthsOfHistory < 2) {
    host.append(h("p", { class: "muted small" },
      "Only one monthly snapshot cached so far, so the read is unsold count only. Absorption pace and the median-price trend sharpen the score as monthly snapshots accumulate — keep the daily URA refresh running."));
  }
  const list = h("div", { class: "comp-list" });
  for (const cp of data.projects) list.append(clearoutRow(cp));
  host.append(list);
  host.append(sourceNote(data.source, data.latestMonth));
}

// Forward-looking accessibility: upcoming MRT lines, highlighting the areas
// tied to the user's current focus (work town / picked district). A future
// station is a strong price/desirability driver the timing model can't see.
function connectivityPanel(state: AppState, int: Interests): HTMLElement {
  const p = state.profile;
  const box = h("div", { class: "callout column" });
  box.append(
    h("h3", {}, "Upcoming MRT — future upside"),
    h("p", { class: "muted small" },
      "Announced LTA rail projects mapped to the areas they serve. Proximity to a new station lifts prices and desirability — a forward-looking positive the (backward-looking) timing score doesn't capture. Dates/locations are indicative per LTA and can shift."),
  );

  // Areas the user is focused on, to flag "your area" hits.
  const myTown = (p.hdbFilterTown ?? p.workLocationArea ?? "").toUpperCase();
  const myDistrict = Number(p.privateDistrictPick ?? 0);
  const mine: string[] = [];
  if (int.hdb && myTown) for (const r of mrtForTown(myTown)) mine.push(`${r.line} ~${r.year} (near ${titleCase(myTown)})`);
  if (int.private && myDistrict) for (const r of mrtForDistrict(myDistrict)) mine.push(`${r.line} ~${r.year} (D${myDistrict})`);
  if (mine.length > 0) {
    box.append(h("div", { class: "verdict-hero good" },
      h("div", { class: "lead" }, "Your focus areas are getting new stations:"),
      h("ul", {}, ...Array.from(new Set(mine)).map((m) => h("li", {}, m)))));
  }

  const list = h("div", { class: "comp-list" });
  for (const r of UPCOMING_MRT) {
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" }, `${r.line} · `, h("span", { class: "chip" }, `~${r.year}`)),
        h("div", { class: "muted small" }, `${r.areas} · districts ${r.districts.map((d) => `D${d}`).join(", ")}`),
      ),
    );
  }
  box.append(list);
  box.append(h("p", { class: "muted small" }, "Supply pipeline (upcoming BTO / Government Land Sales) is a planned follow-up — it needs a verified official data source."));
  box.append(sourceNote("LTA — announced rail expansion projects (indicative dates)"));
  return box;
}

// Agent-free transaction guide: a stage-by-stage checklist per transaction
// type, with official portal links, a cost estimate, and a copyable offer
// template. Progress persists in the local vault.
function diyPanel(state: AppState, actions: Actions, budgets: Record<FlowClass, number>, classes: FlowClass[]): HTMLElement {
  const p = state.profile;
  const box = h("div", { class: "callout column" });
  const avail = FLOWS.filter((f) => classes.includes(f.klass));
  const owns = (p.currentPropertyValue ?? 0) > 0;

  // Two modes: "move" composes the sell+buy sequence from the Sell → Buy
  // planner (catered to each property's type); "single" is one picked flow.
  let mode: "move" | "single" = p.diyMode ?? (owns ? "move" : "single");
  let flowId = avail.some((f) => f.id === p.diyFlow) ? (p.diyFlow as string) : (avail[0]?.id ?? "hdb-buy");

  const flowSel = h("select", { onchange: () => { flowId = (flowSel as HTMLSelectElement).value; void actions.saveProfileQuiet({ ...state.profile, diyFlow: flowId }); rebuild(); } },
    ...avail.map((f) => h("option", { value: f.id, selected: f.id === flowId ? "selected" : null }, f.label)),
  ) as HTMLSelectElement;
  const flowRow = h("div", { class: "row" }, field("Transaction", flowSel));

  const body = h("div", { class: "column" });

  // Render one flow's checklist (+ cost estimate & offer template for a buy)
  // into `host`. `priceHint` seeds the buy cost estimate's default price.
  const renderFlow = (flow: Flow, host: HTMLElement, priceHint: number) => {
    host.append(h("p", { class: "muted small" }, flow.blurb));
    const progress = { ...(state.profile.diyProgress ?? {}) };
    const done = flow.steps.filter((s) => progress[`${flow.id}:${s.id}`]).length;
    host.append(h("p", { class: "field-label" }, `Progress: ${done}/${flow.steps.length} steps`));
    const list = h("ol", { class: "diy-list" });
    for (const s of flow.steps) {
      const key = `${flow.id}:${s.id}`;
      const chk = h("input", { type: "checkbox", checked: progress[key] ? "checked" : null,
        onchange: (e: Event) => {
          const np = { ...(state.profile.diyProgress ?? {}) };
          np[key] = (e.target as HTMLInputElement).checked;
          void actions.saveProfileQuiet({ ...state.profile, diyProgress: np });
          rebuild();
        } }) as HTMLInputElement;
      list.append(
        h("li", { class: progress[key] ? "diy-step done" : "diy-step" },
          h("label", { class: "diy-head" }, chk, h("span", { class: "diy-title" }, s.title)),
          h("p", { class: "muted small" }, s.detail),
          h("div", { class: "diy-meta" },
            s.link ? h("a", { href: s.link.url, target: "_blank", rel: "noopener noreferrer" }, `${s.link.label} ↗`) : null,
            s.cost ? h("span", { class: "muted small" }, s.cost) : null),
        ),
      );
    }
    host.append(list);

    // Cost estimate + offer template for buy flows. Progress persists per flow
    // id, so both legs of a move keep independent checklists.
    if (flow.side === "buy") {
      const defaultPrice = Math.round(priceHint) || 0;
      const price = p.diyPrice && p.diyPrice > 0 ? p.diyPrice : defaultPrice;
      const ownsOther = p.currentPropertyValue > 0 && p.buyIntent === "second";
      const priceInp = numberInput(price, (v) => { void actions.saveProfileQuiet({ ...state.profile, diyPrice: v || 0 }); rebuild(); }, "10000", "change");
      const costs = buyCostSummary(flow.klass, price, ownsOther, buyerOf(state.profile));
      const costBox = h("div", { class: "mp-block" }, h("div", { class: "mp-title" }, "Your agent-free cost estimate"));
      for (const c of costs) {
        costBox.append(h("div", { class: "mp-row" },
          h("span", { class: "muted small" }, c.label + (c.note ? ` (${c.note})` : "")),
          h("span", {}, c.amount == null ? "—" : `${c.amount < 0 ? "−" : ""}${fmtSGD(Math.abs(c.amount))}`)));
      }
      const net = costs.reduce((s, c) => s + (c.amount ?? 0), 0);
      costBox.append(h("div", { class: "mp-row emph" }, h("span", {}, "Net transaction cost (excl. price)"), h("span", {}, fmtSGD(net))));

      const tmpl = offerTemplate(price, flow.klass);
      const copyBtn = h("button", { class: "ghost", onclick: () => { void navigator.clipboard?.writeText(tmpl); (copyBtn as HTMLButtonElement).textContent = "Copied ✓"; setTimeout(() => ((copyBtn as HTMLButtonElement).textContent = "Copy offer template"), 1500); } }, "Copy offer template");

      host.append(
        h("div", { class: "callout column" },
          h("label", { class: "field" }, h("span", { class: "field-label" }, "Assumed price for the estimate"), priceInp),
          costBox,
          h("details", { class: "section" }, h("summary", {}, "Offer / OTP template"),
            h("div", { class: "section-body" },
              h("pre", { class: "diy-template" }, tmpl),
              h("div", { class: "actions" }, copyBtn))),
        ),
      );
    }
  };

  const rebuild = () => {
    clear(body);
    flowRow.style.display = mode === "single" ? "" : "none";

    if (mode === "move") {
      const scenario: MoveScenario = MOVE_SCENARIOS.some((s) => s.id === p.movePlanScenario) ? (p.movePlanScenario as MoveScenario) : "sell-first";
      const currentClass = classOf(p.currentPropertyType);
      const nextClass: PropClass = p.movePlanNextClass ?? currentClass ?? "private";
      const sells = scenario !== "keep-buy-second";
      const scenarioLabel = MOVE_SCENARIOS.find((s) => s.id === scenario)?.label ?? "";
      const seq = moveFlows({ scenario, currentClass, nextClass, sells });

      body.append(h("p", { class: "muted small" },
        `Your move: ${scenarioLabel}${sells && currentClass ? ` · sell ${currentClass.toUpperCase()}` : ""} → buy ${nextClass.toUpperCase()}. Change it in the Sell → Buy planner above.`));

      const notes = moveSequencing({ scenario, currentClass, nextClass, sells });
      if (notes.length) {
        const nb = h("div", { class: "mp-block" }, h("div", { class: "mp-title" }, "Sequencing — how the two transactions fit together"));
        for (const n of notes) nb.append(h("p", { class: "muted small" }, n));
        body.append(nb);
      }

      if (seq.length === 0) {
        body.append(h("p", { class: "muted" }, "Set your current property type and next-purchase type in the Sell → Buy planner to build the playbook."));
      }
      seq.forEach((ph, i) => {
        const priceHint = ph.phase === "buy"
          ? (p.movePlanNextPrice && p.movePlanNextPrice > 0 ? p.movePlanNextPrice : (budgets[ph.flow.klass] ?? 0))
          : 0;
        const phaseBox = h("div", { class: "diy-phase" });
        phaseBox.append(h("h4", { class: "diy-phase-head" },
          h("span", { class: `diy-phase-tag ${ph.phase}` }, `Step ${i + 1} · ${ph.phase === "sell" ? "Sell" : "Buy"}`),
          h("span", {}, ph.flow.label)));
        renderFlow(ph.flow, phaseBox, priceHint);
        body.append(phaseBox);
      });
    } else {
      const flow = FLOWS.find((f) => f.id === flowId);
      if (flow) renderFlow(flow, body, budgets[flow.klass] ?? 0);
    }

    body.append(h("p", { class: "muted small" }, "Informational, not legal advice. A conveyancing lawyer (or HDB, for resale flats) handles the legal work — that's not the agent. Verify fees and timelines against the official portals."));
  };

  const tabMove = h("button", { class: "tab", onclick: () => { if (mode !== "move") { mode = "move"; void actions.saveProfileQuiet({ ...state.profile, diyMode: "move" }); syncTabs(); rebuild(); } } }, "My move");
  const tabSingle = h("button", { class: "tab", onclick: () => { if (mode !== "single") { mode = "single"; void actions.saveProfileQuiet({ ...state.profile, diyMode: "single" }); syncTabs(); rebuild(); } } }, "Single transaction");
  const syncTabs = () => { tabMove.className = mode === "move" ? "tab active" : "tab"; tabSingle.className = mode === "single" ? "tab active" : "tab"; };
  syncTabs();

  box.append(
    h("h3", {}, "Cut out the agent"),
    h("p", { class: "muted small" }, owns
      ? "Your full move as an ordered playbook — the sale and the purchase, catered to each property's type, with official portals, cost estimates and offer templates. Progress saves automatically."
      : "Step-by-step playbooks to buy or sell directly, with the official portals, a cost estimate, and an offer template. Progress saves automatically."),
  );
  if (owns) box.append(h("div", { class: "subtabs" }, tabMove, tabSingle));
  box.append(flowRow, body);
  rebuild();
  return box;
}

const BAND_CLASS: Record<string, string> = {
  within: "buy-ready", reachable: "reachable", stretch: "stretch", beyond: "out-of-reach",
};

function privateRecs(state: AppState, budget: number, areaSqft: number, actions: Actions): HTMLElement {
  const p = state.profile;
  const unit = p.priceUnit ?? "psf";
  const wrap = h("div", { class: "column" });

  // Unit toggle: $psf ⇄ $/m². Persists and re-renders so all prices stay consistent.
  const mkUnitBtn = (u: "psf" | "psm", label: string) =>
    h("button", { class: unit === u ? "tab active" : "tab", onclick: () => { if (unit !== u) void actions.saveProfile({ ...state.profile, priceUnit: u }); } }, label);
  wrap.append(h("div", { class: "subtabs" }, h("span", { class: "muted small" }, "Price basis:"), mkUnitBtn("psf", "$psf"), mkUnitBtn("psm", "$/m²")));

  const affordablePsf = budget / (areaSqft || 900);

  // Profile-aware entry-fit layer: infer the buyer's stance and fold the
  // longitudinal district trends into the ranking. Additive — the underlying
  // affordability band + entry score are still shown alongside.
  const stance = inferEntryStance(p);
  const trendMap = new Map<number, DistrictTrend>();
  for (const d of state.trends?.districts ?? []) trendMap.set(Number(d.district), d);
  const fitOpts: EntryFitOpts = { stance, trends: trendMap };
  const decorClass: Record<string, string> = {
    "enter-now": "buy-ready", "watch-dip": "reachable", "smaller-unit": "stretch", beyond: "out-of-reach",
  };

  const recs = recommendPrivateRegions(budget, areaSqft, state.signals, fitOpts);
  const anyUra = recs.some((r) => r.medianSource === "URA");
  wrap.append(h("p", { class: "muted small" },
    `Private buying power ${fmtSGD(budget)} at ${areaSqft} sqft → you can afford ≈ ${fmtPsf(affordablePsf, unit)}. Medians are ${anyUra ? "live URA data" : "indicative"}.`));
  const stanceWord = stance.urgency >= 0.6 ? "act-when-it-fits" : stance.urgency <= 0.4 ? "patient value-hunting" : "balanced";
  wrap.append(h("p", { class: "muted small" },
    `Ranked for your situation (${stanceWord})${stance.reasons[0] ? ` — ${stance.reasons[0]}` : ""}. Entry fit blends affordability, market timing and each district's multi-year price trajectory.`));

  // District-level (real URA per-district median) — the primary, granular view.
  const districts = recommendPrivateDistricts(budget, areaSqft, state.signals, fitOpts);
  if (districts.length > 0) {
    wrap.append(h("h4", { class: "field-label" }, "By district — best entry fit first"));
    const list = h("div", { class: "comp-list" });
    for (const d of districts.slice(0, 12)) {
      const fit = d.entryFit;
      const color = fit
        ? (fit.decision === "enter-now" ? "var(--good)" : fit.decision === "beyond" ? "var(--bad)" : "var(--neutral)")
        : (d.band === "within" ? "var(--good)" : d.band === "beyond" ? "var(--bad)" : "var(--neutral)");
      const badge = mrtBadge(mrtForDistrict(d.district));
      list.append(
        h("div", { class: "comp" },
          h("div", { class: "comp-main" },
            `D${d.district} — ${d.name} · `,
            h("span", { style: `color:${color};font-weight:700` }, fit ? fit.label : d.band.replace("-", " ")),
            fit ? h("span", { class: "muted small" }, ` · fit ${fit.score}/100`) : null,
            badge ? h("span", { class: "chip", title: "Upcoming MRT" }, badge) : null),
          h("div", { class: "muted small" },
            `median ${fmtPsf(d.medianPsf, unit)} · you ${fmtPsf(d.affordablePsf, unit)} · ${d.band.replace("-", " ")}${d.verdict ? ` · ${verdictLabel(d.verdict)}` : ""}`),
          fit ? h("div", { class: "muted small" }, fit.rationale) : null,
        ),
      );
    }
    wrap.append(list);
  }

  // Region timing summary — one compact line (the smoother quarterly signal).
  wrap.append(h("h4", { class: "field-label" }, "Region market timing"));
  for (const r of recs) {
    const fit = r.entryFit;
    wrap.append(
      h("div", { class: `feas feas-${fit ? decorClass[fit.decision] : BAND_CLASS[r.band]}` },
        h("div", { class: "feas-row" },
          h("span", { class: "chip" }, r.label),
          h("span", { class: "muted small" },
            `median ${fmtPsf(r.medianPsf, unit)} · ${fit ? fit.label : (r.verdict ? verdictLabel(r.verdict) : "no timing yet")}`),
        ),
        h("p", { class: "muted small" }, r.districts),
      ),
    );
  }

  // Real transacted private units (URA) for a chosen district.
  const dPick = p.privateDistrictPick ?? "";
  const dSel = h("select", { onchange: () => void actions.saveProfileQuiet({ ...state.profile, privateDistrictPick: dSel.value }) },
    h("option", { value: "" }, "Pick a district…"),
    ...Array.from({ length: 28 }, (_, i) => i + 1).map((d) =>
      h("option", { value: String(d).padStart(2, "0"), selected: String(d).padStart(2, "0") === dPick ? "selected" : null }, `D${d} — ${DISTRICT_NAMES[d] ?? ""}`)),
  ) as HTMLSelectElement;
  const unitsHost = h("div", { class: "comps" });
  const loadUnits = async () => {
    if (!dSel.value) { unitsHost.replaceChildren(h("p", { class: "muted" }, "Pick a district first.")); return; }
    unitsHost.replaceChildren(h("p", { class: "muted" }, "Loading real transacted units…"));
    try {
      const { comps, cached } = await fetchPrivateComps({ district: dSel.value, ...privFilterParams(state.profile) });
      renderPrivateComps(unitsHost, comps, cached, state.profile.priceUnit ?? "psf");
    } catch (e) {
      unitsHost.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`));
    }
  };
  const unitsBtn = h("button", { class: "ghost", onclick: loadUnits }, "See real units");
  const onUnitsFilter = () => { if (dSel.value && unitsHost.childElementCount > 0) void loadUnits(); };
  wrap.append(
    h("h4", { class: "field-label" }, "Real transacted units (URA)"),
    h("div", { class: "row" }, field("District", dSel), unitsBtn),
    privFilterControls(state, actions, onUnitsFilter),
    unitsHost,
  );

  wrap.append(h("p", { class: "muted small" },
    "Real transacted units are actual URA-recorded sales — go view comparable listings for them. Time entry with the price signals above."));
  return wrap;
}

function renderPrivateComps(host: HTMLElement, comps: PrivateComp[], cached: number, unit: "psf" | "psm"): void {
  clear(host);
  if (comps.length === 0) {
    host.append(h("p", { class: "muted" },
      cached === 0
        ? "No private units cached yet — run a URA crawl to populate them (see setup)."
        : "No recent transactions in that district. Try another district."));
    return;
  }
  host.append(h("p", { class: "muted small" }, `${comps.length} recent transacted units (URA).`));
  const list = h("div", { class: "comp-list" });
  for (const c of comps) {
    const size = unit === "psm" ? `${Math.round(c.area_sqm)} m²` : `${Math.round(c.area_sqm * 10.7639)} sqft`;
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" }, `${titleCase(c.project || c.street)} · ${fmtSGD(c.price)} `,
          leaseChip({ freehold: c.freehold, leaseLeft: c.leaseLeft, raw: c.tenure })),
        h("div", { class: "muted small" },
          `D${Number(c.district)} ${DISTRICT_NAMES[Number(c.district)] ?? ""} · ${titleCase(c.property_type)} · ${size} · ${fmtPsf(c.psf, unit)} · ${c.month}`),
        h("div", { class: "muted small" }, mrtLazyLink(c.project || c.street, c.street)),
      ),
    );
  }
  if (!comps.some((c) => c.tenure)) {
    host.append(h("p", { class: "muted small" }, "Tenure (freehold / leasehold) appears here once the URA cache is refreshed with the new tenure data — trigger a URA ingest to populate it."));
  }
  host.append(list);
  host.append(sourceNote("URA Data Service — private residential transactions", latestMonth(comps.map((c) => c.month))));
}

function hdbRecs(state: AppState, budget: number, actions: Actions): HTMLElement {
  const wrap = h("div", { class: "column" });
  const p = state.profile;
  // Preselect town if the job-location text matches a town name.
  const jobTown = HDB_TOWNS.find((t) => p.workLocationArea && t.includes(p.workLocationArea.toUpperCase().trim()));
  const town0 = p.hdbFilterTown ?? jobTown ?? "";
  // Flat type is the shared top-of-tab target, not a per-panel filter.
  const flatType = hdbTarget(p);
  let minArea = p.hdbFilterMinArea ?? 0;
  let minLease = p.hdbFilterMinLease ?? 0;
  const remember = () => void actions.saveProfileQuiet({
    ...state.profile,
    hdbFilterTown: townSel.value,
    hdbFilterMinArea: minArea, hdbFilterMinLease: minLease,
  });

  const townSel = h("select", { onchange: () => remember() },
    h("option", { value: "" }, "Any town"),
    ...HDB_TOWNS.map((t) => h("option", { value: t, selected: t === town0 ? "selected" : null }, titleCase(t))),
  ) as HTMLSelectElement;
  const minAreaInp = numberInput(minArea, (v) => { minArea = v; remember(); }, "5", "change");
  const minLeaseInp = numberInput(minLease, (v) => { minLease = v; remember(); }, "5", "change");
  const resultHost = h("div", { class: "comps" });

  const findBtn = h("button", { class: "primary", onclick: async () => {
    resultHost.replaceChildren(h("p", { class: "muted" }, "Searching real transactions…"));
    try {
      const { comps } = await fetchComps({ town: townSel.value, flatType, maxPrice: budget, minArea, minLease });
      renderComps(resultHost, comps, budget);
    } catch (e) {
      resultHost.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`));
    }
  } }, "Find matches");

  // School finder — helps target a town near a child's school. Prefilled from
  // the profile's saved school and writes edits back to it.
  const schoolInp = schoolField("e.g. Tao Nan School", p.childSchool ?? "",
    (v) => void actions.saveProfileQuiet({ ...state.profile, childSchool: v.trim() }));
  const schoolHost = h("div", { class: "comps" });
  const schoolBtn = h("button", { class: "ghost", onclick: async () => {
    schoolHost.replaceChildren(h("p", { class: "muted" }, "Searching schools…"));
    try {
      const schools = await fetchSchools(schoolInp.value);
      renderSchools(schoolHost, schools);
    } catch (e) {
      schoolHost.replaceChildren(h("p", { class: "muted" }, `Couldn't load: ${(e as Error).message}`));
    }
  } }, "Find school");

  wrap.append(
    h("p", { class: "muted small" }, `Real transacted HDB units within ${fmtSGD(budget)}, ${flatType || "all flat types"}${jobTown ? `, near your job area (${titleCase(jobTown)})` : ""}. Change flat type at the top of the tab.`),
    h("div", { class: "row" }, field("Town", townSel)),
    h("div", { class: "row" }, field("Min area (sqm)", minAreaInp), field("Min lease (yrs)", minLeaseInp), findBtn),
    resultHost,
    h("p", { class: "field-label" }, "Shortlist near a child's school"),
    h("div", { class: "row" }, field("School name", schoolInp), schoolBtn),
    schoolHost,
  );
  return wrap;
}

function renderSchools(host: HTMLElement, schools: School[]): void {
  clear(host);
  if (schools.length === 0) {
    host.append(h("p", { class: "muted" }, "No matching schools. Try a shorter name."));
    return;
  }
  host.append(h("p", { class: "muted small" }, "Read the address to pick the nearby town above."));
  const list = h("div", { class: "comp-list" });
  for (const s of schools) {
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" }, titleCase(s.name)),
        h("div", { class: "muted small" }, `${titleCase(s.address)}${s.postal ? ` · S(${s.postal})` : ""}${s.level ? ` · ${s.level}` : ""}`),
      ),
    );
  }
  host.append(list);
  host.append(sourceNote("data.gov.sg / MOE — general information of schools"));
}

function renderComps(host: HTMLElement, comps: Comp[], budget: number): void {
  clear(host);
  if (comps.length === 0) {
    host.append(h("p", { class: "muted" }, "No transactions in that range. Widen the town/type, or your budget may be below recent prices there."));
    return;
  }
  host.append(h("p", { class: "muted small" }, `Showing ${comps.length} within ${fmtSGD(budget)} — real transacted prices.`));
  const list = h("div", { class: "comp-list" });
  for (const c of comps) {
    list.append(
      h("div", { class: "comp" },
        h("div", { class: "comp-main" }, `${c.flat_type} · ${fmtSGD(c.price)} `,
          leaseChip({ leaseLeft: hdbLeaseLeft(c.remaining_lease), raw: c.remaining_lease })),
        h("div", { class: "muted small" },
          `Blk ${c.block} ${titleCase(c.street)}, ${titleCase(c.town)} · ${c.storey} · ${Math.round(c.area_sqm)} sqm · ${c.month}`),
        h("div", { class: "muted small" }, mrtLazyLink(`${c.block} ${c.street}, ${c.town}`)),
      ),
    );
  }
  host.append(list);
  host.append(sourceNote("data.gov.sg / HDB — resale flat transactions", latestMonth(comps.map((c) => c.month))));
}

function mortgageRateLine(state: AppState): HTMLElement {
  const stress = `${(RULES.stressRateAnnual * 100).toFixed(1)}%`;
  const userRate = (state.profile.mortgageRatePct ?? 3.5).toFixed(2);
  const sora = state.rates?.sora;
  const soraNote = sora != null ? ` Live 3M SORA (MAS): ${sora.toFixed(2)}%.` : "";
  const wrap = h("div", { class: "column" },
    h("p", { class: "muted small" },
      `Monthly repayment is estimated at your ${userRate}% rate (edit in Profile). Loan eligibility is stress-tested at ${stress} per MAS rules.${soraNote} Check current packages against mas.gov.sg/sora.`));
  if (state.rates?.source) wrap.append(sourceNote(state.rates.source, state.rates.asOf));
  return wrap;
}

function titleCase(s: string): string {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------- Cars
export function renderCars(state: AppState, actions: Actions): HTMLElement {
  const p = state.profile;
  const root = h("section", { class: "view" });
  root.append(h("h1", { class: "page-title" }, "Cars"), h("p", { class: "muted small" }, "Excludes motorbikes and vans."));

  const carSell = carSellPanel(state, actions);
  if (carSell) root.append(carSell);

  const carBp = carBuyingPower(p);
  root.append(
    h("div", { class: "callout" },
      h("div", { class: "power" },
        h("h3", {}, "Your car buying power"),
        kv("Max car price", hasFinances(p) ? fmtEstimate(carBp.maxPrice) : "— (add income or savings in Profile)"),
        kv("Limited by", hasFinances(p) ? limitLabel(carBp.boundedBy).replace(/^limited by /, "") : "—"),
        kv("Downpayment (40%)", fmtSGD(carBp.downpayment)),
        kv("Monthly instalment", fmtSGD(carBp.monthlyInstalment)),
        h("p", { class: "muted small" },
          `Assumes ${RULES.car.ltv * 100}% max financing, ${RULES.car.tenureYears}yr, ${(p.carLoanRatePct ?? 2.78)}% flat rate (edit in Profile). COE is the biggest cost swing — the signals below track it.`),
        h("p", { class: "muted small" },
          "Car loans are flat-rate and bank-set — there's no official rate feed, so set your quoted rate in Profile."),
      ),
    ),
  );

  // Manual target price -> feasibility (per the "COE proxy + manual entry"
  // model). Inputs are created once and never rebuilt while typing; the badge
  // updates in place so focus/keyboard are never lost.
  const carSignals = state.signals.filter((s) => s.asset_class === "car");
  let targetPrice = p.targetCarPrice ?? 0;
  let category: Profile["targetCarCategory"] = p.targetCarCategory ?? "cat-a";
  const feasHost = h("div", { class: "feas-host" });

  const rebuildFeas = () => {
    clear(feasHost);
    const catSignal = carSignals.find((s) => s.id === `coe.${category}`) ?? carSignals[0];
    if (targetPrice > 0) {
      feasHost.append(feasibilityBadge(feasibility(carBp.maxPrice, targetPrice, catSignal?.entry_score ?? null)));
    } else {
      feasHost.append(h("p", { class: "muted" }, "Enter a target price to check feasibility against your buying power and the COE cycle."));
    }
  };

  const priceInput = numberInput(targetPrice, (v) => {
    targetPrice = v;
    rebuildFeas();
    void actions.saveProfileQuiet({ ...state.profile, targetCarPrice: v });
  }, "1000");
  const catSelect = h("select", {
    onchange: (e: Event) => {
      category = (e.target as HTMLSelectElement).value as Profile["targetCarCategory"];
      rebuildFeas();
      void actions.saveProfileQuiet({ ...state.profile, targetCarCategory: category });
    },
  },
    ...["cat-a", "cat-b", "cat-e"].map((c) =>
      h("option", { value: c, selected: (category ?? "cat-a") === c ? "selected" : null },
        c === "cat-a" ? "Cat A (small)" : c === "cat-b" ? "Cat B (large)" : "Cat E (open)")),
  );

  root.append(
    h("div", { class: "callout" },
      h("h3", {}, "Evaluate a specific car"),
      h("div", { class: "row" },
        field("Target on-road price (SGD)", priceInput, "Enter the dealer's quote incl. COE"),
        field("COE category", catSelect),
      ),
      feasHost,
    ),
  );
  rebuildFeas();

  root.append(h("h3", {}, "COE market timing"));
  for (const s of carSignals) {
    root.append(seriesRow(actions, s, null));
  }

  // Agent-free (dealer-free) car transaction guide.
  root.append(section("Cut out the dealer — DIY car transaction guide",
    diyPanel(state, actions, { hdb: 0, private: 0, car: carBp.maxPrice }, ["car"]), false, "car-diy"));

  root.append(h("p", { class: "disclaimer" }, disclaimer));
  return root;
}

// ---------------------------------------------------------------- Profile
export function renderProfile(state: AppState, actions: Actions): HTMLElement {
  // Deep working copy so nested objects (household/interests) don't leak edits
  // back into state by reference before a save.
  const p = JSON.parse(JSON.stringify(state.profile)) as Profile;
  // Baseline captured at render time, so a later explicit Save can still detect
  // material changes for the re-profile prompts even though fields auto-persist.
  const baseline = JSON.parse(JSON.stringify(state.profile)) as Profile;
  // One save model (UX_FINDINGS U-12): every field auto-saves on change, the
  // buying-power figures recompute in place, and a status line confirms it.
  // Material life changes (work, school, household) still raise the re-profile
  // prompt on the Home tab — detected here against the render-time baseline.
  const saved = h("p", { class: "save-status muted small", role: "status", "aria-live": "polite" },
    "Saved automatically, encrypted on this device.");
  let refreshTiles = () => {};
  const persist = () => {
    void actions.saveProfileQuiet(p);
    refreshTiles();
    const prompts = detectReprofilePrompts(baseline, p);
    if (prompts.length) state.reprofilePrompts = prompts.map((q) => q.id);
    const t = new Date();
    saved.textContent = `Saved ✓ ${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")} — encrypted on this device.`;
  };
  const root = h("section", { class: "view" });
  root.append(
    h("h1", { class: "page-title" }, "Your profile"),
    h("p", { class: "muted" }, "Stored encrypted in this browser only. Nothing here is ever sent to the server."),
    saved,
  );

  // What are you evaluating? — drives which tabs and sections appear.
  const int = p.interests ?? { hdb: true, private: true, car: false };
  const mkChk = (label: string, key: "hdb" | "private" | "car") => {
    const c = h("input", { type: "checkbox", checked: int[key] ? "checked" : null,
      onchange: (e: Event) => { void actions.saveProfile({ ...p, interests: { ...int, [key]: (e.target as HTMLInputElement).checked } }); } }) as HTMLInputElement;
    return field(label, c);
  };
  root.append(
    h("h2", {}, "What are you evaluating?"),
    h("p", { class: "muted small" }, "Untick what you don't need — irrelevant tabs and sections are hidden so the app shows only what matters to you."),
    h("div", { class: "form-grid" },
      mkChk("HDB (public housing)", "hdb"),
      mkChk("Private property", "private"),
      mkChk("Car", "car")),
  );

  // Live buying-power readout — recomputed in place on every edit (no Save).
  const tileHost = h("div", { class: "tiles", "aria-live": "polite" });
  refreshTiles = () => {
    const hdb = propertyBuyingPower(p, true, { sora: state.rates?.sora });
    const priv = propertyBuyingPower(p, false, { sora: state.rates?.sora });
    const car = carBuyingPower(p);
    const has = hasFinances(p);
    tileHost.replaceChildren(...[
      int.hdb ? tile("HDB buying power", has ? fmtEstimate(hdb.maxPrice) : "—", has ? `estimate · ${limitLabel(hdb.boundedBy)}` : "add income or savings below") : null,
      int.private ? tile("Private buying power", has ? fmtEstimate(priv.maxPrice) : "—", has ? `estimate · ${limitLabel(priv.boundedBy)}` : "add income or savings below") : null,
      int.car ? tile("Car buying power", has ? fmtEstimate(car.maxPrice) : "—", has ? `estimate · ${limitLabel(car.boundedBy)}` : "add income or savings below") : null,
    ].filter((x): x is HTMLElement => x !== null));
  };
  refreshTiles();
  root.append(tileHost);

  const bind = (key: keyof Profile, step = "1000") =>
    numberInput(p[key] as number, (v) => { (p[key] as number) = v; persist(); }, step, "change");
  // Money fields use a comma-formatted text box, persisted on blur.
  const money = (key: keyof Profile) =>
    moneyInput(p[key] as number, (v) => { (p[key] as number) = v; persist(); }, "change");

  root.append(
    h("div", { class: "form-grid" },
      field("Age", bind("ageYears", "1")),
      field("Residency (for ABSD)", (() => {
        const sel = h("select", { onchange: () => { p.residency = (sel as HTMLSelectElement).value as "SC" | "PR"; persist(); } },
          h("option", { value: "SC" }, "Singapore Citizen"),
          h("option", { value: "PR" }, "Permanent Resident"),
        ) as HTMLSelectElement;
        sel.value = p.residency ?? "SC";
        return sel;
      })(), "PRs pay ABSD from the first property (5% on the 1st, 30% on the 2nd, per IRAS from 27 Apr 2023)."),
      field("Buying with a spouse?", (() => {
        const sel = h("select", { onchange: () => { const v = (sel as HTMLSelectElement).value; p.married = v !== "no"; p.spouseResidency = v === "no" ? undefined : (v as "SC" | "PR"); persist(); } },
          h("option", { value: "no" }, "No — buying alone / not married"),
          h("option", { value: "SC" }, "Yes — spouse is a Singapore Citizen"),
          h("option", { value: "PR" }, "Yes — spouse is a Permanent Resident"),
        ) as HTMLSelectElement;
        sel.value = p.married ? (p.spouseResidency ?? "SC") : "no";
        return sel;
      })(), "Married couples buying jointly pay the higher of the two ABSD profiles. With an SC spouse: no ABSD on a first home, and ABSD on a second home is refunded if the first is sold within 6 months (IRAS)."),
      field("Use a property agent?", (() => {
        const sel = h("select", { onchange: () => { p.useAgent = (sel as HTMLSelectElement).value === "yes"; persist(); } },
          h("option", { value: "yes" }, "Yes — include agent fees"),
          h("option", { value: "no" }, "No — DIY (see the agent-free playbooks)"),
        ) as HTMLSelectElement;
        sel.value = p.useAgent === false ? "no" : "yes";
        return sel;
      })(), "With an agent we assume ~2% when selling and ~1% when buying (typical market rates, not quotes). DIY removes both; legal fees still apply."),
      field("Household gross income (monthly)", money("monthlyGrossIncome")),
      field("Cash savings", money("cashSavings")),
      field("CPF OA balance", money("cpfOaBalance")),
      field("Other liquid assets", money("otherLiquidAssets")),
      field("Other monthly debt repayments", money("monthlyDebtRepayments")),
      field("Existing home-loan monthly", money("existingHomeLoanMonthly")),
    ),
  );

  // Holdings — "owns" is derived from the property value, so no separate flag.
  root.append(
    h("h2", {}, "Holdings"),
    h("p", { class: "muted small" }, "Enter a current property/car value if you own one — that's what drives stamp-duty tiers, free cash on a sale, and the sell signals."),
    h("div", { class: "form-grid" },
      field("Current property — estimated value", money("currentPropertyValue"),
        "Leave 0 if you don't own. Used to gauge free cash for your next purchase."),
      field("Current property — outstanding loan", money("currentPropertyOutstandingLoan"),
        "Value minus this is your equity (free cash if you sell to upgrade)."),
      field("Current property — CPF used at purchase", moneyInput(p.cpfLumpSumUsed ?? 0, (v) => { p.cpfLumpSumUsed = v; persist(); }, "change"),
        "CPF used for the downpayment, stamp duty and legal fees. With the monthly figure below, the app estimates the accrued interest (CPF OA rate, 2.5%) you must refund on sale."),
      field("Current property — monthly CPF towards the loan", moneyInput(p.cpfMonthlyInstalment ?? 0, (v) => { p.cpfMonthlyInstalment = v; persist(); }, "change"),
        "The CPF part of your monthly mortgage instalment."),
      field("Exact CPF refund (optional)", money("cpfUsedInCurrent"),
        "If you have it, the 'What happens if' figure from your CPF Home ownership dashboard (principal + accrued interest). Overrides the estimate."),
      field("Current property — years held", yearsHeldInput(p, persist),
        "Leave blank if you don't own. Until you fill this in, Seller's Stamp Duty and the MOP check are left out and flagged. Drives Seller's Stamp Duty (sold within 3 years if bought 11 Mar 2017 – 3 Jul 2025; within 4 years if bought on or after 4 Jul 2025) and the HDB 5-year Minimum Occupation Period."),
      field("Current car — estimated value", money("currentCarValue"),
        "Leave 0 if none. Used for the car sell signal and free cash toward the next car."),
      field("Current car — outstanding loan", money("currentCarOutstandingLoan")),
    ),
  );

  // Loan rates (drive the estimated actual monthly repayment)
  root.append(
    h("h2", {}, "Loan rates"),
    h("div", { class: "form-grid" },
      field("Mortgage rate (% p.a.)", numberInput(p.mortgageRatePct ?? 3.5, (v) => { p.mortgageRatePct = v; p.mortgageRateManual = true; persist(); }, "0.1", "change"),
        "Your bank's package rate. Leave untouched to use live 3M SORA + 0.5%. Eligibility still uses the MAS/HDB stress-test rate."),
      field("Use live SORA instead", (() => {
        const cb = h("input", { type: "checkbox", onchange: () => { p.mortgageRateManual = !(cb as HTMLInputElement).checked; persist(); } }) as HTMLInputElement;
        cb.checked = !rateIsManual(p);
        return cb;
      })(), "Ticked: repayment estimates use 3M SORA (MAS) + 0.5% spread. Unticked: your rate above."),
      field("Car loan flat rate (% p.a.)", numberInput(p.carLoanRatePct ?? 2.78, (v) => { p.carLoanRatePct = v; persist(); }, "0.1", "change"),
        "Flat rate on the car loan."),
    ),
  );

  // Household, work & schools — first-class location context. Captured once
  // here and reused by the commute ranking, school-proximity shortlisting and
  // the smart re-profile prompts (not re-typed as a per-panel filter).
  root.append(
    h("h2", {}, "Household, work & schools"),
    h("p", { class: "muted small" }, "Where you work and your children's schooling drive which towns/districts are realistic. Set them once here — the commute ranking and school-proximity shortlist read straight from this."),
  );
  const work = workInput(p.workLocationArea ?? "", (v) => { p.workLocationArea = v; persist(); });
  const spouseWork = workInput(p.spouseWorkLocationArea ?? "", (v) => { p.spouseWorkLocationArea = v; persist(); });
  const adults = numberInput(p.household.adults, (v) => { p.household.adults = v; persist(); }, "1", "change");
  const kids = numberInput(p.household.children, (v) => { p.household.children = v; persist(); }, "1", "change");
  const stage = h("select", {
    onchange: (e: Event) => { p.household.childrenSchoolingStage = (e.target as HTMLSelectElement).value as Profile["household"]["childrenSchoolingStage"]; persist(); } },
    ...["none", "preschool", "primary", "secondary", "tertiary"].map((s) =>
      h("option", { value: s, selected: (p.household.childrenSchoolingStage ?? "none") === s ? "selected" : null }, s)),
  );
  const school = schoolField("e.g. Tao Nan School", p.childSchool ?? "", (v) => { p.childSchool = v.trim(); persist(); });
  const noDrive = h("input", { type: "checkbox", checked: p.transitDependent ? "checked" : null,
    onchange: (e: Event) => { p.transitDependent = (e.target as HTMLInputElement).checked; persist(); } }) as HTMLInputElement;
  root.append(
    h("div", { class: "form-grid" },
      field("Your work location / area", work, SMART_QUESTIONS[0].why),
      field("Spouse / partner work location", spouseWork, "Used with yours to rank areas by combined commute."),
      field("Adults in household", adults),
      field("Children", kids),
      field("Children's schooling stage", stage, SMART_QUESTIONS[1].why),
      field("Child's school (to prioritise)", school, "Anchors the 1–2km priority zones — feeds the commute ranking and HDB school-proximity shortlist."),
      field("I don't drive (prioritise MRT access)", noDrive, "When on, a listing far from an MRT (≥~12 min walk) is flagged and caps a BUY down to CONSIDER."),
    ),
  );

  // Appearance — follow the device, or force light/dark (stored per device,
  // not in the vault: it's a display preference, not personal data).
  const themeSel = h("select", { onchange: () => setTheme((themeSel as HTMLSelectElement).value as ThemePref) },
    h("option", { value: "system" }, "Match my device"),
    h("option", { value: "light" }, "Light"),
    h("option", { value: "dark" }, "Dark"),
  ) as HTMLSelectElement;
  themeSel.value = getTheme();
  root.append(
    h("h2", {}, "Appearance"),
    h("div", { class: "form-grid" }, field("Theme", themeSel)),
  );

  root.append(
    h("div", { class: "actions" },
      h("button", { class: "ghost", onclick: () => actions.lock() }, "Lock vault"),
    ),
  );
  return root;
}

// Years held, as a blank-able input: empty = unknown (never 0). Typing any
// value — including 0 for "bought this year" — marks it as answered.
function yearsHeldInput(p: Profile, persist: () => void): HTMLInputElement {
  const known = yearsHeldKnown(p);
  const inp = h("input", {
    type: "number", step: "0.5", min: "0", inputmode: "decimal", placeholder: "e.g. 6",
    value: known ? String(p.currentPropertyYearsHeld) : "",
    onchange: () => {
      const raw = inp.value.trim();
      p.currentPropertyYearsHeld = raw === "" ? 0 : Math.max(0, Number(raw) || 0);
      p.currentPropertyYearsHeldSet = raw !== "";
      persist();
    },
  }) as HTMLInputElement;
  return inp;
}

// ---------------------------------------------------------------- helpers
/** True once the user has given anything buying power can be computed from.
 *  Before that, figures are an empty state — never a "S$0" result (U-03). */
function hasFinances(p: Profile): boolean {
  return (p.monthlyGrossIncome ?? 0) > 0 || (p.cashSavings ?? 0) > 0 || (p.cpfOaBalance ?? 0) > 0
    || (p.otherLiquidAssets ?? 0) > 0 || (p.currentPropertyValue ?? 0) > 0;
}

/** Plain-language "what limits this number" (replaces "bounded by loan"). */
function limitLabel(b: "loan" | "cash" | "income"): string {
  return b === "cash" ? "limited by your cash & CPF" : "limited by the loan your income supports";
}

function tile(label: string, value: string, sub: string): HTMLElement {
  return h("div", { class: "tile" }, h("div", { class: "tile-value" }, value),
    h("div", { class: "tile-label" }, label), h("div", { class: "tile-sub" }, sub));
}

function kv(k: string, v: string): HTMLElement {
  return h("div", { class: "kv" }, h("span", {}, k), h("strong", {}, v));
}

function signalCard(s: SeriesSignal, inReach: boolean | null = null, areaSqft = 900): HTMLElement {
  const card = h("div", { class: "signal-card" });
  card.append(scoreGauge(s.entry_score ?? 50, "timing"));
  card.append(
    h("div", { class: "signal-meta" },
      h("div", { class: "seg" }, plainSegment(s)),
      h("div", { class: "verdict", style: `color:${verdictColor(s.verdict)}` }, verdictLabel(s.verdict)),
      h("div", { class: "muted small" },
        `${fmtMetric(s.latest_value, s.metric)} · `, deltaText(s.momentum_12m, "12m")),
      inReach === null ? null
        : h("span", { class: inReach ? "reach in" : "reach" },
            h("span", { "aria-hidden": "true" }, inReach ? "✓ " : "– "),
            inReach ? "Within your budget" : (s.metric === "median_psf" || s.metric === "new_sale_psf") ? `Above your budget at ${areaSqft} sqft` : "Above your budget"),
    ),
  );
  return card;
}

// Plain-language market name (U-18): "Condos — outside the city centre (OCR)"
// rather than the dataset label "Private OCR — Outside Central resale (median $psf)".
function plainSegment(s: SeriesSignal): string {
  const id = s.id;
  const reg: Record<string, string> = { ccr: "core central (CCR)", rcr: "city fringe (RCR)", ocr: "outside the city centre (OCR)" };
  const m = /^private\.(resale|new)\.psf(?:\.(ccr|rcr|ocr))?$/.exec(id);
  if (m) {
    const kind = m[1] === "new" ? "New-launch condos" : "Resale condos";
    return m[2] ? `${kind} — ${reg[m[2]]}` : `${kind} — all Singapore`;
  }
  const hdb = /^hdb\.resale\.(.+)$/.exec(id);
  if (hdb) return `HDB ${titleCase(hdb[1].replace(/-/g, " "))} resale`;
  return s.segment.replace(/\s*\(median \$psf\)\s*/i, "");
}

// Sign + arrow + period — colour is never the only cue (DESIGN_SYSTEM §2).
function deltaText(v: number | null, period: string): HTMLElement {
  if (v === null || !Number.isFinite(v)) return h("span", {}, `${period} —`);
  const up = v > 0.05, down = v < -0.05;
  return h("span", { class: up ? "delta up" : down ? "delta down" : "delta" },
    h("span", { "aria-hidden": "true" }, up ? "▲ " : down ? "▼ " : "■ "),
    `${fmtPct(v)} (${period})`);
}

function powerBreakdown(title: string, bp: ReturnType<typeof propertyBuyingPower>): HTMLElement {
  return h("div", { class: "power" },
    h("h3", {}, title),
    kv("Max price", fmtSGD(bp.maxPrice)),
    kv("Bounded by", bp.boundedBy === "loan" ? "loan (income/TDSR)" : "cash/CPF"),
    kv("Max loan", fmtSGD(bp.maxLoan)),
    kv("Cash/CPF available", fmtSGD(bp.availableFunds)),
    bp.freeCashFromProperty > 0
      ? kv("— incl. free cash from sale", fmtSGD(bp.freeCashFromProperty))
      : null,
    kv("Cash/CPF needed", fmtSGD(bp.cashCpfNeeded)),
    kv("Est. monthly @ your rate", fmtSGD(bp.monthlyAtMarketRate)),
    kv("Loan tenure", `${bp.tenureYears} yrs`),
    bp.lease
      ? kv("Lease impact",
          bp.lease.coversToAge
            ? "full CPF & LTV (covers buyer to 95)"
            : `CPF/LTV pro-rated to ~${Math.round(bp.lease.cpfFraction * 100)}% (short lease)`)
      : null,
    bp.lease && !bp.lease.coversToAge
      ? h("p", { class: "muted small" }, bp.lease.note)
      : null,
    bp.options.length > 1 ? lenderTable(bp.options.map((o, i, all) => ({ label: o.label, eligible: o.eligible, note: o.note,
      used: o.eligible && o.maxPrice === bp.maxPrice && all.findIndex((q) => q.eligible && q.maxPrice === bp.maxPrice) === i, f: o.funding }))) : null,
    h("p", { class: "muted small" },
      `${bp.rateSource === "sora" ? "Repayment estimated at live 3M SORA + 0.5% spread (enter your own rate in Profile)" : bp.rateSource === "manual" ? "Repayment estimated at your rate (Profile)" : "Repayment estimated at the default 3.5% (SORA unavailable)"}; HDB loan at HDB's concessionary rate. Loans are sized at the MAS/HDB stress-test rate.`),
  );
}

// Loan options (HDB loan vs bank tiers, MAS/HDB rules) as one card each — a
// 7-column table collapsed digit-by-digit on phones. Cards read the same at
// every width; the option behind your figures is badged, ineligible ones are
// muted with the reason on its own line.
type LenderRow = {
  label: string; eligible: boolean; note: string; used: boolean;
  f: { price: number; loan: number; terms: { ltv: number; tenureYears: number; minCashPct: number; marketRate: number }; cashNeeded: number; cpfNeeded: number; monthlyMarket: number; shortfall: number };
};
function lenderTable(rows: LenderRow[]): HTMLElement {
  const monthly = (v: number) => `S$${(Math.round(v / 10) * 10).toLocaleString()}`;
  const row = (k: string, v: string, cls = "") => h("div", { class: `lo-row ${cls}` }, h("dt", {}, k), h("dd", {}, v));
  return h("div", { class: "lender-cards", role: "list", "aria-label": "Loan options" },
    ...rows.map(({ label, eligible, note, used, f }) =>
      h("div", { class: `lender-card${eligible ? "" : " ineligible"}${used ? " used" : ""}`, role: "listitem" },
        h("div", { class: "lo-head" },
          h("span", { class: "lo-title" }, label),
          used ? h("span", { class: "chip lo-badge" }, "Used in your figures") : null,
          !eligible ? h("span", { class: "chip" }, "Not eligible") : null),
        !eligible && note ? h("p", { class: "lo-note small" }, note) : null,
        h("dl", { class: "lo-rows" },
          row("Price", fmtEstimate(f.price)),
          row("Loan", fmtEstimate(f.loan)),
          row("LTV · tenure", `${Math.round(f.terms.ltv * 100)}% · ${f.terms.tenureYears} yrs`),
          f.terms.minCashPct > 0 ? row("Minimum cash", `${Math.round(f.terms.minCashPct * 100)}% of price`) : null,
          row("Upfront cash / CPF", `${fmtEstimate(f.cashNeeded)} / ${fmtEstimate(f.cpfNeeded)}`),
          row("Monthly", `${monthly(f.monthlyMarket)} @ ${(f.terms.marketRate * 100).toFixed(2)}%`),
          row("Funding gap", f.shortfall > 0 ? fmtEstimate(f.shortfall) : "None", f.shortfall > 0 ? "warn" : ""),
        ),
      )),
  );
}

// Budget-fit bands in plain words — descriptive, never "buy-ready" (no nudges).
const FEAS_LABEL: Record<string, string> = {
  "buy-ready": "Comfortably within budget", reachable: "Within budget", stretch: "A stretch", "out-of-reach": "Above budget",
};

function feasibilityBadge(feas: ReturnType<typeof feasibility>): HTMLElement {
  const cls = feas.affordabilityBand;
  return h("div", { class: `feas feas-${cls}` },
    h("div", { class: "feas-row" },
      h("span", { class: "chip" }, FEAS_LABEL[feas.affordabilityBand]),
      h("span", { class: "muted small" }, feas.affordabilityRatio > 0
        ? `typical price is ${Math.round(100 / feas.affordabilityRatio)}% of your budget · fit ${feas.overallScore}/100`
        : `fit ${feas.overallScore}/100`),
    ),
    h("p", {}, feas.recommendation),
  );
}

function seriesRow(
  actions: Actions,
  s: SeriesSignal,
  feas: ReturnType<typeof feasibility> | null,
): HTMLElement {
  const isIndex = s.metric === "price_index";
  const detail = isIndex
    ? `${fmtMetric(s.latest_value, s.metric)} · trend ${fmtPct(s.momentum_12m)}`
    : `${fmtMetric(s.latest_value, s.metric)} · 3m ${fmtPct(s.momentum_3m)} · 12m ${fmtPct(s.momentum_12m)}`;
  const row = h("div", { class: "series-row" });
  const head = h("div", { class: "series-head" },
    h("div", {},
      h("div", { class: "seg" }, s.segment),
      h("div", { class: "muted small" }, detail),
    ),
    scoreGauge(s.entry_score ?? 50, verdictLabel(s.verdict)),
  );
  row.append(head);

  if (s.entry_score != null && !timingInfluences(s.id)) {
    row.append(h("p", { class: "muted small" }, `For information only — ${TIMING_RULE[familyOf(s.id)].evidence}`));
  }

  // "Why this score" — the three timing factors behind the entry score.
  if (s.entry_score != null) {
    const f = entryFactors(s);
    const fmtF = (v: number | null) => (v == null ? "—" : Math.round(v).toString());
    row.append(h("details", { class: "why" },
      h("summary", { class: "muted small" }, "Why this score"),
      h("div", { class: "section-body" },
        h("div", { class: "muted small" },
          `Valuation vs trend ${fmtF(f.valuation)}/100 (weight 50%) · 12m momentum ${fmtF(f.momentum12m)}/100 (30%) · 3m momentum ${fmtF(f.momentum3m)}/100 (20%) → entry ${f.entry}/100.`),
        h("div", { class: "muted small" },
          "Higher = more favourable: trading below its own recent trend and cooling scores high; chasing a rally scores low. Timing context on past prices only — not a forecast."))));
  }

  // Volume / velocity — a leading read the price-only score can't give. Shown as
  // a separate signal (it does NOT move the entry score) so a soft-price value
  // trap vs firming demand is visible at a glance.
  const conv = convictionText(s.conviction);
  if (conv) {
    const cls =
      s.conviction === "softening" ? "chip good"
        : s.conviction === "building" || s.conviction === "exhaustion" ? "chip warn"
          : "chip";
    row.append(h("div", { class: "vol-signal" },
      h("span", { class: cls, title: conv.hint }, `Volume: ${conv.label}`),
      s.vol_12m != null
        ? h("span", { class: "muted small" }, ` · transactions 12m ${fmtPct(s.vol_12m)}`)
        : null));
  }

  if (feas) row.append(feasibilityBadge(feas));

  const chartHost = h("div", { class: "chart-host" });
  const toggle = h("button", { class: "link", onclick: async () => {
    if (chartHost.childElementCount > 0) { clear(chartHost); return; }
    const detail = await actions.openSeries(s.id);
    if (!detail) { chartHost.append(h("p", { class: "muted" }, "No detail available.")); return; }
    // The current month is still filling (late caveats / registrations): draw it
    // dashed from the last complete point. The entry score ignores it.
    const done = detail.observations.filter((o) => !o.provisional);
    const prov = detail.observations.filter((o) => o.provisional);
    const lines: LineSeries[] = [{ points: done, color: "var(--accent)", label: s.segment }];
    if (prov.length) lines.push({ points: [...done.slice(-1), ...prov], color: "var(--accent)", label: "provisional", dashed: true });
    chartHost.append(lineChart(lines, { fill: true }));
    if (detail.signalHistory.length > 1) {
      chartHost.append(
        lineChart([
          { points: detail.signalHistory.map((x) => ({ period: x.as_of, value: x.entry_score })), color: "var(--good)", label: "entry score" },
        ], {
          height: 170,
          yDomain: { min: 0, max: 100 },
          bands: [
            { from: 62, to: 100, color: "var(--good)", label: "favourable" },
            { from: 0, to: 40, color: "var(--bad)", label: "stretched" },
          ],
        }),
      );
    }
    chartHost.append(
      h("p", { class: "muted small" }, "Top: price/premium history (dashed = current month, still filling — not used in the signal). Bottom: entry-score over time — green band ≥62 favourable, red band ≤40 stretched."),
    );
  } }, "Show price & signal history");
  row.append(toggle, chartHost);
  row.append(sourceNote(s.source, s.as_of));
  return row;
}
